import { Fragment, useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import { videoEmbedUrl, extractVideoUrl, extractLinkUrl } from "./videoUrl";
import { ProtectedFields, ProtectedLink, protectedError, protectedFields, expiryToLocalInput } from "./protectedPost";
import VideoCaption from "./VideoCaption";
import { createPortal } from "react-dom";
import { cachedPost } from "./apiCache";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toastPoints, toastLikePoints } from "./pointsToast";
import axios from "axios";
import config from "./config";
import { useMentions } from "./mentions";
import { nativeLogin } from "./nativeLogin";
import { fetchProfile, fetchMyPlans } from "./nativeProfile";
import { resolveComposerImages, uploadFile } from "./uploadMedia";
import { acceptImages, withinMediaLimit, transferFiles, dragHasFiles } from "./mediaLimits";
import MyPlansModal from "./MyPlansModal";
import AffiliateEarningsPage from "./AffiliateEarningsPage";
import SubscriptionExpiredOverlay from "./SubscriptionExpiredOverlay";
import { subscriptionGate } from "./subscriptionGate";
import GroupSwitcher from "./GroupSwitcher";
// Commented out alongside the switcher's render site further down (search this
// file for "Membership switcher"). Restore both together.
// import MembershipSwitcher from "./MembershipSwitcher";
import MembershipHeader, { BrandLogo, CompactNavMenu } from "./MembershipHeader";
import { PENDING_ICON_PATH, GROUP_SCOPED_PAGES, EARNINGS_PAGE, homeEnabled, landingSection, sectionPath, isCustomKey, customIdOf, splitInline } from "./nav";
import CustomPage from "./CustomPage";
import HomeView from "./home/HomeView";
import SpotlightBanner, { spotlightBannerFor } from "./spotlight/SpotlightBanner";
import { BannerIcon } from "./spotlight/bannerIcons";
import SpotlightSearchResults from "./spotlight/SpotlightSearchResults";
import MembershipSidebar from "./MembershipSidebar";
import CommentsSheet, { useCompactViewport } from "./CommentsSheet";
import {
  POST_BG_PRESETS,
  POST_BG_ORDER,
  POST_BG_MAX_CHARS,
  parseBg,
  encodeBg,
  bgFontSize,
} from "./postBackgrounds";
import { useLongPressBinder, CommentActionSheet, EditIcon, DeleteIcon } from "./CommentActions";
import ProfileModal from "./ProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";
import AccountSettingsModal from "./AccountSettingsModal";
import MobileCodeModal from "./MobileCodeModal";
import AuthModal from "./AuthModal";
import PolicyFooter from "./PolicyFooter";
import BannerPopup from "./BannerPopup";
import applyCommunityFont, { applyCommunityDirection, applyCommunityTheme } from "./applyFont";
import { useBrand } from "./useBrand";
import PageLoader, { Spinner, InlineLoader, FeedSkeleton } from "./PageLoader";
import InlineBanner from "./InlineBanner";
import EmojiPicker from "./EmojiPicker";
import GifPicker from "./GifPicker";
import PostImages from "./PostImages";
import LinkPreview, { LinkPreviewStrip } from "./LinkPreview";
import ImageEditor, { EditPhotoButton, isEditableImage } from "./ImageEditor";
import WelcomeFlowers from "./WelcomeFlowers";
import GroupLanding from "./GroupLanding";
import { MembersView, LeaderboardView, RewardsView, EventsView, UpcomingEventBanner, MyPointsCard, useMyPoints, SectionHeader } from "./MembershipViews";
import MapView from "./MapView";
import ChatModal from "./ChatModal";
import CoursesView, { GroupMiniBanner, CommunityMiniBanner } from "./CourseViews";
import MockTestLauncher from "./MockTestLauncher";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";
import profileBanner from "./profileBanner";
import PoweredByFlexiFunnels from "./PoweredByFlexiFunnels";

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", otp: "", password_confirmation: "", gdpr_consent: false };

// Role capabilities for this group, as the feed endpoint reports them in `can`.
// Mirrors groupCapabilities() in the bridge's MembershipFeedController — the
// server is the authority and re-checks every one of these; this only decides
// what the UI offers. Defaults to "no powers" so a member never sees an admin
// control during the first render, before the feed payload lands.
const NO_PERMS = {
    manage_posts: false,     // edit or delete any post        (moderator +)
    delete_comments: false,  // delete any comment             (moderator +)
    admin_categories: false, // post in admin-only categories  (admin +)
    pin_posts: false,        // pin / unpin posts              (admin +)
    approve_posts: false,    // approve or reject member posts (admin +)
    protected_posts: false,  // post a private link behind a button (admin +)
};

// How many posts the feed requests per page. The feed auto-loads the next page
// when the sentinel below the list scrolls into view. Must be <= the API's cap.
const POSTS_PER_PAGE = 30;

// How many extra pages a ?post= deep link may pull while hunting for its post
// before giving up. 5 × POSTS_PER_PAGE is deep enough for any notification a
// member is realistically still clicking, and stops a stale id from paging
// through the entire group.
const FOCUS_MAX_AUTO_PAGES = 5;

// Shared premium input styling + a themed focus ring (border + soft glow in the
// community colour) reused across the composer / edit / comment forms.
const INPUT_CLS =
  "w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none transition focus:bg-surface placeholder:text-gray-400";
const themeFocus = (color) => ({
  onFocus: (e) => {
    e.currentTarget.style.borderColor = color;
    e.currentTarget.style.boxShadow = `0 0 0 3px ${color}26`;
  },
  onBlur: (e) => {
    e.currentTarget.style.borderColor = "";
    e.currentTarget.style.boxShadow = "";
  },
});

const DEFAULT_BANNER =
  "https://img.flexifunnels.com/flexiviral-assets/logo-empty.png";

/**
 * Ask the membership ADMIN app to email about a feed event.
 *
 * Comment / reply / mention / like emails have to be raised from here rather
 * than by the bridge: the bridge's dispatcher returns early unless
 * INTERNAL_API_SECRET is present in its own .env, so without that value it never
 * makes the call at all. New-post emails kept working only because they already
 * came from the browser — this puts the rest on the same footing.
 *
 * Only the id is sent. The admin app reads the actor, the recipients, the group
 * and the excerpt from the row itself, so nothing here decides who gets mailed.
 *
 * Fire-and-forget: the comment/like is already applied and on screen, so a
 * failure here must never surface as an error. The admin side is idempotent per
 * comment (per post for a post like), so retries cannot double-send.
 */
async function notifyEmailEvent(membershipToken, type, ids) {
  if (!membershipToken || !type) return;
  try {
    await axios.post(`${config.MEMBERSHIP_API_URL}/membership-event-notify`, {
      membership_token: membershipToken,
      type,
      ...ids,
    });
  } catch {
    /* best-effort — email notification is non-critical to the action */
  }
}

/**
 * Floating group-info bubble (mobile only). Replaces the right-rail group card
 * on small screens: a bottom-left FAB showing the group banner that opens a
 * premium bottom-sheet with the group's details + stats.
 */
function GroupInfoBubble({ group, totalMembers, primary }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
  if (!group) return null;
  const isPrivate = group.access_type === 2;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Community info"
        className="lg:hidden fixed bottom-5 start-4 z-40 w-14 h-14 rounded-full shadow-xl ring-4 ring-surface/60 overflow-hidden flex items-center justify-center text-white active:scale-95 transition"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, var(--brand-secondary, ${primary}cc) 100%)` }}
      >
        {group.banner && (
          <>
            <img src={group.banner} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            <span className="absolute inset-0 bg-black/30" />
          </>
        )}
        <svg className="relative w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[9999] flex items-end justify-center bg-ink/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-surface w-full rounded-t-3xl shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-36 bg-gray-100">
              <img
                src={group.banner || DEFAULT_BANNER}
                alt={group.name}
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.src = DEFAULT_BANNER)}
              />
              <span className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
              <span className="absolute top-2.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-surface/70" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-surface/90 hover:bg-surface text-gray-700 text-lg leading-none shadow"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <h3 className="absolute bottom-3 left-4 right-4 text-white text-xl font-bold leading-tight flex items-center gap-2">
                <span className="truncate">{group.name}</span>
                {isPrivate && <span title="Private community">🔒</span>}
              </h3>
            </div>
            <div className="p-5 pb-7">
              <p className="text-sm text-gray-600 leading-relaxed">{group.description || "No description provided."}</p>
              <div className="grid grid-cols-3 gap-3 mt-5">
                {[
                  { v: group.member_count, l: "Members" },
                  { v: totalMembers, l: "Community" },
                  { v: isPrivate ? "Private" : "Open", l: "Access" },
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl bg-gray-50 py-3.5 text-center">
                    <div className="font-bold text-gray-900 text-lg" style={{ color: primary }}>{s.v}</div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-0.5">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Skool-style category pills under the composer. Posts have no topic taxonomy,
// so we filter by post_type (1/unset = discussion, 2 = photo, 3 = poll, 4 = video, 5 = audio).
const FILTER_ICONS = {
  all: "M4 6h16M4 12h16M4 18h16",
  text: "M8 10h8M8 14h5M21 12a9 9 0 11-3.6-7.2L21 4v5h-5",
  image: "M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z",
  video: "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z",
  poll: "M9 17V9m4 8V5m4 12v-6M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z",
  audio: "M19 11a7 7 0 01-14 0M12 18v3m0-3a4 4 0 01-4-4V7a4 4 0 018 0v7a4 4 0 01-4 4z",
};
const FEED_FILTERS = [
  { key: "all", label: "All" },
  { key: "text", label: "Discussions" },
  { key: "image", label: "Photos" },
  { key: "video", label: "Videos" },
  { key: "audio", label: "Audio" },
  { key: "poll", label: "Polls" },
];

const FilterGlyph = ({ k, className = "w-[18px] h-[18px]" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d={FILTER_ICONS[k]} />
  </svg>
);

// A single category filter chip above the feed (All + one per group category).
// Active chip is tinted with the brand colour; inactive is a plain outlined pill.
function CategoryChip({ emoji, label, active, onClick, primary }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition"
      style={
        active
          ? { backgroundColor: `${primary}14`, color: primary, borderColor: `${primary}59` }
          : { backgroundColor: "rgb(var(--c-surface))", color: "rgb(var(--c-gray-700))", borderColor: "rgb(var(--c-gray-200))" }
      }
    >
      {emoji && <span className="text-[15px] leading-none">{emoji}</span>}
      {label}
    </button>
  );
}

// Compact "Filter" button + premium popover (mirrors the community page filter).
// Single-select post-type filter shown as labelled chips with counts.
function FeedFilter({ items, active, onChange, primary }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, []);
  const cur = items.find((i) => i.key === active) || items[0];
  const isFiltered = active && active !== "all";

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-surface px-4 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
      >
        <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
        </svg>
        <span>{isFiltered ? cur.label : "Filter"}</span>
        {isFiltered && (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white" style={{ background: primary, color: "var(--brand-btn-text, #fff)" }}>
            {cur.count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-30 mt-2 w-[270px] overflow-hidden rounded-2xl border border-gray-100 bg-surface shadow-2xl ring-1 ring-gray-900/5">
            {/* header */}
            <div className="flex items-center gap-2 border-b border-gray-100 bg-gradient-to-b from-gray-50/80 to-surface px-4 py-3">
              <svg className="h-4 w-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 12h12M10 20h4" />
              </svg>
              <p className="text-sm font-bold text-gray-900">Filter posts</p>
            </div>
            {/* options — vertical list; each row: icon + label (left) + count (right). */}
            <div className="flex flex-col gap-1.5 p-2.5">
              {items.map((f) => {
                const isActive = f.key === active;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => { onChange(f.key); setOpen(false); }}
                    className="flex w-full items-center gap-2.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-all"
                    style={
                      isActive
                        ? { borderColor: primary, backgroundColor: `${primary}14`, color: primary, boxShadow: `0 1px 6px -2px ${primary}66` }
                        : { borderColor: "rgb(var(--c-gray-200))", backgroundColor: "rgb(var(--c-surface))", color: "rgb(var(--c-gray-600))" }
                    }
                  >
                    <FilterGlyph k={f.key} className="w-4 h-4 shrink-0" />
                    <span className="flex-1 text-left">{f.label}</span>
                    <span
                      className="rounded-full px-2 py-0.5 text-[11px] font-bold"
                      style={isActive ? { backgroundColor: "rgb(var(--c-surface))", color: primary } : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
                    >
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Icon paths for the composer's post-type buttons (trigger row + modal toolbar).
const COMPOSER_ICONS = {
  text: "M5 5h14M12 5v14M9 19h6",
  photo:
    "M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z",
  audio: "M19 11a7 7 0 01-14 0M12 18v3m0-3a4 4 0 01-4-4V7a4 4 0 018 0v7a4 4 0 01-4 4z",
  video:
    "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z",
  poll: "M9 17V9m4 8V5m4 12v-6M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z",
};

// Distinct accent per post type — gives the composer a premium, colour-coded look.
const COMPOSER_ACCENT = {
  text: "#0ea5e9",  // sky
  photo: "#10b981", // emerald
  audio: "#8b5cf6", // violet
  video: "#ef4444", // red
  poll: "#f59e0b",  // amber
};

/**
 * One attachment control in the composer: the icon alone, with a tooltip.
 *
 * The tooltip is ours rather than the browser's `title`. A native one waits
 * about a second, renders in the OS's own style, and never appears on a phone at
 * all — on a control this small, a label that arrives late and looks foreign is
 * barely better than none. `aria-label` still carries the name for screen
 * readers, and the accent colour does the identifying at a glance.
 */
/**
 * Hover label for an icon-only control. Ours rather than the browser's `title`:
 * a native tooltip waits about a second, renders in the OS's own style, and
 * never appears on a phone at all — on controls this small, a label that arrives
 * late and looks foreign is barely better than none.
 *
 * `className` lands on the wrapper, because the wrapper is what sits in the
 * parent's flex layout now — the trigger row sizes its buttons with flex-1, and
 * that has to move out here or the row loses its even split.
 */
function IconTooltip({ label, className = "", children }) {
  return (
    <span className={`group relative ${className}`}>
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute -top-8 left-1/2 z-20 -translate-x-1/2 translate-y-1 scale-95 whitespace-nowrap rounded-lg bg-ink px-2 py-1 text-[11px] font-semibold text-white opacity-0 shadow-lg transition duration-150 group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100"
      >
        {label}
      </span>
    </span>
  );
}

function ComposerAttachChip({ icon, label, active, onClick }) {
  const accent = COMPOSER_ACCENT[icon] || "#0ea5e9";
  // Active, the chip is the way OUT of this post type — so it says so. A toggle
  // whose off-switch is the button that is already lit is invisible otherwise,
  // and this one is the only route back to a plain text post (and to the
  // backdrop palette, which only text posts get).
  const tip = active ? "Back to text post" : label;
  return (
    <IconTooltip label={tip} className="shrink-0">
      <button
        type="button"
        onClick={onClick}
        aria-label={tip}
        aria-pressed={active}
        className="grid h-10 w-10 place-items-center rounded-2xl transition duration-200 ease-out hover:-translate-y-0.5"
        style={{
          background: active ? `${accent}24` : `${accent}12`,
          color: accent,
          boxShadow: active ? `inset 0 0 0 1.5px ${accent}66` : "none",
        }}
      >
        <svg className="h-[19px] w-[19px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
          <path strokeLinecap="round" strokeLinejoin="round" d={COMPOSER_ICONS[icon]} />
        </svg>
      </button>
    </IconTooltip>
  );
}

/**
 * The post's category, as a pill under the author's name.
 *
 * It was a full-width labelled <select> at the bottom of the modal, below the
 * media toolbar. That put a decision you make once, and rarely change, between
 * the member and the Post button on every single post. Up here it reads as part
 * of the byline, which is what it is.
 *
 * Hand-rolled rather than a native <select> so the emoji, the tick and the pill
 * shape survive on every platform; a native control cannot be styled to match on
 * Android or Safari.
 */
function PostCategoryChip({ categories, value, onChange, primary = "#0f7a4d", align = "left" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    addOutsidePress(onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      removeOutsidePress(onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!categories.length) return null;
  // `null` is a real choice here, not a missing one — a post does not have to
  // belong to a category, and there was previously no way to say so.
  const NONE = { membership_group_category_id: null, name: "None", emoji: null };
  const current =
    value === null
      ? NONE
      : categories.find((c) => String(c.membership_group_category_id) === String(value)) ||
        categories[0];
  const options = [...categories, NONE];
  const noneSelected = current.membership_group_category_id === null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-9 max-w-[230px] items-center gap-2 rounded-full bg-surface pl-1.5 pr-2.5 text-[13px] font-semibold text-gray-700 transition duration-200 hover:-translate-y-px hover:text-gray-900"
        style={{ boxShadow: open ? `inset 0 0 0 1.5px ${primary}, 0 6px 16px -10px ${primary}` : "inset 0 0 0 1px rgb(var(--c-gray-200)), 0 1px 2px rgba(16,24,40,0.04)" }}
      >
        <span
          className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[13px] leading-none"
          style={{ background: noneSelected ? "#f3f4f6" : `${primary}17` }}
        >
          {current.emoji ? (
            current.emoji
          ) : noneSelected ? (
            <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="M6 18L18 6" />
            </svg>
          ) : (
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: primary }} />
          )}
        </span>
        <span className={`truncate ${noneSelected ? "text-gray-500" : ""}`}>{current.name}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2.4"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className={`thin-scroll absolute top-full z-30 mt-2 max-h-64 w-64 overflow-y-auto rounded-2xl bg-surface p-2 shadow-[0_18px_44px_-16px_rgba(16,24,40,0.35)] ring-1 ring-black/5 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {options.map((c, idx) => {
            const id = c.membership_group_category_id;
            const isNone = id === null;
            const selected = isNone
              ? current.membership_group_category_id === null
              : String(id) === String(current.membership_group_category_id);
            return (
              <Fragment key={isNone ? "__none" : id}>
              {isNone && idx > 0 && <span className="my-1 block h-px bg-gray-100" />}
              <button
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(isNone ? null : Number(id));
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left text-[13.5px] transition hover:bg-gray-50 ${
                  selected ? "font-bold text-gray-900" : "font-medium text-gray-600"
                }`}
              >
                {c.emoji ? (
                  <span className="shrink-0 leading-none">{c.emoji}</span>
                ) : isNone ? (
                  <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" />
                    <path strokeLinecap="round" d="M6 18L18 6" />
                  </svg>
                ) : null}
                <span className={`min-w-0 flex-1 truncate ${isNone ? "text-gray-500" : ""}`}>{c.name}</span>
                {selected && (
                  <svg className="h-4 w-4 shrink-0" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

const BG_RECENTS_KEY = "mp_post_bg_recents";

/** Last few custom backdrops this person built, newest first. */
function readBgRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(BG_RECENTS_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((v) => parseBg(v)).slice(0, 6) : [];
  } catch {
    // A private window, or someone else's junk under our key. Not worth a
    // console line — recents are a convenience, their absence changes nothing.
    return [];
  }
}

function rememberBgRecent(value) {
  if (!value || value.startsWith("p:")) return; // presets are already on screen
  try {
    const next = [value, ...readBgRecents().filter((v) => v !== value)].slice(0, 6);
    localStorage.setItem(BG_RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — skip silently */
  }
}

/**
 * One backdrop swatch.  `bg` is CSS; null renders the "no backdrop" tile.
 *
 * Round, not square. A grid of rounded squares reads as buttons — things that
 * DO something — while a row of discs reads as paint, which is what these are.
 * Each carries a hairline inner ring so a pale swatch still has an edge on
 * white, and a soft drop shadow so the row sits ON the sheet rather than being
 * printed into it.
 *
 * Selection is a ring OUTSIDE the disc plus a tick inside: a border alone is
 * invisible against ink and washed out against blush, and both extremes are in
 * this palette.
 */
function BgSwatch({ bg, selected, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={selected}
      className={`relative grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md ${
        selected ? "ring-[2.5px] ring-gray-900 ring-offset-2 ring-offset-white" : ""
      }`}
      style={{
        background: bg || "#fff",
        boxShadow: bg
          ? "inset 0 0 0 1px rgba(255,255,255,0.22), inset 0 -6px 10px -8px rgba(0,0,0,0.45), 0 2px 5px -2px rgba(16,24,40,0.28)"
          : "inset 0 0 0 1px rgb(var(--c-gray-200))",
      }}
    >
      {!bg && (
        <svg viewBox="0 0 34 34" className="h-full w-full text-gray-300 dark:text-gray-500" aria-hidden="true">
          <line x1="9" y1="25" x2="25" y2="9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      )}
      {selected && bg && (
        <svg
          className="h-[15px] w-[15px]"
          style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.45))" }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="#fff"
          strokeWidth="3.4"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

/**
 * Small-caps section heading, sat INLINE with its controls rather than on a row
 * of its own. Two label rows cost ~56px of a composer that has to fit a preview,
 * a colour rail, an attachment row and a button inside a laptop viewport — and
 * a label beside what it names reads no worse than a label above it.
 */
function ComposerSectionLabel({ children }) {
  return (
    <span className="shrink-0 text-[10.5px] font-bold uppercase tracking-[0.14em] text-gray-400">
      {children}
    </span>
  );
}

/**
 * Backdrop picker — the colour a short text post is published on.
 *
 * The swatch rail is ALWAYS on screen rather than folded behind a single button.
 * Hiding it costs a tap to discover and a tap to use, on the one control here
 * people are most likely to be hunting for; showing it costs one 32px row, and
 * only on posts that can actually take a backdrop — the whole section stops
 * rendering once something is attached. "Custom" is what stays folded, because
 * that is the part you go looking for deliberately.
 *
 * `value` / `onChange` speak the STORED string (see postBackgrounds.js), not
 * CSS — the composer hands the same string to the API, so there is no second
 * representation to keep in sync.
 *
 * Every control writes through immediately rather than behind an Apply button:
 * the canvas above IS the preview, and a colour you must commit to before you
 * can see it is what makes custom pickers unpleasant to use.
 */
function PostBackdropRail({ value, onChange, primary = "#0f7a4d" }) {
  const [custom, setCustom] = useState(false);
  // The rail scrolls, so the last visible disc is sliced clean in half against
  // Custom, which reads as a rendering fault rather than "there is more". A
  // fade says the same thing honestly — and it is switched OFF at the end of
  // the scroll, because fading a swatch that IS fully in view would be its own
  // small lie about what is there.
  const railRef = useRef(null);
  const [railAtEnd, setRailAtEnd] = useState(false);
  const syncRailEnd = useCallback(() => {
    const el = railRef.current;
    if (el) setRailAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);
  useEffect(syncRailEnd, [syncRailEnd]);
  const [recents, setRecents] = useState(readBgRecents);
  // Builder state, seeded from `value` when Custom opens so tweaking an
  // already-chosen colour continues from it instead of resetting.
  // Solid is where the builder opens: it is the one-decision option, and a
  // gradient is the deliberate step past it rather than the starting point.
  const [mode, setMode] = useState("solid");
  const [solid, setSolid] = useState("#d0184c");
  const [stops, setStops] = useState(["#f9295f", "#3b3bea"]);
  const [angle, setAngle] = useState(135);

  // Closing the builder is when a custom earns its place in Recents — not every
  // drag of the colour well, which would fill the row with one hue's shades.
  const closeCustom = useCallback(() => {
    setCustom(false);
    if (value && !value.startsWith("p:")) {
      rememberBgRecent(value);
      setRecents(readBgRecents());
    }
  }, [value]);

  // Click-away / Escape, because the builder no longer has a place in the flow
  // you can simply scroll past — it floats, so it has to be dismissible the way
  // every other floating thing in the app is.
  // Escape leaves the builder and puts the presets back. No click-away handler:
  // this is an inline section now, not a popover, so clicking elsewhere in the
  // composer is ordinary use rather than a dismissal.
  useEffect(() => {
    if (!custom) return undefined;
    const onKey = (e) => e.key === "Escape" && closeCustom();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [custom, closeCustom]);

  const openCustom = () => {
    if (value?.startsWith("s:")) {
      setMode("solid");
      setSolid(`#${value.slice(2)}`);
    } else if (value?.startsWith("g:")) {
      const [, a, st] = value.split(":");
      setMode("gradient");
      setAngle(Number(a) || 135);
      setStops(st.split(",").map((h) => `#${h}`));
    } else {
      // Nothing custom to continue from (no backdrop, or a preset) — start on
      // Solid rather than on whatever the last visit left the tab set to, and
      // apply that colour straight away. Opening the builder without pushing
      // left the well showing a colour the canvas above did not have, so the
      // first click on the already-selected Solid tab looked like what applied
      // it. Every other control here writes through on sight; so does this.
      setMode("solid");
      pushSolid(solid);
    }
    setCustom(true);
  };

  const pushSolid = (hex) => {
    setSolid(hex);
    onChange(encodeBg({ mode: "solid", solid: hex }));
  };
  const pushStops = (next) => {
    setStops(next);
    onChange(encodeBg({ mode: "gradient", stops: next, angle }));
  };
  const pushAngle = (a) => {
    setAngle(a);
    onChange(encodeBg({ mode: "gradient", stops, angle: a }));
  };

  const wells =
    mode === "solid" ? (
      <>
        <input
          type="color"
          value={solid}
          onChange={(e) => pushSolid(e.target.value)}
          className="h-9 w-11 shrink-0 cursor-pointer rounded-xl border-0 bg-transparent p-0"
          aria-label="Backdrop colour"
        />
        <input
          value={solid}
          onChange={(e) => {
            const v = e.target.value;
            setSolid(v);
            // Only push a COMPLETE hex — otherwise the canvas flickers to "no
            // backdrop" after every keystroke while typing.
            if (/^#[0-9a-fA-F]{6}$/.test(v)) pushSolid(v);
          }}
          placeholder="#d0184c"
          maxLength={7}
          spellCheck={false}
          className="w-[92px] shrink-0 rounded-xl bg-surface px-2.5 py-2 font-mono text-[12.5px] uppercase outline-none ring-1 ring-inset ring-gray-200 focus:ring-gray-400"
          aria-label="Hex colour"
        />
      </>
    ) : (
      <>
        {stops.map((c, i) => (
          <span key={i} className="relative shrink-0">
            <input
              type="color"
              value={c}
              onChange={(e) => pushStops(stops.map((v, idx) => (idx === i ? e.target.value : v)))}
              className="h-9 w-11 cursor-pointer rounded-xl border-0 bg-transparent p-0"
              aria-label={`Gradient colour ${i + 1}`}
            />
            {stops.length > 2 && (
              <button
                type="button"
                onClick={() => pushStops(stops.filter((_, idx) => idx !== i))}
                className="absolute -right-1.5 -top-1.5 grid h-4 w-4 place-items-center rounded-full bg-ink text-white shadow"
                aria-label={`Remove colour ${i + 1}`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-2.5 w-2.5">
                  <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}
        {stops.length < 3 && (
          <button
            type="button"
            onClick={() => pushStops([...stops, "#ffffff"])}
            className="grid h-9 w-11 shrink-0 place-items-center rounded-xl border-2 border-dashed border-gray-300 text-gray-400 transition hover:border-gray-400 hover:text-gray-600"
            aria-label="Add a third colour"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
              <path strokeLinecap="round" d="M12 5v14M5 12h14" />
            </svg>
          </button>
        )}
      </>
    );

  return (
    <div className="mt-3.5">
      {custom ? (
        /* The builder REPLACES the swatch rail rather than opening beneath it,
           and lays out on one row (plus an angle row for gradients). Both the
           earlier shapes were wrong: expanding in place added ~170px and put a
           scrollbar on the whole composer just to pick a colour, and floating it
           as a popover covered the message field it was meant to be decorating.
           Swapping one control for another costs 40px and hides nothing. */
        <>
          {/* flex-wrap, not a fixed row: label + segmented + three colour wells
              + close is ~440px, which fits a 576px modal and overflows a phone.
              Wrapping puts the wells on their own line there instead of
              squashing the segmented control into an ellipsis. */}
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
            <ComposerSectionLabel>Backdrop</ComposerSectionLabel>
            <div className="inline-flex shrink-0 rounded-full bg-surface p-1 ring-1 ring-inset ring-gray-200">
              {["solid", "gradient"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMode(m);
                    onChange(
                      m === "solid"
                        ? encodeBg({ mode: "solid", solid })
                        : encodeBg({ mode: "gradient", stops, angle }),
                    );
                  }}
                  className="rounded-full px-3.5 py-1 text-[12px] font-bold capitalize transition duration-200"
                  style={
                    mode === m
                      ? {
                          background: primary,
                          color: "var(--brand-btn-text, #fff)",
                          boxShadow: `0 6px 14px -8px ${primary}`,
                        }
                      : { color: "rgb(var(--c-gray-500))" }
                  }
                >
                  {m}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:ml-auto">{wells}</div>

            <button
              type="button"
              onClick={closeCustom}
              aria-label="Back to preset backdrops"
              title="Back to presets"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            >
              <svg className="h-[17px] w-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {mode === "gradient" && (
            <div className="mt-2.5 flex items-center gap-3">
              {/* Live dial. The slider says what the number is; this says what
                  the number DOES, which is the part you care about. */}
              <span
                className="h-9 w-9 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
                style={{
                  background: `linear-gradient(${angle}deg, ${stops.join(",")})`,
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)",
                }}
                aria-hidden="true"
              />
              <input
                type="range"
                min={0}
                max={360}
                step={15}
                value={angle}
                onChange={(e) => pushAngle(Number(e.target.value))}
                className="h-1.5 flex-1 cursor-pointer rounded-full"
                style={{ accentColor: primary }}
                aria-label="Gradient angle"
              />
              <span
                className="shrink-0 rounded-full px-2.5 py-1 text-[11.5px] font-bold tabular-nums"
                style={{ background: `${primary}14`, color: primary }}
              >
                {angle}°
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
          <ComposerSectionLabel>Backdrop</ComposerSectionLabel>
          {/* `sm:contents` dissolves this wrapper on a real screen, so the rail
              and Custom go back to being direct children of the row. On a phone
              it keeps them side by side under the stacked label — without it the
              column stretched Custom to the full width, which read as a primary
              button rather than the secondary option it is. */}
          <div className="flex min-w-0 items-center gap-2 sm:contents">
          <div
            ref={railRef}
            onScroll={syncRailEnd}
            className="scrollbar-hide flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1.5"
            style={
              railAtEnd
                ? undefined
                : {
                    maskImage: "linear-gradient(90deg,#000 calc(100% - 30px),transparent)",
                    WebkitMaskImage: "linear-gradient(90deg,#000 calc(100% - 30px),transparent)",
                  }
            }
          >
            <BgSwatch bg={null} label="No backdrop" selected={!value} onClick={() => onChange("")} />
            <span className="h-6 w-px shrink-0 bg-gray-200" />
            {recents.map((v) => {
              const b = parseBg(v);
              return b ? (
                <BgSwatch key={v} bg={b.css} label="Recent colour" selected={value === v} onClick={() => onChange(v)} />
              ) : null;
            })}
            {recents.length > 0 && <span className="h-6 w-px shrink-0 bg-gray-200" />}
            {POST_BG_ORDER.map((id) => (
              <BgSwatch
                key={id}
                bg={POST_BG_PRESETS[id].css}
                label={id}
                selected={value === `p:${id}`}
                onClick={() => onChange(`p:${id}`)}
              />
            ))}
          </div>

          {/* Pinned outside the scroller: as the rail's last item it sat past
              fourteen swatches, so you had to already know it was there. */}
          <button
            type="button"
            onClick={openCustom}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-full pl-1 pr-3 text-[12px] font-bold transition duration-200"
            style={{ background: "rgb(var(--c-surface))", color: "rgb(var(--c-gray-600))", boxShadow: "inset 0 0 0 1px rgb(var(--c-gray-200))" }}
          >
            <span
              className="h-7 w-7 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
              style={{
                background: "conic-gradient(from 210deg,#f43f5e,#f59e0b,#22c55e,#06b6d4,#6366f1,#f43f5e)",
              }}
            />
            Custom
          </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Dashed upload drop-zone shown in the Create Post modal for the active media
// mode. Clicking opens the file picker; a dropped file is passed to onFile.
const DROPZONE_CFG = {
  photo: { label: "Add Photo", hint: "PNG, JPG, JPEG, GIF · up to 5 MB", icon: COMPOSER_ICONS.photo },
  video: { label: "Add Video", hint: "MP4, WEBM, MOV · up to 50 MB", icon: COMPOSER_ICONS.video },
  audio: { label: "Add Audio", hint: "MP3, WAV, M4A, AAC · up to 15 MB", icon: COMPOSER_ICONS.audio },
};
// `onFiles` takes every dropped file (photos allow several); `onFile` only the
// first (audio / video are one-per-post).
function MediaDropzone({ kind, primary, onOpen, onFile, onFiles }) {
  const cfg = DROPZONE_CFG[kind];
  return (
    <div
      onClick={onOpen}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const files = e.dataTransfer?.files;
        if (!files?.length) return;
        if (onFiles) onFiles(files);
        else onFile(files[0]);
      }}
      className="mt-3 cursor-pointer rounded-2xl border-2 border-dashed px-4 py-7 text-center transition hover:opacity-90"
      style={{ borderColor: `${primary}80`, background: `${primary}0a` }}
    >
      <span className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full" style={{ background: `${primary}1a`, color: primary }}>
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
          <path strokeLinecap="round" strokeLinejoin="round" d={cfg.icon} />
        </svg>
      </span>
      <p className="text-[15px] font-semibold" style={{ color: primary }}>{cfg.label}</p>
      <p className="mt-1 text-xs text-gray-500">{cfg.hint}</p>
    </div>
  );
}

// Maps a nav tab to its menu-hide flag. Used for both the per-group flags
// (group.menus.*) and the project-wide flags (community.menus.*), which share
// the same short keys. `groups` (Community) only has a project-wide flag; feed
// is never a nav button.
const TAB_MENU_KEY = {
  home: "home",
  courses: "course",
  groups: "community",
  events: "event",
  members: "member",
  map: "map",
  leaderboard: "leaderboard",
  rewards: "reward",
  memberchat: "memberchat",
};

// Admin-only "Pending Posts" entry for the header "More" dropdown (Approve Posts).
// Maps the current sub-page to its banner placement key (matches the admin
// Banner Ads "Show on" options), so popup banners and inline banner ads honour
// the same per-page placements. "Community" covers the feed and the group's
// About page; Courses/Members/Map/Leaderboard/Rewards/Events map 1:1.
const SUBVIEW_PLACEMENT = {
  // Account pages, search and custom pages carry no banner ads.
  page: "account",
  search: "account",
  profile: "account",
  password: "account",
  plans: "account",
  // Banner Ads can't target Home yet (no "Home" in the admin's Show-on list),
  // so it names a placement no banner carries — rather than falling back to
  // "community" and popping community banners over the dashboard.
  home: "home",
  courses: "courses",
  feed: "community",
  about: "community",
  members: "members",
  map: "map",
  leaderboard: "leaderboard",
  rewards: "rewards",
  events: "events",
};

/**
 * Compact group-nav dropdown for small/medium screens (replaces the horizontal
 * scrolling tab bar). Shows the active section, opening a premium popover list
 * of all sections (incl. About). Mirrors the desktop tabs' behaviour.
 */
/**
 * "More ▾" overflow dropdown for the desktop horizontal nav — holds the
 * secondary sections (Leaderboard / Rewards / About) so the primary tabs stay
 * on one row like the reference design.
 */
function matchesFilter(key, p) {
  switch (key) {
    case "image":
      return p.post_type === 2;
    case "poll":
      return p.post_type === 3;
    case "video":
      return p.post_type === 4;
    case "audio":
      return p.post_type === 5;
    case "text":
      return !p.post_type || p.post_type === 1;
    default:
      return true; // "all"
  }
}

function readMember(token) {
  try {
    return JSON.parse(localStorage.getItem(`cm_${token}`) || "null");
  } catch {
    return null;
  }
}

/**
 * Community group feed (shell).
 *
 * /:token/feed/:groupToken — guruai-style 3-column layout:
 * left = about the group, center = composer + feed, right = leaderboard.
 * Posting is a placeholder until a community_posts backend exists.
 */
function MembershipShell({ token: propToken, groupToken: propGroupToken, groupReady = true, community: communityProp, totalMembers: totalMembersProp, activeGroup: activeGroupProp, refCode, focusPostId, focusCommentId, onMemberChange, onBack, navTabs, onNavigate, onInstructor, page, onChangePage, policies, communityName, template = "classic", myGroups = [], onSwitchGroup, onDiscoverGroups, membershipSwitcherOn = false, myMemberships = [], onSwitchMembership }) {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // `?open=<member_token>` (from a message notification) → auto-open that chat.
  const openChatToken = searchParams.get("open");
  const token = propToken ?? params.token;
  const groupToken = propGroupToken ?? params.groupToken;

  // Embedded (inline on the community page) vs standalone route. The logo falls
  // back to Courses — the membership home — rather than the group list.
  const goBack = onBack ?? (() => navigate(`/membership/${token}/courses`));
  // The logo is a "home" affordance → the membership's landing page (the Home
  // dashboard when it's switched on, else Courses), NOT "leave this group"
  // (that's the back arrow, which uses goBack). Read at click time, by which
  // point the community payload has landed.
  const goHome = () => navigate(sectionPath(token, landingSection((data?.community || communityProp)?.menus)));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [member, setMember] = useState(() => readMember(token));
  const [showProfile, setShowProfile] = useState(false);
  const [showMyPlans, setShowMyPlans] = useState(false);
  // Whether EVERY subscription this member holds has run out — see
  // subscriptionGate.js for the rule. Starts unblocked so nothing is gated
  // while the answer is still in flight.
  const [subGate, setSubGate] = useState({ blocked: false, reason: "pending", expired: [] });
  // Catalog renders its course search up in the header bar. CoursesView portals
  // its <CourseSearch> into this slot node (see the header `extra` below).
  const [courseSearchSlot, setCourseSearchSlot] = useState(null);
  // Creator's left sidebar collapses to an icons-only rail (persisted; shared key
  // with the membership home so it stays consistent across pages).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("creatorSidebarCollapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => setSidebarCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem("creatorSidebarCollapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });
  // In-group sub-view: "feed" | "members" | "leaderboard" | "rewards".
  // These are per-group and only reachable here (i.e. after the member joined).
  // When the parent drives navigation via the URL (?page=), the active section
  // and its setter come from the `page`/`onChangePage` props so the header
  // highlight stays in sync with the address bar; otherwise it's local state.
  const [localSubView, setLocalSubView] = useState("courses");
  const subView = onChangePage ? page || "courses" : localSubView;
  const setSubView = onChangePage || setLocalSubView;

  // Once the notification-driven chat has opened, drop the ?open= param so the
  // URL stays clean and a later refresh doesn't force it open again.
  useEffect(() => {
    if (subView === "memberchat" && openChatToken) {
      const t = setTimeout(() => {
        setSearchParams(
          (prev) => {
            const p = new URLSearchParams(prev);
            p.delete("open");
            return p;
          },
          { replace: true }
        );
      }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [subView, openChatToken, setSearchParams]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showMobileCode, setShowMobileCode] = useState(false);

  // Same brand source as the login page (getfunnel → brand_settings), not the
  // community payload — see useBrand.
  // Aliased: `subscriptionGate` is already the imported RULE function, and
  // destructuring the flag under that name shadowed it inside this component.
  const { primary, brand, subscriptionGate: gateEnabled } = useBrand(token);

  /*
   * The subscription gate. One /get-myplans call per signed-in member, run on
   * mount and again whenever the member changes (so a sign-in re-evaluates it)
   * and after a cancellation, which can move the answer.
   *
   * Failures are swallowed on purpose: if the plans cannot be read we do NOT
   * gate. Locking a paying member out because a request failed is far worse
   * than briefly showing content to one who has lapsed — and the content
   * itself is refused server-side regardless (membership_accesses.expires_at).
   */
  const checkSubGate = useCallback(
    (signal = {}) => {
      /*
       * The creator has to opt in (admin → Settings → Features → Subscription
       * Expiry Popup). When they have not, we do not merely hide the overlay —
       * we never ASK. No get-myplans call, no request per member per load for a
       * membership that may sell nothing by subscription at all.
       */
      if (!gateEnabled) {
        setSubGate({ blocked: false, reason: "feature-off", expired: [] });
        return Promise.resolve();
      }
      if (!member?.community_member_id) {
        setSubGate({ blocked: false, reason: "no-member", expired: [] });
        return Promise.resolve();
      }
      return fetchMyPlans({
        funnelId: member.funnel_id,
        memberId: member.community_member_id,
        jwt: member.member_token,
        // Only the deliberate re-checks go past the cache; the mount read
        // shares whatever the My Plans modal may already have fetched.
        force: !!signal.force,
      })
        .then((plans) => { if (!signal.off) setSubGate(subscriptionGate(plans)); })
        .catch(() => { if (!signal.off) setSubGate({ blocked: false, reason: "error", expired: [] }); });
    },
    [gateEnabled, member?.community_member_id, member?.funnel_id, member?.member_token],
  );

  useEffect(() => {
    const signal = { off: false };
    checkSubGate(signal);
    return () => { signal.off = true; };
    // NOT keyed on showMyPlans. It was, so that a cancellation inside the modal
    // would be picked up — but that fired a fresh gate check on every open AND
    // every close, for a verdict that cancelling cannot change anyway (it caps
    // the window at the period already paid for, it does not end it today).
    // cancelSubscription busts the cached plans instead.
  }, [checkSubGate]);

  /*
   * Re-check when the tab comes back to the foreground.
   *
   * "Buy now" opens the sales page in a NEW TAB, so the member pays somewhere
   * else and returns here — to an overlay still telling them their access has
   * ended, at the exact moment they have just paid for it. Nothing else would
   * clear it short of a full reload. Only while the gate is actually up, so
   * this costs nothing for everyone else.
   */
  useEffect(() => {
    if (!subGate.blocked) return undefined;
    const signal = { off: false, force: true };
    let last = 0;
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      // Returning from the checkout tab is worth a fresh read; bouncing between
      // tabs is not. One forced read per 5s at most.
      if (Date.now() - last < 5000) return;
      last = Date.now();
      checkSubGate(signal);
    };
    // visibilitychange ONLY — deliberately not window's `focus`. That fires
    // every time the page regains focus, including a plain click back into it
    // from devtools or another window, so simply clicking around the overlay
    // fired a get-myplans call each time. Leaving for the checkout tab hides
    // this one, so visibility alone already covers the case this exists for.
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      signal.off = true;
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [subGate.blocked, checkSubGate]);

  // Tab title: the community name (falls back to FlexiMemberships) — otherwise
  // the browser shows the raw URL in the tab.
  useEffect(() => {
    document.title = data?.community?.name || "FlexiMemberships";
  }, [data?.community?.name]);

  // Signed-in member's standing — shared by the feed right rail "My Points"
  // card and the same card on the Members/Leaderboard pages (same source, so
  // the figures stay in sync).
  // The last argument says the Leaderboard page is on screen. It mounts its own
  // full-board request, so the card asks for the same thing there rather than a
  // cheaper-but-different body that would cost the tab a second call.
  const { me: myStanding, pts: myPointsSettings, refresh: refreshMyPoints } = useMyPoints(token, groupToken, member, subView === "leaderboard", groupReady);

  const signOut = () => {
    try {
      localStorage.removeItem(`cm_${token}`);
    } catch {
      /* ignore */
    }
    setMember(null);
    // Keep the parent (MembershipHome) member state in sync and let it own the
    // post-logout navigation (→ the login page). Don't goBack() here, or it
    // would override the parent's redirect to /login.
    onMemberChange?.(null);
  };

  const saveMember = (m) => {
    setMember(m);
    try {
      localStorage.setItem(`cm_${token}`, JSON.stringify(m));
    } catch {
      /* ignore */
    }
    onMemberChange?.(m);
  };

  // ----- In-place auth (no redirect to the landing page) -----
  const [authMode, setAuthMode] = useState(null); // null | "join" | "signin" | "forgot"
  // One-shot welcome flower shower shown right after a successful signup.
  const [welcome, setWelcome] = useState({ show: false, name: "" });
  const [authForm, setAuthForm] = useState(EMPTY_FORM);
  const [authBusy, setAuthBusy] = useState(false);
  const [authMsg, setAuthMsg] = useState({ type: "", text: "" });
  // Forgot-password OTP flow: step 1 email → 2 code → 3 new password.
  const [forgotStep, setForgotStep] = useState(1);
  const [resetToken, setResetToken] = useState("");

  const openAuth = (mode) => {
    // Sign In no longer opens a popup — redirect to the dedicated login page
    // (covers session-out, explicit "Log In", and modal switches to sign in).
    if (mode === "signin") {
      navigate(`/membership/${token}/login`);
      return;
    }
    setAuthMsg({ type: "", text: "" });
    setAuthForm(EMPTY_FORM);
    setForgotStep(1);
    setResetToken("");
    setAuthMode(mode);
  };
  const closeAuth = () => !authBusy && setAuthMode(null);

  // Guests may browse membership pages without a session — no auto sign-in
  // prompt on mount. Signing in is explicit (the "Log In" action redirects to
  // the dedicated /membership/:token/login page).

  // ----- Referral tracking -----
  // The ?ref= code is persisted per community for the session so it survives tab
  // switches in the auth modal and is still available at signup time.
  useEffect(() => {
    if (refCode) {
      try {
        sessionStorage.setItem(`cref_${token}`, refCode);
      } catch {
        /* ignore */
      }
    }
  }, [refCode, token]);
  const getRef = () => {
    if (refCode) return refCode;
    try {
      return sessionStorage.getItem(`cref_${token}`) || undefined;
    } catch {
      return undefined;
    }
  };

  // After sign-in/sign-up on a group page, join the member to this group so the
  // referral link drops them straight into the group. Idempotent server-side.
  const autoJoinGroup = async (m) => {
    if (!groupToken || !m?.auth_token) return null;
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-group-join`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: m.community_member_id,
        member_token: m.auth_token,
        ref: getRef(),
      });
      return res.data || null;
    } catch {
      /* ignore — membership stays joinable from the group card */
      return null;
    }
  };

  const submitJoin = async (e) => {
    e.preventDefault();
    // GDPR: block signup if consent is mandatory and not given.
    if (community?.gdpr?.enabled && community?.gdpr?.mandatory && !authForm.gdpr_consent) {
      return setAuthMsg({ type: "error", text: "Please accept the Privacy and Terms to continue." });
    }
    // Phone: block signup if the community made the phone number mandatory.
    if (Number(community?.phone_required) === 1 && !String(authForm.phone || "").replace(/\D/g, "")) {
      return setAuthMsg({ type: "error", text: "Please enter your phone number to continue." });
    }
    setAuthBusy(true);
    setAuthMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-join`, {
        community_token: token,
        group_token: groupToken,
        ref: getRef(),
        ...authForm,
      });
      if (res.data?.error === 0) {
        const m = res.data.member;
        saveMember(m);
        setAuthMode(null);
        const joinRes = await autoJoinGroup(m);
        // Authorized Members Only: the email may not be on the group's allow-list,
        // so the sign-up succeeds but the group join is refused — surface why.
        if (joinRes?.error === 1) {
          setAuthMsg({ type: "error", text: joinRes?.msg || "You are not authorized to join this community." });
        } else if (!joinRes?.pending) {
            // New customer onboarding: celebrate with a flower shower — but not
            // when the group needs admin approval (the member is only pending).
            setWelcome({ show: true, name: m?.name || "" });
        }
        await load();
      } else {
        setAuthMsg({ type: "error", text: res.data?.msg || "Could not join." });
      }
    } catch (err) {
      setAuthMsg({ type: "error", text: err.response?.data?.msg || "Something went wrong. Please try again." });
    } finally {
      setAuthBusy(false);
    }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthMsg({ type: "", text: "" });
    try {
      // Native membership login (api/login); funnel_id = the community id.
      const m = await nativeLogin({
        funnelId: data?.community?.community_id,
        email: authForm.email,
        password: authForm.password,
      });
      saveMember(m);
      await autoJoinGroup(m);
      await load();
      setAuthMode(null);
      toast.success(`Welcome back, ${m?.name || ""}!`.trim());
    } catch (err) {
      setAuthMsg({ type: "error", text: err.response?.data?.message || err.message || "Something went wrong. Please try again." });
    } finally {
      setAuthBusy(false);
    }
  };

  // 3-step OTP reset: send code → verify code → set new password.
  const submitForgot = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthMsg({ type: "", text: "" });
    try {
      if (forgotStep === 1) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-send-otp`, {
          community_token: token,
          email: authForm.email,
        });
        if (res.data?.success) {
          setResetToken(res.data.token || "");
          setForgotStep(2);
        } else {
          setAuthMsg({ type: "error", text: res.data?.message || "Could not send the code." });
        }
      } else if (forgotStep === 2) {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-verify-otp`, {
          community_token: token,
          email: authForm.email,
          otp: authForm.otp,
          token: resetToken,
        });
        if (res.data?.success) {
          setForgotStep(3);
        } else {
          setAuthMsg({ type: "error", text: res.data?.message || "Invalid code." });
        }
      } else {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/community-reset-password`, {
          community_token: token,
          email: authForm.email,
          otp: authForm.otp,
          token: resetToken,
          password: authForm.password,
          password_confirmation: authForm.password_confirmation,
        });
        if (res.data?.success) {
          setForgotStep(1);
          setAuthMsg({ type: "success", text: res.data?.message || "Your password has been reset." });
        } else {
          setAuthMsg({ type: "error", text: res.data?.message || "Could not reset your password." });
        }
      }
    } catch (err) {
      setAuthMsg({
        type: "error",
        text: err.response?.data?.message || err.response?.data?.msg || "Something went wrong. Please try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  };

  const [leaving, setLeaving] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const leaveGroup = async () => {
    setLeaving(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-group-leave`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
      });
      setConfirmLeave(false);
      toast.success(res.data?.msg || `You left ${group?.name || "the community"}.`);
      goBack();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Could not leave the community. Please try again.");
      setLeaving(false);
    }
  };

  // ----- Posts -----
  const [posts, setPosts] = useState([]);
  // Map of post_id -> AI-generated comment, surfaced live into the PostCard.
  const [aiComments, setAiComments] = useState({});
  // Approve Posts: member's "pending approval" notice + the admin review modal.
  const [pendingNotice, setPendingNotice] = useState(false);
  const [pendingList, setPendingList] = useState(null);
  const [pendingBusyId, setPendingBusyId] = useState(null);
  const [feedFilter, setFeedFilter] = useState("all");
  // Category filter chip row above the feed ("all" or a category id).
  const [activeCategory, setActiveCategory] = useState("all");
  const [isAdmin, setIsAdmin] = useState(false); // current member holds ANY elevated role
  // What this member may actually DO here, from the feed payload's `can`. A
  // moderator is NOT an admin: they may edit/delete any post and delete any
  // comment, but not pin, approve or post in admin-only categories — gating all
  // of those on one `isAdmin` flag handed moderators the full admin set.
  const [perms, setPerms] = useState(NO_PERMS);
  const [postsLoading, setPostsLoading] = useState(true);
  // Paging: the feed loads POSTS_PER_PAGE at a time and appends older posts when
  // "Load more" is clicked. `totalPosts` is the server's full count (the header
  // shows it, not just what's loaded so far).
  const [postsPage, setPostsPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPosts, setTotalPosts] = useState(0);

  // Deep-link to a single post (?post={id}, used by post / reply emails): once
  // the feed has loaded, scroll the matching card into view (done once/target).
  const focusedDoneRef = useRef(null);
  useEffect(() => {
    if (!focusPostId || postsLoading) return;
    if (focusedDoneRef.current === focusPostId) return;
    // When a comment is also targeted, the PostCard scrolls to that comment — don't
    // also scroll to the post (two competing smooth-scrolls read as janky).
    if (focusCommentId) { focusedDoneRef.current = focusPostId; return; }
    const el = document.getElementById(`post-${focusPostId}`);
    if (el) {
      focusedDoneRef.current = focusPostId;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusPostId, focusCommentId, postsLoading, posts]);
  const [composer, setComposer] = useState("");
  const [posting, setPosting] = useState(false);
  // Unified post attachments: uploaded images and/or GIF picks, mixed, up to 10.
  // Each item: { file?: File, url: string (object-URL preview or GIF url), isGif }.
  const [postMedia, setPostMedia] = useState([]);
  const composerFileRef = useRef(null);
  const composerAudioRef = useRef(null);
  const composerVideoRef = useRef(null);
  const [pollMode, setPollMode] = useState(false);
  /*
   * Protected post — a private link (a Zoom room, a workshop doc) members reach
   * through a button instead of seeing the URL. Admins and instructors only
   * (perms.protected_posts); the server re-checks, so forged fields just give an
   * ordinary post. See ./protectedPost.
   */
  const [protectedPost, setProtectedPost] = useState({ on: false, text: "", url: "", expiry: "" });
  const setProtected = (patch) => setProtectedPost((p) => ({ ...p, ...patch }));
  const resetProtected = () => setProtectedPost({ on: false, text: "", url: "", expiry: "" });
  const protectedProblem = protectedError(protectedPost);
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoFilePreview, setVideoFilePreview] = useState("");
  const [postAudio, setPostAudio] = useState(null);
  const [postAudioPreview, setPostAudioPreview] = useState("");
  const POST_MEDIA_MAX = 10;
  // Skool-style composer: a trigger row opens this "Create Post" modal.
  const [composerOpen, setComposerOpen] = useState(false);
  /**
   * The same modal edits an existing post. null = creating.
   *
   * A post is COMPOSED in a popup, so it should be EDITED in one — and in the
   * same one, or the two drift: the inline editor this replaces had no category
   * field, because category was added to create only and nobody remembered the
   * second copy.
   *
   * `apply` is the callback the card hands over so the saved result can be
   * pushed back into its local state — PostCard keeps its own `content` /
   * `images` / `poll` state, so updating the feed array alone would not show.
   *
   * NOTE `createPost` is deliberately NOT touched by any of this. Editing gets
   * its own submit (`submitComposerEdit`); the posting path — the most-used in
   * the app — keeps exactly the code it had.
   */
  const [composerEdit, setComposerEdit] = useState(null); // { post, apply }

  // Coloured background for a TEXT post — the stored string from
  // postBackgrounds.js ("" = none), passed straight to the API.
  const [postBg, setPostBg] = useState("");
  // Which upload drop-zone the modal shows: 'photo' | 'audio' | 'video' | null.
  const [mediaMode, setMediaMode] = useState(null);
  // The link whose preview card the author closed ("" = none). Keyed by URL so
  // pasting a different link brings a preview back, as on Facebook.
  const [hiddenPreviewUrl, setHiddenPreviewUrl] = useState("");

  // @mention picker: this group's members (id + name + img) feed the composer's
  // autocomplete; picks insert @[name](id) markup counted by the tag-limit gate.
  const [mentionMembers, setMentionMembers] = useState([]);
  useEffect(() => {
    // Composer-only data — only the feed section renders the composer, so don't
    // fetch it on Courses/other sections (path-based loading).
    if (subView !== "feed") return;
    if (!groupToken || !member?.auth_token) return;
    let cancelled = false;
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-group-mention-members`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
      })
      .then((res) => {
        if (!cancelled) setMentionMembers(res.data?.members || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [subView, groupToken, token, member?.auth_token, member?.community_member_id]);

  // Post categories for this group (membership_group_categories) — populate the
  // composer's Category dropdown. A new post starts with NO category: picking
  // one is the member's call, and seeding the first category quietly filed
  // every post under whichever one happened to sort first.
  const [categories, setCategories] = useState([]);
  //   null   no category — the default, and also an explicit choice
  //   <id>   a category the member picked
  const [postCategory, setPostCategory] = useState(null);
  useEffect(() => {
    // Composer category dropdown — feed section only.
    if (subView !== "feed") return;
    if (!groupToken) return;
    let cancelled = false;
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-group-categories`, {
        community_token: token,
        group_token: groupToken,
      })
      .then((res) => {
        if (cancelled) return;
        const cats = res.data?.categories || [];
        setCategories(cats);
        // Nothing is selected on the member's behalf — the composer opens on
        // "No category" and stays there until they choose otherwise.
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [subView, groupToken, token]);

  // ----- Coloured background: when it is allowed, and what it renders as -----
  // A background belongs to a SHORT, TEXT-ONLY post. Anything attached takes the
  // card over — a colour behind a photo is just a border — and past the length
  // cap there is no type size that still reads as a statement rather than a
  // paragraph on a coloured box.
  const bgHasMedia =
    pollMode ||
    // The type being SELECTED is enough. Waiting for a file to actually land
    // left the palette sitting under an empty "Add Photo" drop-zone, offering a
    // choice that the next click would throw away.
    !!mediaMode ||
    postMedia.length > 0 ||
    !!postAudio ||
    !!postAudioPreview ||
    !!videoFile ||
    !!videoFilePreview;
  const bgTooLong = composer.length > POST_BG_MAX_CHARS;
  const bgAllowed = !bgHasMedia && !bgTooLong;
  // The background actually in force. Derived rather than read straight from
  // state so the canvas disappears the instant a photo is attached, without
  // waiting on an effect — the state itself is cleaned up just below.
  const activeBg = bgAllowed ? parseBg(postBg) : null;

  // Attaching media ends the choice outright — the picker is gone, so leaving
  // the value set would let a colour reappear later with no way to have asked
  // for it. Going over the LENGTH cap deliberately does NOT clear it: that is a
  // state you type your way back out of, and wiping the colour on the character
  // that crossed the line would mean re-picking it to delete one word.
  useEffect(() => {
    if (postBg && bgHasMedia) setPostBg("");
  }, [postBg, bgHasMedia]);

  // Categories the current member may POST to — admin-only ones (e.g.
  // Announcements) are hidden from regular members in the composer. The filter
  // chips still show every category (anyone can view/filter them).
  const composerCategories = categories.filter((c) => perms.admin_categories || !Number(c.admin_only));
  // The category actually sent with a new post. Resolved at SEND time, not from
  // state: the categories list and the member's permissions arrive separately,
  // so a selection made from a stale list can still be sitting in state when the
  // member hits Post — and an admin-only one makes the server reject the whole
  // post with "Only admins can post in the … category". Anything the member may
  // not post to becomes null here, which the server accepts as "no category".
  const postCategoryId = composerCategories.some(
    (c) => String(c.membership_group_category_id) === String(postCategory)
  )
    ? postCategory
    : null;
  // Keep the composer's selected category valid for this member (e.g. if the
  // category was deleted, or is admin-only and this member is not an admin).
  useEffect(() => {
    if (postCategory === null) return; // "No category" — nothing to correct
    const list = categories.filter((c) => perms.admin_categories || !Number(c.admin_only));
    if (!list.some((c) => String(c.membership_group_category_id) === String(postCategory))) {
      // The selection is gone, or is admin-only and this member is not an admin
      // (sending it makes the server answer "Only admins can post in the …
      // category" and blocks the post outright). Fall back to no category
      // rather than to another one the member never asked for.
      setPostCategory(null);
    }
  }, [categories, perms.admin_categories, postCategory]);

  const postMentions = useMentions({
    value: composer,
    setValue: setComposer,
    members: mentionMembers,
    primary,
  });

  // Auto-grow the composer textarea so the Create Post popup gets taller as the
  // member types, up to the field's CSS max-height (viewport-relative) — after
  // which the textarea scrolls instead of pushing the popup past the screen.
  // Runs on every content change and whenever the modal (re)opens.
  useLayoutEffect(() => {
    if (!composerOpen) return;
    const el = postMentions.ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    // `postBg` is in here because switching to a coloured canvas changes the
    // type size without changing the text, so scrollHeight moves on its own.
  }, [composer, composerOpen, postBg, postMentions.ref]);

  // Add one or more uploaded images (mixes freely with GIFs; audio/video are
  // exclusive so they get cleared).
  const addPostImages = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    clearPostAudio();
    clearVideoFile();
    setPollMode(false);
    setMediaMode("photo");
    const accepted = acceptImages(arr, postMedia.length, POST_MEDIA_MAX)
      .map((file) => ({ file, url: URL.createObjectURL(file), isGif: false }));
    if (accepted.length) setPostMedia((cur) => [...cur, ...accepted].slice(0, POST_MEDIA_MAX));
    if (composerFileRef.current) composerFileRef.current.value = "";
  };
  // Files pasted or dropped anywhere on the Create post popup — no need to
  // pick Photo / Video / Audio first, the way Facebook does it. Several images
  // at once all go in. Returns true when it took something.
  const attachComposerFiles = (files) => {
    const images = files.filter((f) => f.type.startsWith("image/"));
    const video = files.find((f) => f.type.startsWith("video/"));
    const audio = files.find((f) => f.type.startsWith("audio/"));
    if (images.length && !photoDisabled) {
      // Photos are additive, so they would silently wipe a poll, audio or video
      // already in the post. Say so instead.
      if (pollMode || postAudio || videoFile) {
        toast.info("Remove the poll, audio or video first to add photos.");
      } else {
        addPostImages(images);
      }
      return true;
    }
    if (video && !videoDisabled) {
      pickVideoFile(video);
      return true;
    }
    if (audio && !audioDisabled) {
      pickPostAudio(audio);
      return true;
    }
    return false;
  };
  // Text pastes are left alone: Excel/Word put a rendered PNG on the clipboard
  // *beside* the text, and the text is what the member meant to paste.
  const pasteComposerFiles = (e) => {
    if (!e.clipboardData || e.clipboardData.getData("text/plain")) return;
    if (attachComposerFiles(transferFiles(e.clipboardData))) e.preventDefault();
  };
  const [composerDrag, setComposerDrag] = useState(false);
  const composerDragProps = {
    onDragOver: (e) => {
      // Only file drags — moving selected text within the textarea still works.
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      if (!composerDrag) setComposerDrag(true);
    },
    onDragLeave: (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) setComposerDrag(false);
    },
    onDrop: (e) => {
      setComposerDrag(false);
      // The photo drop-zone / Add tile already handled it.
      if (e.defaultPrevented || !dragHasFiles(e)) return;
      // Always swallow a file drop: unhandled, the browser opens the file in
      // the tab and the half-written post is gone.
      e.preventDefault();
      if (!attachComposerFiles(transferFiles(e.dataTransfer))) {
        toast.warn("That file type can't be added to a post.");
      }
    },
  };
  const pickPostGif = (url) => {
    if (!url) return;
    clearPostAudio();
    clearVideoFile();
    setPollMode(false);
    setMediaMode("photo");
    setPostMedia((cur) => cur.length >= POST_MEDIA_MAX ? cur : [...cur, { url, isGif: true }]);
  };
  // Photo editor (crop / rotate / adjust) on a picked image — index into postMedia.
  const [editingMedia, setEditingMedia] = useState(null);
  const saveEditedImage = (file) => {
    const i = editingMedia;
    setEditingMedia(null);
    if (i === null || !withinMediaLimit(file, "image")) return;
    setPostMedia((cur) => cur.map((m, j) => (j === i ? { ...m, file, url: URL.createObjectURL(file) } : m)));
  };
  const removePostMedia = (i) =>
    setPostMedia((cur) => {
      const next = cur.filter((_, idx) => idx !== i);
      // Last one gone: this is a text post again. Leaving mediaMode on "photo"
      // kept the drop-zone up and the backdrop palette hidden for a post with
      // no photo in it.
      if (next.length === 0) setMediaMode(null);
      return next;
    });
  const clearPostMedia = () => {
    setPostMedia([]);
    if (composerFileRef.current) composerFileRef.current.value = "";
  };
  const pickPostAudio = (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) return;
    if (!withinMediaLimit(file, "audio")) return;
    clearPostMedia();
    clearVideoFile();
    setPollMode(false);
    setPostAudio(file);
    setPostAudioPreview(URL.createObjectURL(file));
  };
  const clearPostAudio = () => {
    setPostAudio(null);
    setPostAudioPreview("");
    if (composerAudioRef.current) composerAudioRef.current.value = "";
  };
  const pickVideoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.warn("Choose a video file.");
      return;
    }
    if (!withinMediaLimit(file, "video")) return;
    clearPostMedia();
    clearPostAudio();
    setPollMode(false);
    setVideoFile(file);
    setVideoFilePreview(URL.createObjectURL(file));
  };
  const clearVideoFile = () => {
    setVideoFile(null);
    setVideoFilePreview("");
    if (composerVideoRef.current) composerVideoRef.current.value = "";
  };

  /**
   * The X on the video preview.
   *
   * A link-based video lives in the post BODY — that is where the composer
   * found it and where a save reads it back. Clearing only the preview
   * therefore removed nothing: the URL was still in the text box, so the next
   * save detected it again and the player came straight back. Taking the URL
   * out of the text with it makes the button mean what it says, and keeps the
   * editor honest about where the video comes from.
   *
   * An uploaded file has no URL in the text, so the replace is a no-op there.
   */
  const removeComposerVideo = () => {
    const url = videoFilePreview;
    clearVideoFile();
    if (url && /^https?:\/\//i.test(url)) {
      setComposer((t) => t.split(url).join("").replace(/[ \t]+\n/g, "\n").trim());
    }
  };

  // Media-mode handlers shared by the trigger row and the modal toolbar. Each
  // switches the modal into that upload mode and shows its drop-zone — the file
  // picker only opens when the user clicks the drop-zone itself.
  // Tapping the ACTIVE type turns it off again and puts the post back to plain
  // text — which is also how you get the backdrop palette back. Poll has always
  // toggled; the three media types did not, so once you picked one there was no
  // way out of it short of closing the composer and losing what you had typed.
  // Toggling off clears that type's staged files, for the same reason Poll
  // clears its options: a post cannot be half an image post.
  const startPhoto = () => {
    if (mediaMode === "photo") {
      clearPostMedia();
      setMediaMode(null);
      return;
    }
    setPollMode(false);
    clearPostAudio();
    clearVideoFile();
    setMediaMode("photo");
  };
  const startAudio = () => {
    if (mediaMode === "audio") {
      clearPostAudio();
      setMediaMode(null);
      return;
    }
    setPollMode(false);
    clearPostMedia();
    clearVideoFile();
    setMediaMode("audio");
  };
  const startVideo = () => {
    if (mediaMode === "video") {
      clearVideoFile();
      setMediaMode(null);
      return;
    }
    setPollMode(false);
    clearPostMedia();
    clearPostAudio();
    setMediaMode("video");
  };
  const togglePoll = () => {
    setMediaMode(null);
    clearPostMedia();
    clearPostAudio();
    clearVideoFile();
    setPollMode((v) => {
      if (!v) setPollOptions(["", ""]);
      return !v;
    });
  };

  // Open the Create Post modal, optionally jumping straight into a media mode.
  const openComposer = (mode = "text") => {
    setComposerOpen(true);
    if (mode === "photo") startPhoto();
    else if (mode === "audio") startAudio();
    else if (mode === "video") startVideo();
    else if (mode === "poll") {
      setMediaMode(null);
      setPollOptions(["", ""]);
      setPollMode(true);
    } else {
      setMediaMode(null);
      setPollMode(false);
    }
  };

  // page 1 replaces the list (initial load / refresh); later pages append the
  // next batch of older posts for infinite scroll.
  const loadPosts = useCallback(async (page = 1) => {
    if (page === 1) setPostsLoading(true);
    else setLoadingMore(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-posts`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
        // Proves membership to the backend gate for private/approval groups.
        member_token: member?.auth_token,
        page,
        limit: POSTS_PER_PAGE,
      });
      if (res.data?.error === 0) {
        const batch = res.data.posts || [];
        setPosts((prev) => {
          if (page === 1) return batch;
          // Guard against duplicates: offset paging can re-serve a row if a new
          // post lands between requests and shifts everything down a slot.
          const seen = new Set(prev.map((p) => p.post_id));
          return [...prev, ...batch.filter((p) => !seen.has(p.post_id))];
        });
        setIsAdmin(!!res.data.is_admin);
        setPerms({ ...NO_PERMS, ...(res.data.can || {}) });
        setHasMorePosts(!!res.data.has_more);
        setTotalPosts(res.data.total ?? batch.length);
        setPostsPage(page);
      }
    } catch {
      /* ignore */
    } finally {
      setPostsLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, groupToken, member?.community_member_id]);

  useEffect(() => {
    // The post list only renders in the feed section — don't load it on
    // Courses/other sections (path-based loading).
    if (subView !== "feed") return;
    loadPosts();
  }, [subView, loadPosts]);

  // "Load more" pulls the next page of older posts on demand (POSTS_PER_PAGE at
  // a time). Guarded so a double-click can't fire two requests for the same page.
  const loadMorePosts = () => {
    if (!hasMorePosts || postsLoading || loadingMore) return;
    loadPosts(postsPage + 1);
  };

  // A deep-linked post (?post= from a notification or an email) is only on the
  // first page if it's recent. When it isn't, keep pulling older pages until it
  // turns up, so the link lands on the post instead of silently dropping the
  // member at the top of the feed. Bounded — an id that isn't in this group at
  // all (deleted post, wrong group) must not walk the whole feed.
  const autoPagesRef = useRef(0);
  useEffect(() => {
    autoPagesRef.current = 0;
  }, [focusPostId, groupToken]);
  useEffect(() => {
    if (!focusPostId || subView !== "feed") return;
    if (postsLoading || loadingMore || !hasMorePosts) return;
    if (posts.some((p) => String(p.post_id) === String(focusPostId))) return;
    if (autoPagesRef.current >= FOCUS_MAX_AUTO_PAGES) return;
    autoPagesRef.current += 1;
    loadPosts(postsPage + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPostId, subView, posts, postsLoading, loadingMore, hasMorePosts, postsPage]);

  // After a post is created, ask the backend to (maybe) add the group's
  // automatic AI reply. The endpoint self-checks the group setting and no-ops
  // when it's off, so this is safe to call for every new post. Best-effort:
  // bumps the post's comment count when a reply is generated.
  const triggerAiComment = useCallback(
    async (post) => {
      if (!post?.post_id) return;
      try {
        // Goes to the membership ADMIN app, not the bridge. Only that app can
        // read the group's "Reply Tone" answers (Group Settings → Automatic AI
        // reply → Reply Tone) and shape the prompt with them; the bridge has a
        // fixed prompt and its own OpenAI key. It derives the group from the
        // post, so no group/member ids are sent.
        const res = await axios.post(`${config.MEMBERSHIP_API_URL}/membership-ai-reply`, {
          membership_token: token,
          post_id: post.post_id,
        });
        if (res.data?.generated === 1 && res.data.comment) {
          // Surface the generated reply live (no page refresh): hand it to the
          // matching PostCard, which injects it into its comment thread.
          setAiComments((m) => ({ ...m, [post.post_id]: res.data.comment }));
          setPosts((ps) =>
            ps.map((p) =>
              p.post_id === post.post_id
                ? { ...p, comment_count: (p.comment_count || 0) + 1 }
                : p
            )
          );
        }
      } catch {
        /* best-effort — AI reply is non-critical */
      }
    },
    [token, groupToken, member?.community_member_id, member?.auth_token]
  );

  // Ask the membership ADMIN app to email the group about this new post.
  //
  // Goes straight to fleximembership (Laravel), which owns the notification
  // model / job / controller and dispatches the send job. Authenticated with
  // `membership_token` (the project slug — the naming the admin app's own member
  // endpoints use) plus the sealed member_token: a browser cannot hold the
  // server-to-server secret, and the admin side only accepts a post the caller
  // authored.
  //
  // Fire-and-forget: the post is already on screen, so a failure here must never
  // surface as a posting error. The admin side is idempotent per post, so a retry
  // or a double render cannot mail the group twice.
  const notifyNewPost = useCallback(
    async (post) => {
      if (!post?.post_id) return;
      try {
        await axios.post(`${config.MEMBERSHIP_API_URL}/membership-post-notify`, {
          membership_token: token,
          member_token: member?.auth_token,
          post_id: post.post_id,
        });
      } catch {
        /* best-effort — email notification is non-critical to posting */
      }
    },
    [token, member?.auth_token]
  );

  // Drop a freshly created post into the feed — unless the group holds member
  // posts for approval, in which case it isn't shown and we tell the member.
  /** Reset the composer to "creating an empty post" and shut it. */
  const closeComposer = () => {
    setComposerOpen(false);
    setComposerEdit(null);
    resetProtected();
    setComposer("");
    clearPostMedia();
    clearPostAudio();
    clearVideoFile();
    setPollMode(false);
    setPollOptions(["", ""]);
    setMediaMode(null);
    setPostBg("");
    setHiddenPreviewUrl("");
    setPostCategory(null); // back to "No category" for the next post
  };

  /** Load an existing post into the composer and open it in edit mode. */
  const openComposerForEdit = (post, apply) => {
    const imgs = (post.images?.length ? post.images : post.image ? [post.image] : []).map((u) =>
      String(u).replace(/\?t=\d+$/, ""),
    );
    setComposer(post.content || "");
    // `isExisting` marks a URL the server already holds, so a save keeps it
    // rather than re-uploading it (see resolveComposerImages).
    setPostMedia(imgs.map((u) => ({ url: u, isExisting: true })));
    setPollMode(!!post.poll);
    setPollOptions(post.poll?.options?.map((o) => o.text) || ["", ""]);
    setPostAudio(null);
    setPostAudioPreview(post.audio || "");
    setVideoFile(null);
    setVideoFilePreview(post.video || "");
    setMediaMode(imgs.length ? "photo" : post.audio ? "audio" : post.video ? "video" : null);
    setPostCategory(post.membership_group_category_id ?? null);
    // `bg` only ever comes back on a text post (the API nulls it for anything
    // else), so this is safe to set unconditionally.
    setPostBg(post.bg || "");
    setHiddenPreviewUrl(post.hide_link_preview ? extractLinkUrl(post.content || "") || "" : "");
    setComposerEdit({ post, apply });
    setComposerOpen(true);

    /*
     * Protected link. The feed payload has the label and the expiry but never
     * the URL, so the editor has to ask for the stored one — the only other
     * place it leaves the server, and only for someone who may edit this post.
     * Failing quietly is right: the rest of the edit still works, the fields
     * just start empty rather than showing a link that isn't there.
     */
    resetProtected();
    if (post.protected_link && member?.auth_token) {
      axios
        .post(`${config.COMMUNITY_API_URL}/membership-post-protected-detail`, {
          community_token: token,
          member_token: member.auth_token,
          post_id: post.post_id,
        })
        .then((res) => {
          const d = res.data?.protected;
          if (res.data?.error === 0 && d?.on) {
            setProtectedPost({
              on: true,
              text: d.button_text || "",
              url: d.url || "",
              // Stored as a UTC instant; the input wants the local wall clock.
              expiry: expiryToLocalInput(d.expires_at),
            });
          }
        })
        .catch(() => {
          /* the link stays as it is — the editor simply cannot prefill it */
        });
    }
  };

  const addCreatedPost = (res) => {
    if (res.data?.pending) {
      // Held for approval — the group is emailed when an admin approves it.
      // No points toast: the backend defers the award to the approval step, so
      // points_awarded is 0 here and announcing one would be a lie.
      setPendingNotice(true);
      return;
    }
    setPosts((ps) => {
      // Newest first, but BELOW the pinned block — the server orders by
      // `pinned DESC, id DESC`, so prepending blindly put a brand-new post
      // above the pinned ones until the next refetch quietly corrected it.
      // Splicing it in at the first unpinned slot makes the optimistic order
      // match what a reload gives you.
      const firstUnpinned = ps.findIndex((p) => !Number(p.pinned));
      const at = firstUnpinned === -1 ? ps.length : firstUnpinned;
      return [...ps.slice(0, at), res.data.post, ...ps.slice(at)];
    });
    toastPoints(res.data.points_awarded, "Posted.");
    triggerAiComment(res.data.post);
    notifyNewPost(res.data.post);
  };

  // ----- Approve Posts (admin review of pending member posts) -----
  const loadPending = useCallback(async () => {
    setPendingList(null);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-pending-posts`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
      });
      setPendingList(res.data?.error === 0 ? res.data.posts || [] : []);
    } catch {
      setPendingList([]);
    }
  }, [token, groupToken, member?.community_member_id, member?.auth_token]);

  const reviewPending = async (post, action) => {
    setPendingBusyId(post.post_id);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-approve`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
        action,
      });
      if (res.data?.error === 0) {
        setPendingList((ps) => (ps || []).filter((x) => x.post_id !== post.post_id));
        if (action === "approve") {
          setPosts((ps) => [post, ...(ps || [])]);
          toast.success("Post approved.");
        } else {
          toast.info("Post rejected.");
        }
      } else {
        toast.error(res.data?.msg || "Could not update the post.");
      }
    } catch {
      toast.error("Could not update the post.");
    } finally {
      setPendingBusyId(null);
    }
  };

  // Load pending posts whenever the admin opens the Pending Posts page.
  useEffect(() => {
    if (subView === "pendingpost") loadPending();
  }, [subView, loadPending]);

  /**
   * Save the composer over an existing post. Separate from `createPost` on
   * purpose: the create path is the app's busiest and stays byte-for-byte as it
   * was. Mirrors the branches the edit endpoint understands — poll options,
   * audio replace, and text + image reconciliation.
   */
  /** Did the author close the preview card for the link that's in the text now? */
  const linkPreviewHidden = () => {
    const link = extractLinkUrl(composer);
    return !!link && link === hiddenPreviewUrl;
  };

  const submitComposerEdit = async () => {
    const target = composerEdit?.post;
    if (!target) return;
    const content = postMentions.serialize(composer.trim());
    /*
     * Protected link. `manage_protected` is what tells the API this editor is
     * responsible for the link: without it the API keeps whatever is stored, so
     * an editor that doesn't show the fields can never strip one off. Sent on
     * the text and image branches — the two a link can ride on.
     */
    const managesProtected = !pollMode && !postAudio;
    const protectedEdit = managesProtected
      ? { manage_protected: 1, ...protectedFields(protectedPost), ...(protectedPost.on ? {} : { protected_post: 0 }) }
      : {};

    const base = {
      community_token: token,
      community_member_id: member?.community_member_id,
      member_token: member?.auth_token,
      post_id: target.post_id,
      content,
      hide_link_preview: linkPreviewHidden() ? 1 : 0,
    };
    setPosting(true);
    try {
      let res;
      if (pollMode) {
        const opts = pollOptions.map((o) => o.trim()).filter(Boolean);
        if (!content) return toast.warn("Add a question for your poll.");
        if (opts.length < 2) return toast.warn("A poll needs at least 2 options.");
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, {
          ...base,
          poll_options: JSON.stringify(opts),
        });
      } else if (postAudio) {
        const audio_url = await uploadFile(postAudio, {
          communityToken: token,
          memberId: member?.community_member_id,
        });
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, {
          ...base,
          ...(audio_url ? { audio_url } : {}),
        });
      } else if (postMedia.length > 0 || (target.images?.length || target.image)) {
        // manage_images tells the API this is the complete new set: keep_images
        // are the ones already stored that survived, image_urls are new uploads.
        const fd = new FormData();
        Object.entries(base).forEach(([k, v]) => fd.append(k, v));
        Object.entries(protectedEdit).forEach(([k, v]) => fd.append(k, v));
        fd.append("manage_images", "1");
        const { keep, fresh } = await resolveComposerImages(postMedia, {
          communityToken: token,
          memberId: member?.community_member_id,
        });
        keep.forEach((u) => fd.append("keep_images[]", u));
        fresh.forEach((u) => fd.append("image_urls[]", u));
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        /*
         * Video follows the TEXT, on this branch only.
         *
         * This editor has no video field of its own — the composer reads a link
         * out of the body, exactly as createPost does. So the body is the
         * single source of truth for the video here:
         *
         *   a link in the text        -> that video
         *   no link, but there was one -> remove it
         *
         * Until now none of these branches sent video at all, so a link pasted
         * while editing was ignored and a video could never be removed —
         * whatever the editor appeared to offer. The card's own saveEdit path
         * handled it, but that is not the editor this modal uses.
         */
        const detectedVideo = extractVideoUrl(content);
        const hadVideo = Boolean(target.video);

        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, {
          ...base,
          ...protectedEdit,
          // Sent on the text branch ONLY, and sent even when empty — "" is how
          // the picker says "remove the colour". The other branches leave the
          // field off entirely, so the API keeps whatever the post already had
          // (or clears it itself, once media makes it ineligible).
          post_bg: activeBg ? postBg : "",
          ...(detectedVideo ? { video_url: detectedVideo } : {}),
          ...(!detectedVideo && hadVideo ? { remove_video: "1" } : {}),
        });
      }

      if (res?.data?.error === 0) {
        // Hand the result back to the card that asked for the edit; it holds
        // its own copy of the post's content.
        composerEdit.apply?.(res.data);
        setPosts((ps) =>
          ps.map((x) =>
            x.post_id === target.post_id
              ? {
                  ...x,
                  content: res.data.content,
                  bg: res.data.bg ?? null,
                  hide_link_preview: !!res.data.hide_link_preview,
                  // null when the link was removed, which is exactly what the
                  // card needs to drop the button.
                  protected_link: res.data.protected_link ?? null,
                }
              : x,
          ),
        );
        closeComposer();
      } else {
        toast.error(res?.data?.msg || "Could not save your changes.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Could not save your changes.");
    } finally {
      setPosting(false);
    }
  };

  const createPost = async () => {
    const content = postMentions.serialize(composer.trim());
    if (pollMode) {
      const opts = pollOptions.map((o) => o.trim()).filter(Boolean);
      if (!content) return toast.warn("Add a question for your poll.");
      if (opts.length < 2) return toast.warn("A poll needs at least 2 options.");
      setPosting(true);
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          membership_group_category_id: postCategoryId,
          content,
          poll_options: JSON.stringify(opts),
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer("");
          setComposerOpen(false);
          refreshMyPoints();
          setPollMode(false);
          setPollOptions(["", ""]);
        } else {
          toast.error(res.data?.msg || "Could not create poll.");
        }
      } catch {
        /* ignore */
      } finally {
        setPosting(false);
      }
      return;
    }
    if (videoFile) {
      setPosting(true);
      try {
        // The video goes to the media service, and the post carries its URL —
        // the same path a pasted video link already took.
        const video_url = await uploadFile(videoFile, {
          communityToken: token,
          memberId: member?.community_member_id,
        });
        const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          content,
          membership_group_category_id: postCategoryId ?? "",
          video_url,
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer("");
          setComposerOpen(false);
          refreshMyPoints();
          setMediaMode(null);
          clearVideoFile();
        } else {
          toast.error(res.data?.msg || "Could not post video.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.msg || err?.message || "Could not post video.");
      } finally {
        setPosting(false);
      }
      return;
    }
    if (postAudio) {
      setPosting(true);
      try {
        const audio_url = await uploadFile(postAudio, {
          communityToken: token,
          memberId: member?.community_member_id,
        });
        const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          content,
          membership_group_category_id: postCategoryId ?? "",
          audio_url,
        });
        if (res.data?.error === 0) {
          addCreatedPost(res);
          setComposer("");
          setComposerOpen(false);
          refreshMyPoints();
          clearPostAudio();
        } else {
          toast.error(res.data?.msg || "Could not post audio.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.msg || err?.message || "Could not post audio.");
      } finally {
        setPosting(false);
      }
      return;
    }
    const hasMedia = postMedia.length > 0;
    if (!content && !hasMedia) return;
    // Also auto-detect a video link pasted directly in the text (only when there
    // are no image/GIF attachments).
    const detectedVideo = !hasMedia ? extractVideoUrl(content) : null;
    // The protected link rides on THIS branch only — text and photo posts. The
    // poll, video and audio branches returned above, and the composer hides the
    // block in those modes, so a link can never be silently dropped.
    const protectedPayload = perms.protected_posts ? protectedFields(protectedPost) : {};
    setPosting(true);
    try {
      let res;
      if (hasMedia) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member?.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("content", content);
        fd.append("membership_group_category_id", postCategoryId ?? "");
        Object.entries(protectedPayload).forEach(([k, v]) => fd.append(k, v));
        // Photos upload to the media service first; the post then carries URLs
        // only (same field GIF picks use), so nothing is written to the
        // membership API's own storage. Mixed picks and GIFs keep their order.
        const { fresh } = await resolveComposerImages(postMedia, {
          communityToken: token,
          memberId: member?.community_member_id,
        });
        fresh.forEach((u) => fd.append("image_urls[]", u));
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id,
          member_token: member?.auth_token,
          membership_group_category_id: postCategoryId,
          content,
          ...protectedPayload,
          ...(linkPreviewHidden() ? { hide_link_preview: 1 } : {}),
          ...(detectedVideo ? { video_url: detectedVideo } : {}),
          // Only on a genuine text post. A pasted video link turns this into a
          // video post server-side, which would drop the colour anyway — not
          // sending it keeps the two ends telling the same story.
          ...(activeBg && !detectedVideo ? { post_bg: postBg } : {}),
        });
      }
      if (res.data?.error === 0) {
        addCreatedPost(res);
        setComposer("");
        setPostBg("");
        setComposerOpen(false);
        refreshMyPoints();
        clearPostMedia();
        resetProtected();
      } else if (res.data?.msg) {
        // e.g. the member was banned from posting by the admin Moderation panel.
        toast.error(res.data.msg);
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Could not post.");
    } finally {
      setPosting(false);
    }
  };


  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // No request here at all any more.
      //
      // This was /membership-feed, then a lean /membership-groups call for the
      // active group. Both existed for one reason: the shell could not name the
      // group it was rendering until the parent's groups request came back, so
      // it had to ask afterwards. The parent now asks for that group on the SAME
      // request (active_group_token / with_active_group), and hands the answer
      // down — so the page went from two sequential membership-groups round
      // trips to one.
      //
      // The fetch below is kept for the case where nothing was handed down: the
      // shell rendered outside MembershipHome, or against a server that predates
      // active_group. Same object either way — both are built by groupDetail().
      let res;
      if (communityProp && activeGroupProp !== undefined) {
        res = { data: { error: 0, active_group: activeGroupProp } };
      } else {
        res = await cachedPost(`${config.COMMUNITY_API_URL}/membership-groups`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member?.community_member_id || undefined,
          active_only: 1,
        }, { ttl: 10000 });
      }
      if (res.data?.error === 0) {
        // Same shape the feed response had, assembled from the two sources.
        const community = communityProp || null;
        setData({ community, group: res.data.active_group ?? null, total_members: totalMembersProp ?? 0 });
        applyCommunityFont(community?.membership_font);
        applyCommunityDirection(community?.rtl);
        applyCommunityTheme(community?.brand);
      } else setError(res.data?.msg || "Unable to load this membership.");
    } catch (err) {
      setError(err.response?.data?.msg || "Something went wrong while loading the feed.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, groupToken, communityProp, totalMembersProp, activeGroupProp]);

  useEffect(() => {
    // Shell bootstrap: sets `data` (community meta), applies theme, and clears
    // the global `loading` gate (see `if (loading)` below). Must run on EVERY
    // subview — gating it to the feed left non-feed routes stuck on the loader.
    load();
  }, [load]);

  // Keep the avatar/profile button in sync with the saved profile photo.
  useEffect(() => {
    const mid = member?.community_member_id;
    if (!mid) return;
    let active = true;
    fetchProfile({ funnelId: member?.funnel_id, memberId: mid, jwt: member?.member_token })
      .then((d) => ({ data: d }))
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        const fresh = res.data.member;
        if (fresh && (fresh.img !== member?.img || fresh.name !== member?.name)) {
          saveMember({ ...member, name: fresh.name, email: fresh.email, img: fresh.img });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, member?.community_member_id]);

  if (loading) {
    return (
      <div className="relative min-h-screen bg-canvas">
        <PageLoader
          page={{ feed: "Community", courses: "Courses", members: "Members", map: "Map", leaderboard: "Leaderboard", rewards: "Rewards", events: "Events", memberchat: "Members Chat", about: "About", pendingpost: "Pending posts", earnings: "Earnings" }[subView] || "Community"}
          logo={data?.community?.logo || undefined}
          primary={primary}
        />
        {/* Keep the welcome celebration visible even while the feed reloads
            right after signup (load() flips `loading`). */}
        <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-canvas px-4 gap-3">
        <p className="text-red-500 text-center">{error}</p>
        <button onClick={goBack} className="text-sm underline" style={{ color: primary }}>
          ← Back to community
        </button>
      </div>
    );
  }

  const { community, group, total_members } = data;
  // Group settings can hide post-type options from the composer (1 = hide).
  const photoDisabled = Number(group?.disable_photo_post) === 1;
  const audioDisabled = Number(group?.disable_audio_post) === 1;
  const videoDisabled = Number(group?.disable_video_post) === 1;
  const pollDisabled = Number(group?.disable_poll_post) === 1;
  // Nav menus can be hidden per-group (Group Settings → group.menus) and/or
  // membership-wide (Settings → Membership Menus → community.menus). A menu is
  // hidden when EITHER flag is 1 (project-wide overrides, per-group still applies).
  const groupMenus = group?.menus || {};
  const communityMenus = community?.menus || {};
  const menuHidden = (mk) =>
    !!mk && (Number(groupMenus[mk]) === 1 || Number(communityMenus[mk]) === 1);
  // Nav tabs visible after applying the group + membership menu-hide settings.
  const visibleNavTabs = (navTabs || []).filter((t) => {
    // Feed is no longer a navbar tab anywhere — it lives inside the group and is
    // reached via the /:token/feed slug (or entering a group), not a nav button.
    if (t.key === "feed") return false;
    // Home is opt-in: shown only when explicitly switched on (see homeEnabled).
    if (t.key === "home") return homeEnabled(communityMenus);
    return !menuHidden(TAB_MENU_KEY[t.key]);
  });
  // Group-scoped pages (drive which nav set / About + Pending tabs apply).
  const inGroupContext = GROUP_SCOPED_PAGES.includes(subView);
  // The "Feed" item is intentionally hidden from the top-header nav. The feed is
  // still reachable (group switcher / back actions call setSubView("feed")), and
  // all subView === "feed" rendering logic below is unaffected.
  const contextNavTabs = visibleNavTabs.filter((t) => t.key !== "feed");

  // Only surface filters that actually have posts; fall back to "all" if the
  // active filter no longer matches anything (e.g. its last post was deleted).
  const availableFilters = FEED_FILTERS.filter(
    (f) => f.key === "all" || posts.some((p) => matchesFilter(f.key, p))
  );
  const activeFilter = availableFilters.some((f) => f.key === feedFilter) ? feedFilter : "all";
  const visiblePosts = posts.filter(
    (p) =>
      matchesFilter(activeFilter, p) &&
      (activeCategory === "all" || String(p.category_id) === String(activeCategory))
  );

  // Studio, Catalog and Campus use the left-sidebar feed chrome; Creator uses an
  // app-like fixed bottom nav instead.
  // Only Studio is a sidebar layout — that's what its community-home shell
  // (MembershipHome) renders and what the admin's template picker describes.
  // Catalog (search-forward top bar) and Campus (top-header dashboard) use the
  // top nav on every other page, so giving them a sidebar here made the nav jump
  // between the Groups and Feed pages of the same template.
  const isSpotlight = template === "spotlight";
  const feedSidebar = template === "studio" || template === "creator" || isSpotlight;
  // Creator's mobile nav is the header hamburger + drawer now (no bottom nav).
  const feedBottomNav = false;
  // Studio pads the root (header pushed beside its top-0 sidebar). Creator keeps
  // the header full-width and pads only the content column below it.
  // Spotlight's sidebar is full height AND collapsible, so the root padding
  // follows its width.
  const sidebarPl = isSpotlight
    ? (sidebarCollapsed ? "lg:ps-[72px]" : "lg:ps-64")
    : feedSidebar && template !== "creator" ? "lg:ps-64" : "";
  const creatorContentPl = template === "creator"
    ? `flex min-w-0 flex-1 flex-col transition-[padding] duration-300 ${sidebarCollapsed ? "lg:ps-16" : "lg:ps-64"}`
    : "contents";
  // Creator's content fills the width freed beside its collapsible sidebar (other
  // templates keep a centred, readable max-width).
  const contentWrap = template === "creator"
    ? "w-full px-4 py-8 lg:px-8"
    : isSpotlight
      // Full width beside the sidebar, like the club it's modelled on — the
      // catalogue runs edge to edge rather than floating in a centred column.
      ? "w-full px-4 py-8 sm:px-6 lg:px-8"
      : "max-w-7xl mx-auto w-full px-4 py-8";
  // Spotlight heads every section with its banner — except the lesson player
  // (a course is open: the player is the page) and Members Chat (a full-height
  // app surface with no room above it).
  const spotlightBanner = isSpotlight
    && subView !== "memberchat"
    // Search results open straight onto the list, like the club it's modelled on.
    && subView !== "search"
    && !(subView === "courses" && searchParams.get("course"));
  // Which nav item is lit. The feed has no tab (Groups stays lit for it), and a
  // custom menu is lit on its own page, told apart by ?id.
  const customPageId = subView === "page" ? searchParams.get("id") || "" : "";
  const navIsActive = (key) =>
    key === "groups"
      ? subView === "feed"
      : isCustomKey(key)
        ? customPageId === customIdOf(key)
        : subView === key;
  const customPageLabel = customPageId
    ? (community?.custom_menus || []).find((m) => m.id === customPageId)?.label || "Page"
    : "";
  // Spotlight opens the account screens as PAGES (/profile, /password,
  // /plans); every other template keeps them as popups.
  const openProfile = isSpotlight ? () => setSubView("profile") : () => setShowProfile(true);
  const openChangePassword = isSpotlight ? () => setSubView("password") : () => setShowChangePassword(true);
  const openMyPlans = isSpotlight ? () => setSubView("plans") : () => setShowMyPlans(true);
  const bottomNavItems = feedBottomNav
    ? visibleNavTabs.filter((t) => ["groups", "feed", "courses", "members", "leaderboard"].includes(t.key))
    : [];

  // Primary tabs stay inline; the rest (Leaderboard / Rewards / About / Pending)
  // collapse into "More ▾". About and Pending only exist inside a group.
  // navTabs arrive in the creator's menu order; the bar takes the top of it.
  const HEADER_PRIMARY_KEYS = ["home", "groups", "feed", "courses", "events", "members", "map"];
  const [headerTabs, headerMoreTabs] = navTabs ? splitInline(contextNavTabs, HEADER_PRIMARY_KEYS) : [[], []];

  return (
    <div data-template={template} className={`min-h-screen flex flex-col bg-canvas overflow-x-clip transition-[padding] duration-300 ${sidebarPl}${feedBottomNav ? " pb-16 lg:pb-0" : ""}${isSpotlight ? " isolate" : ""}`}>
      {/* Studio / Creator: fixed left sidebar nav (desktop only). Mirrors the
          community-home sidebar; the top nav is hidden on lg so nav lives here. */}
      {feedSidebar && (
        <MembershipSidebar
          logo={<BrandLogo community={community} onClick={goHome} />}
          logoIcon={
            <span className="flex h-9 w-9 items-center justify-center rounded-xl text-sm font-extrabold text-white" style={{ backgroundColor: primary }}>
              {(community?.name || "?").charAt(0).toUpperCase()}
            </span>
          }
          primary={primary}
          tabs={[
            ...contextNavTabs,
          ]}
          activeKey={subView}
          // The feed has no tab; you reach it from Groups, so Groups stays lit.
          isActive={navIsActive}
          onNavigate={(key) => (key === "groups" ? onNavigate?.("groups") : setSubView(key))}
          member={member}
          belowHeader={template === "creator"}
          collapsed={template === "creator" || isSpotlight ? sidebarCollapsed : false}
          onToggle={template === "creator" || isSpotlight ? toggleSidebar : undefined}
          variant={isSpotlight ? "spotlight" : "default"}
          footerItems={isSpotlight && member ? [{ key: "profile", label: "My Profile", icon: <BannerIcon name="profile" className="h-[18px] w-[18px]" />, onClick: openProfile, active: subView === "profile" }] : []}
        />
      )}

      {/* Top bar */}
      <MembershipHeader
        // The template owns the bar's look; this page only says where it is.
        template={template}
        community={community}
        token={token}
        member={member}
        primary={primary}
        tabs={headerTabs}
        moreItems={headerMoreTabs}
        activeKey={subView}
        onNavigate={(key) => (key === "groups" ? onNavigate?.("groups") : setSubView(key))}
        // Catalog carries the course search up in the bar (like the group search on
        // its community landing). CoursesView portals <CourseSearch> into this slot.
        extra={
          template === "catalog" && subView === "courses" ? (
            <div ref={setCourseSearchSlot} className="order-last w-full max-w-none sm:order-none sm:w-auto sm:max-w-xl sm:flex-1" />
          ) : null
        }
        // The feed has no tab of its own; you reach it from Groups, so highlight
        // Groups while the feed is the active view.
        isActive={navIsActive}
        // Back only reads as "leave this group", so it belongs on the group-scoped
        // pages. Courses / Events / Members / Map / Leaderboard / Rewards are
        // membership-wide destinations reachable from the nav — nothing to go back to.
        showBack={inGroupContext}
        onBack={() => goBack()}
        onLogoClick={goHome}
        // Studio's sidebar already carries the brand + nav, so the bar would show
        // a second logo right beside it. Keep it for mobile, where there's no sidebar.
        logoWrapClass={feedSidebar && template !== "creator" ? "lg:hidden" : ""}
        switcher={
          // Feed → switch groups. Courses → switch memberships (the Courses page
          // routes through this shell when the membership has groups). Everywhere
          // else → no switcher.
          member && groupToken && subView === "feed" ? (
            <GroupSwitcher
              groups={myGroups}
              currentToken={groupToken}
              primary={primary}
              onSelect={onSwitchGroup}
              onDiscover={onDiscoverGroups}
            />
          ) : /* Membership switcher — the small up/down icon button beside the
                 logo — commented out on request. Kept, not deleted: restore by
                 removing this comment wrapper and the one on the import at the
                 top of this file. The group switcher above is unaffected.

            membershipSwitcherOn && subView === "courses" ? (
            <MembershipSwitcher
              memberships={myMemberships}
              currentToken={propToken}
              primary={primary}
              onSelect={onSwitchMembership}
            />
          ) : */ null
        }
        mobileNav={
          navTabs && !feedBottomNav ? (
            <CompactNavMenu
              items={[
                ...contextNavTabs,
              ]}
              activeKey={subView}
              isActive={navIsActive}
              onNavigate={(key) => (key === "groups" ? onNavigate?.("groups") : setSubView(key))}
              primary={primary}
            />
          ) : null
        }
        onProfile={openProfile}
        onChangePassword={openChangePassword}
        onAccountSettings={() => setShowAccountSettings(true)}
        onMyPlans={openMyPlans}
        onMobileCode={() => setShowMobileCode(true)}
        onSignOut={signOut}
        onSignIn={() => openAuth("signin")}
      />

      {/* Creator: content beside the below-header sidebar; collapse widens only
          this column. Other templates pass through transparently. */}
      <div className={creatorContentPl}>

      {spotlightBanner ? (
        <>
          <SpotlightBanner section={subView} title={subView === "page" ? customPageLabel : undefined} subtitle={community?.name} primary={primary} {...spotlightBannerFor(brand, subView)} />
        </>
      ) : null}

      {subView === "home" ? (
        // Home stays a centred dashboard on Spotlight too: a welcome card and
        // stat tiles stretched across a 1900px screen read as empty, not wide.
        <div className={isSpotlight ? "mx-auto w-full max-w-[1320px] px-4 py-8 sm:px-6 lg:px-8" : contentWrap}>
          {/* Switched off: MembershipHome is already redirecting to Courses —
              render nothing rather than fetch a dashboard nobody enabled. */}
          {homeEnabled(communityMenus) ? (
            <HomeView
              token={token}
              member={member}
              primary={primary}
              community={community}
              onNavigate={(key) => (key === "groups" ? onNavigate?.("groups") : setSubView(key))}
              onOpenProfile={openProfile}
              onOpenGroup={(gt) => navigate(`/membership/${token}/feed?gid=${encodeURIComponent(gt)}`)}
              onOpenCourse={(c) => navigate(`/membership/${token}/courses?${new URLSearchParams({ course: c.course_token, view: "lessons" }).toString()}`)}
              // The feed's own card, scoped to the post's group, so like /
              // comment / poll work in place. Editing needs the group's
              // composer, so Edit opens the post in its group feed.
              renderPost={(p, { onDeleted }) => (
                <PostCard
                  post={p}
                  template={template}
                  primary={primary}
                  member={member}
                  isAdmin={false}
                  perms={NO_PERMS}
                  token={token}
                  groupToken={p.group_token}
                  onRequireAuth={() => openAuth("signin")}
                  onRequestEdit={() => navigate(`/membership/${token}/feed?gid=${encodeURIComponent(p.group_token)}&post=${p.post_id}`)}
                  onPointsChange={refreshMyPoints}
                  onDeleted={onDeleted}
                  onPinned={() => {}}
                />
              )}
            />
          ) : null}
        </div>
      ) : subView === "page" ? (
        // A creator's custom menu page — their embed, sandboxed. Spotlight's
        // banner already names it; the other templates title it here.
        <div className={contentWrap}>
          <CustomPage token={token} member={member} pageId={customPageId} primary={primary} showTitle={!isSpotlight} onBack={goHome} />
        </div>
      ) : subView === "search" ? (
        <div className={contentWrap}>
          <SpotlightSearchResults token={token} member={member} primary={primary} menus={communityMenus} />
        </div>
      ) : subView === EARNINGS_PAGE ? (
        // "My earnings" — a page in EVERY template, unlike the three account
        // screens below. Those have popups that work; this one IS the fix for
        // its popup, so there is no template where the popup is still right.
        <div className={contentWrap}>
          {!member ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-gray-200 bg-surface p-10 text-center text-sm text-gray-500">
              Sign in to see your earnings.
            </div>
          ) : (
            <div className="mx-auto w-full max-w-3xl">
              <AffiliateEarningsPage
                token={token}
                memberToken={member?.member_token}
                primary={primary}
              />
            </div>
          )}
        </div>
      ) : subView === "profile" || subView === "password" || subView === "plans" ? (
        // Account pages (Spotlight). The popups' own content, laid out as a page.
        <div className={contentWrap}>
          {!member ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-gray-200 bg-surface p-10 text-center text-sm text-gray-500">
              Sign in to see your account.
            </div>
          ) : subView === "profile" ? (
            <div className="mx-auto w-full max-w-3xl">
              <ProfileModal
                embedded
                token={token}
                member={member}
                groupToken={groupToken}
                primary={primary}
                banner={profileBanner(community)}
                onUpdated={saveMember}
                onPointsChange={refreshMyPoints}
              />
            </div>
          ) : subView === "password" ? (
            <div className="mx-auto w-full max-w-xl">
              <ChangePasswordModal embedded token={token} member={member} primary={primary} banner={profileBanner(community)} />
            </div>
          ) : (
            <div className="mx-auto w-full max-w-3xl">
              <MyPlansModal embedded member={member} primary={primary} />
            </div>
          )}
        </div>
      ) : subView === "courses" ? (
        <>
          {/* Same container as Events, whose content + rail layout this mirrors. */}
          <div className={contentWrap}>
            <CoursesView pointsCard={<MyPointsCard ready={groupReady} me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} />} onPointsChange={refreshMyPoints} token={token} groupToken={groupToken} primary={primary} member={member} group={group} template={template} community={community} totalMembers={total_members} headerSearchSlot={template === "catalog" ? courseSearchSlot : null} onInstructor={onInstructor ? (id) => onInstructor(id, "course") : null} />
          </div>
        </>
      ) : subView === "about" ? (
        <>
        <GroupLanding
          token={token}
          gid={groupToken}
          member={member}
          embedded
          // Joined members see their referral link here; non-joined / guests
          // see the Join button (handled inside GroupLanding via `referral`).
          referral={
            member && myStanding?.url_token
              ? { urlToken: myStanding.url_token, points: myPointsSettings?.viral_share || 0 }
              : null
          }
          onBack={() => setSubView("feed")}
          // "Start free trial" / "Join" on the About tab must actually join the
          // group (start the trial), not just switch tabs — otherwise the member
          // never becomes a member and the group keeps reopening to the landing.
          onJoin={async () => {
            if (!member) {
              openAuth("join"); // guest: sign up (auto-joins this group), then in
              return;
            }
            await autoJoinGroup(member); // signed-in: join / start the trial
            await load(); // refresh membership state
            setSubView("feed"); // enter the group feed
          }}
          onLogin={() => openAuth("signin")}
          onInstructor={onInstructor}
        />
        </>
      ) : subView === "members" ? (
        <>
          <div className={contentWrap}>
            <MembersView token={token} groupToken={groupToken} primary={primary} member={member} topBanner={<><MockTestLauncher page="members" funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} /><UpcomingEventBanner token={token} placement="members" primary={primary} className="mb-6" /></>} groupCard={<CommunityMiniBanner community={community} totalMembers={total_members} className="h-44" />} pointsCard={<MyPointsCard ready={groupReady} me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} />} />
          </div>
        </>
      ) : subView === "map" ? (
        <>
          <div className={contentWrap}>
            <MapView token={token} groupToken={groupToken} primary={primary} member={member} topBanner={<><MockTestLauncher page="map" funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} /><UpcomingEventBanner token={token} placement="map" primary={primary} className="mb-6" /></>} groupCard={<CommunityMiniBanner community={community} totalMembers={total_members} className="h-44" />} pointsCard={<MyPointsCard ready={groupReady} me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} />} />
          </div>
        </>
      ) : subView === "leaderboard" ? (
        <>
          <div className={contentWrap}>
            <LeaderboardView token={token} groupToken={groupToken} primary={primary} member={member} topBanner={<><MockTestLauncher page="leaderboard" funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} /><UpcomingEventBanner token={token} placement="leaderboard" primary={primary} className="mb-6" /></>} groupCard={<CommunityMiniBanner community={community} totalMembers={total_members} className="h-44" />} />
          </div>
        </>
      ) : subView === "rewards" ? (
        <>
          <div className={contentWrap}>
            <RewardsView token={token} groupToken={groupToken} primary={primary} member={member} topBanner={<><MockTestLauncher page="rewards" funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} /><UpcomingEventBanner token={token} placement="rewards" primary={primary} className="mb-6" /></>} groupCard={<CommunityMiniBanner community={community} totalMembers={total_members} className="h-44" />} />
          </div>
        </>
      ) : subView === "memberchat" ? (
        <>
          {/* Chat is a full-height app surface, so it skips the usual page
              gutters on phones and fills the viewport under the 64px header. */}
          <div className={template === "creator" ? "w-full sm:px-4 sm:py-6 lg:py-8" : isSpotlight ? "w-full sm:px-6 sm:py-6 lg:px-8 lg:py-8" : "mx-auto w-full max-w-7xl sm:px-4 sm:py-6 lg:py-8"}>
            {member ? (
              <div className="h-[calc(100dvh-4rem)] sm:h-[calc(100vh-9rem)] sm:min-h-[560px] lg:h-[calc(100vh-11rem)]">
                <ChatModal token={token} member={member} primary={primary} embedded startWith={openChatToken || undefined} />
              </div>
            ) : (
              <div className="m-4 rounded-2xl border border-gray-200 bg-surface p-10 text-center text-sm text-gray-500 sm:m-0">
                Sign in to message other members.
              </div>
            )}
          </div>
        </>
      ) : subView === "events" ? (
        <>
          <div className={contentWrap}>
            <EventsView token={token} groupToken={groupToken} primary={primary} member={member} template={template} communityName={community?.name} groupCard={<CommunityMiniBanner community={community} totalMembers={total_members} className="h-44" />} />
          </div>
        </>
      ) : subView === "pendingpost" ? (
        <div className={`${contentWrap} grid grid-cols-1 lg:grid-cols-12 gap-6`}>
          <main className="lg:col-span-8 space-y-5">
            <button
              type="button"
              onClick={() => setSubView("feed")}
              className="group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-surface px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 active:scale-[0.99]"
            >
              <svg className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              Back to feed
            </button>
            <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <SectionHeader
                primary={primary}
                iconPath={PENDING_ICON_PATH}
                title="Pending posts"
                subtitle="Member posts awaiting your approval"
                trailing={pendingList == null ? "…" : `${pendingList.length} ${pendingList.length === 1 ? "post" : "posts"}`}
              />
            </div>
            {!perms.approve_posts ? (
              <div className="rounded-2xl border border-gray-200 bg-surface p-10 text-center text-sm text-gray-400">
                Only community admins can review pending posts.
              </div>
            ) : pendingList === null ? (
              <div className="rounded-2xl border border-gray-200 bg-surface">
                <FeedSkeleton count={2} />
              </div>
            ) : pendingList.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 bg-surface p-10 text-center text-sm text-gray-400">No posts awaiting approval.</div>
            ) : (
              pendingList.map((p) => (
                <div key={p.post_id} className="rounded-2xl border border-amber-200 dark:border-amber-500/30 bg-surface shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-3 border-b border-amber-100 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d={PENDING_ICON_PATH} /></svg>
                      Pending approval
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => reviewPending(p, "reject")}
                        disabled={pendingBusyId === p.post_id}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 dark:text-red-300 ring-1 ring-inset ring-red-200 dark:ring-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => reviewPending(p, "approve")}
                        disabled={pendingBusyId === p.post_id}
                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {pendingBusyId === p.post_id ? "…" : "Approve"}
                      </button>
                    </div>
                  </div>
                  <PostCard
                    post={p}
                    template={template}
                    mentionMembers={mentionMembers}
                    primary={primary}
                    member={member}
                    isAdmin={isAdmin}
                    perms={perms}
                    token={token}
                    groupToken={groupToken}
                    onRequireAuth={() => openAuth("signin")}
                    onRequestEdit={openComposerForEdit}
                    onPointsChange={refreshMyPoints}
                    onDeleted={() => setPendingList((ps) => (ps || []).filter((x) => x.post_id !== p.post_id))}
                    onPinned={() => {}}
                  />
                </div>
              ))
            )}
          </main>
          <aside className="lg:col-span-4 space-y-4 lg:self-start lg:sticky lg:top-20">
            <GroupMiniBanner group={group} className="h-44" />
          </aside>
        </div>
      ) : (
        <>
      {/* 2-column body (Skool layout) — feed 70% / rail 30%. The group banner
          now lives in the right rail (like the other group pages) instead of a
          full-width hero, so the feed leads the left column. */}
      <div className={`${contentWrap} grid grid-cols-1 lg:grid-cols-12 gap-6`}>
        {/* Center — composer + feed */}
        <main className="lg:col-span-8 space-y-5">
          {/* Group-scoped Live Mock Tests (admin "Show on Community → this group"). */}
          {member ? (
            <MockTestLauncher page="community" groupToken={groupToken} funnelId={member?.funnel_id} memberId={member?.community_member_id} jwt={member?.member_token} primary={primary} />
          ) : null}
          {/* Premium combined card — heading band + post composer in one card. */}
          <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <SectionHeader
              primary={primary}
              iconPath="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              title="Feed"
              subtitle="Latest posts in this community"
              // The group's full post count, not just the pages loaded so far.
              trailing={postsLoading ? "…" : `${totalPosts} ${totalPosts === 1 ? "post" : "posts"}`}
            />

          {/* Composer (inside the same card as the heading) */}
          {member ? (
            <>
              {/* Hidden file inputs — kept outside the modal so the trigger-row
                  Photo / Audio buttons can open the picker before it paints. */}
              <input
                ref={composerFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => addPostImages(e.target.files)}
              />
              <input
                ref={composerAudioRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={(e) => pickPostAudio(e.target.files?.[0])}
              />
              <input
                ref={composerVideoRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-m4v"
                className="hidden"
                onChange={(e) => pickVideoFile(e.target.files?.[0])}
              />

              {/* Composer trigger row — compact (full options live in the modal) */}
              <div className="p-3 sm:p-3.5">
                {/* flex-wrap + `w-full sm:w-auto` on the shortcut strip below is
                    what makes this responsive without a second copy of the row.
                    On a phone the strip cannot share a line with the avatar and
                    the pill, so it wraps underneath and the pill gets the whole
                    width; from sm up it is auto-width again and everything sits
                    on one line. Before this the four 36px buttons and the 36px
                    avatar held their size and squeezed the pill to ~97px of
                    text, so the placeholder broke across two lines. */}
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="shrink-0">
                    <Avatar name={member.name} img={member.img} primary={primary} size={36} />
                  </span>
                  <button
                    type="button"
                    onClick={() => openComposer("text")}
                    /* truncate + whitespace-nowrap so the placeholder can never
                       wrap again, whatever the width works out to. */
                    className="flex-1 min-w-0 truncate whitespace-nowrap text-left rounded-full bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-100 transition"
                  >
                    Write something…
                  </button>
                  {/* Quick post-type shortcuts (icon-only; open the modal in that mode) */}
                  <div className="flex w-full items-center gap-1.5 border-t border-gray-100 pt-2.5 sm:w-auto sm:shrink-0 sm:border-t-0 sm:pt-0">
                    {[["photo", "Photo"], ["video", "Video"], ["audio", "Audio"], ["poll", "Poll"]]
                      .filter(([k]) =>
                        !(k === "photo" && photoDisabled) &&
                        !(k === "audio" && audioDisabled) &&
                        !(k === "video" && videoDisabled) &&
                        !(k === "poll" && pollDisabled))
                      .map(([k, label]) => {
                      const accent = COMPOSER_ACCENT[k] || primary;
                      return (
                        /* Stretch to share the row evenly on the wrapped mobile
                           line; fixed 36px square once inline. The sizing lives
                           on the tooltip wrapper because that is now the child
                           the row lays out. */
                        <IconTooltip
                          key={k}
                          label={label}
                          className="flex flex-1 sm:w-9 sm:flex-none"
                        >
                        <button
                          type="button"
                          onClick={() => openComposer(k)}
                          aria-label={label}
                          className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg transition hover:scale-110"
                          style={{ backgroundColor: `${accent}1a`, color: accent }}
                        >
                          <svg className="w-[18px] h-[18px] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                            <path strokeLinecap="round" strokeLinejoin="round" d={COMPOSER_ICONS[k]} />
                          </svg>
                          {/* Only on the wrapped mobile row, where the button is
                              a quarter of the width — an icon alone in a 76px
                              block reads as an empty bar, and icon-only is
                              guesswork anyway. Inline at sm+ the button is a
                              36px square again and the title/aria-label carry
                              the meaning. */}
                          <span className="truncate text-[12px] font-semibold sm:hidden">{label}</span>
                        </button>
                        </IconTooltip>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Create Post modal */}
              {composerOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-stretch sm:items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4 sm:py-10"
                  /* No backdrop-click close — a stray click must not discard a
                     half-written post. Close via the X. */
                >
                  <div
                    className="relative flex w-full sm:max-w-xl flex-col h-full sm:h-auto max-h-none sm:max-h-[calc(100dvh-5rem)] overflow-hidden rounded-none sm:rounded-[26px] bg-surface shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                    onPaste={pasteComposerFiles}
                    {...composerDragProps}
                  >
                    {composerDrag && (
                      <div
                        className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-[22px] border-2 border-dashed text-sm font-semibold backdrop-blur-[2px]"
                        style={{ borderColor: primary, background: `${primary}1f`, color: primary }}
                      >
                        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12m0-12l-4 4m4-4l4 4" />
                        </svg>
                        Drop to add to your post
                      </div>
                    )}
                    {/* Brand hairline across the top edge — the one place the
                        group's colour appears before anything is typed. */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-0 top-0 h-[3px]"
                      style={{ background: `linear-gradient(90deg, ${primary}, ${primary}4d 70%, transparent)` }}
                    />
                    <div className="flex shrink-0 items-start gap-3 px-5 pb-4 pt-5">
                      <span
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl"
                        style={{ background: `${primary}17`, color: primary }}
                        aria-hidden="true"
                      >
                        <svg className="h-[19px] w-[19px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[17px] font-bold tracking-[-0.015em] text-gray-900">
                          {composerEdit ? "Edit post" : "Create post"}
                        </h3>
                        <p className="mt-0.5 truncate text-[12.5px] text-gray-500">
                          {composerEdit ? "Changes go live straight away" : `Sharing to ${group?.name || "your community"}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={closeComposer}
                        disabled={posting}
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                        aria-label="Close"
                      >
                        <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="thin-scroll min-h-0 flex-1 overflow-y-auto border-t border-gray-100 px-5 pb-5 pt-4">
                      {/* Author left, category right — one row rather than
                          stacked. The category is the only thing on this line
                          you can change, so it gets its own edge instead of
                          hanging under the name like part of it. */}
                      <div className="mb-3.5 flex items-center gap-3">
                        <span
                          className="shrink-0 rounded-full p-[2px]"
                          style={{ boxShadow: `0 0 0 2px ${primary}26` }}
                        >
                          <Avatar name={member.name} img={member.img} primary={primary} size={38} />
                        </span>
                        <div className="min-w-0 flex-1 truncate text-[14px] font-bold text-gray-900">
                          {member.name}
                        </div>
                        <PostCategoryChip
                          categories={composerCategories}
                          value={postCategory}
                          onChange={setPostCategory}
                          primary={primary}
                          align="right"
                        />
                      </div>
                      <div className="relative">
                        {/* One textarea in both modes, wrapped rather than swapped:
                            rendering a second element for the coloured canvas
                            would unmount this one on every colour pick, blurring
                            the field mid-sentence and dropping the caret. The
                            wrapper is what carries the colour, and it centres the
                            text vertically — which a textarea cannot do itself. */}
                        <div
                          className={
                            activeBg
                              ? "flex items-center justify-center overflow-hidden rounded-[22px] px-6 py-8 ring-1 ring-inset ring-white/15 transition-all duration-300"
                              : ""
                          }
                          style={
                            activeBg
                              ? {
                                  background: activeBg.css,
                                  minHeight: "220px",
                                  boxShadow: "0 18px 40px -24px rgba(16,24,40,0.6)",
                                }
                              : undefined
                          }
                        >
                          <textarea
                            ref={postMentions.ref}
                            value={composer}
                            onChange={postMentions.onChange}
                            onKeyDown={postMentions.onKeyDown}
                            onFocus={(e) => {
                              if (activeBg) return; // the canvas has no border to light up
                              e.currentTarget.style.borderColor = primary;
                              e.currentTarget.style.boxShadow = `0 0 0 3px ${primary}26`;
                            }}
                            onBlur={(e) => {
                              postMentions.onBlur?.(e);
                              e.currentTarget.style.borderColor = "";
                              e.currentTarget.style.boxShadow = "";
                            }}
                            rows={3}
                            autoFocus
                            placeholder={activeBg ? "Say something…" : "Type your message… use @ to tag members"}
                            className={
                              activeBg
                                ? "scrollbar-hide w-full resize-none overflow-y-auto border-0 bg-transparent py-1 text-center font-extrabold outline-none placeholder:text-[color:var(--canvas-fg)] placeholder:opacity-55"
                                : "w-full resize-none text-[15px] bg-gray-50 border border-gray-200 rounded-xl pl-4 pr-11 py-3 outline-none focus:bg-surface transition placeholder:text-gray-400 overflow-y-auto"
                            }
                            style={
                              activeBg
                                ? {
                                    color: activeBg.fg,
                                    caretColor: activeBg.fg,
                                    // A textarea's `color` does NOT reach its
                                    // ::placeholder — that keeps the browser's
                                    // grey, which vanishes on a dark backdrop
                                    // and greys out on a pale one. The class
                                    // above paints it from this variable.
                                    "--canvas-fg": activeBg.fg,
                                    fontSize: `${bgFontSize(composer.length)}px`,
                                    lineHeight: 1.3,
                                    maxHeight: "40vh",
                                  }
                                : { minHeight: "110px", maxHeight: "45vh" }
                            }
                          />
                        </div>
                        {postMentions.dropdown}
                        <span className="absolute right-2 top-2">
                          <EmojiPicker
                            onPick={(e) => setComposer((c) => c + e)}
                            dropUp={false}
                            /* EmojiPicker's own trigger hardcodes text-gray-500,
                               which disappears into a dark backdrop and greys
                               out on a pale one. Passing the glyph lets it carry
                               the backdrop's own foreground instead, and the
                               scrim flips with it so there is always contrast
                               under it. */
                            icon={
                              activeBg ? (
                                <svg
                                  className="h-[18px] w-[18px]"
                                  style={{ color: activeBg.fg }}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                >
                                  <circle cx="12" cy="12" r="9" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 10h.01M15 10h.01M8.5 14.5a4 4 0 007 0" />
                                </svg>
                              ) : null
                            }
                            className={
                              activeBg
                                ? `rounded-lg px-1.5 py-1.5 backdrop-blur-sm transition ${
                                    activeBg.fg === "#ffffff"
                                      ? "bg-black/25 hover:bg-black/40"
                                      : "bg-surface/70 hover:bg-surface/90"
                                  }`
                                : "px-1.5 py-1.5 rounded-lg hover:bg-gray-100"
                            }
                          />
                        </span>
                      </div>

                      {/* Background colour. Sits directly under the field it
                          paints, which is also where Facebook puts it. */}
                      {/* Backdrops are for text-only posts, so the section is
                          not rendered at all once something is attached —
                          a dimmed row of colours you cannot use is just noise
                          in the middle of the composer. */}
                      {!bgHasMedia && (
                        <>
                          <PostBackdropRail value={postBg} onChange={setPostBg} primary={primary} />

                          {/* Length gauge — only while a colour is on, because
                              that is the only time the cap exists. It appears in
                              the last 50 characters rather than always: a counter
                              on screen from the first keystroke reads as a limit
                              on the post itself, which this is not. */}
                          {postBg && composer.length > POST_BG_MAX_CHARS - 50 && (
                            <p
                              className={`mt-2 text-right text-[11.5px] font-semibold ${
                                bgTooLong ? "text-red-500" : "text-gray-400"
                              }`}
                            >
                              {bgTooLong
                                ? "Too long for a backdrop — this will post as plain text."
                                : `${POST_BG_MAX_CHARS - composer.length} characters left`}
                            </p>
                          )}
                        </>
                      )}

                  {/* Live link preview, Facebook-style: paste a link and see the card
                      the post will carry. Only on a plain text post — photos,
                      video, audio and polls take the card's place in the feed.
                      An open but EMPTY drop-zone (popup opened from "Photo")
                      attaches nothing, so it must not hide the card. */}
                  {postMedia.length === 0 && !pollMode && !postAudio && !videoFile && extractLinkUrl(composer) && (
                    <LinkPreview
                      url={extractLinkUrl(composer)}
                      compose
                      removed={extractLinkUrl(composer) === hiddenPreviewUrl}
                      onRemove={() => setHiddenPreviewUrl(extractLinkUrl(composer))}
                      onRestore={() => setHiddenPreviewUrl("")}
                    />
                  )}

                  {/* Upload drop-zones for the active media mode */}
                  {mediaMode === "photo" && postMedia.length === 0 && (
                    <MediaDropzone kind="photo" primary={primary} onOpen={() => composerFileRef.current?.click()} onFiles={addPostImages} />
                  )}
                  {mediaMode === "audio" && !postAudioPreview && (
                    <MediaDropzone kind="audio" primary={primary} onOpen={() => composerAudioRef.current?.click()} onFile={pickPostAudio} />
                  )}
                  {mediaMode === "video" && !videoFilePreview && (
                    <MediaDropzone kind="video" primary={primary} onOpen={() => composerVideoRef.current?.click()} onFile={pickVideoFile} />
                  )}

                  {/* Image / GIF attachments — thumbnail strip with per-item remove
                      and an "add more" tile (up to POST_MEDIA_MAX). */}
                  {postMedia.length > 0 && !pollMode && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {postMedia.map((m, i) => (
                        <div key={i} className="relative h-24 w-24 shrink-0">
                          <img src={m.url} alt="" className="h-full w-full rounded-xl border border-gray-200 object-cover" />
                          <button
                            type="button"
                            onClick={() => removePostMedia(i)}
                            className="absolute -top-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-white text-sm shadow"
                            aria-label="Remove"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                          {m.isGif && (
                            <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] font-bold text-white">GIF</span>
                          )}
                          {isEditableImage(m) && <EditPhotoButton onClick={() => setEditingMedia(i)} />}
                        </div>
                      ))}
                      {editingMedia !== null && postMedia[editingMedia]?.file && (
                        <ImageEditor
                          file={postMedia[editingMedia].file}
                          primary={primary}
                          onCancel={() => setEditingMedia(null)}
                          onSave={saveEditedImage}
                        />
                      )}
                      {postMedia.length < POST_MEDIA_MAX && (
                        <button
                          type="button"
                          onClick={() => composerFileRef.current?.click()}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            addPostImages(e.dataTransfer?.files);
                          }}
                          className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 transition hover:border-gray-300 hover:text-gray-600"
                          aria-label="Add more images"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                          <span className="text-[11px] font-semibold">Add</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Audio preview (premium player chip) */}
                  {postAudioPreview && (
                    <div className="relative mt-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <span
                        className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="6" cy="18" r="3" />
                          <circle cx="18" cy="16" r="3" />
                        </svg>
                      </span>
                      <audio controls src={postAudioPreview} className="h-9 flex-1 min-w-0" />
                      <button
                        onClick={clearPostAudio}
                        className="shrink-0 w-6 h-6 rounded-full bg-ink text-white text-sm flex items-center justify-center shadow"
                        aria-label="Remove audio"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  )}

                  {videoFilePreview && (
                    <div className="relative mt-3">
                      <video src={videoFilePreview} controls className="w-full max-h-72 rounded-xl border bg-black" />
                      <button
                        onClick={removeComposerVideo}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-ink text-white text-sm flex items-center justify-center shadow"
                        aria-label="Remove video"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  )}

                  {pollMode && (
                    <div className="mt-3 space-y-2.5">
                      {pollOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <span
                            className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ backgroundColor: `${primary}14`, color: primary }}
                          >
                            {i + 1}
                          </span>
                          <input
                            value={opt}
                            onChange={(e) =>
                              setPollOptions((o) => o.map((v, idx) => (idx === i ? e.target.value : v)))
                            }
                            placeholder={`Option ${i + 1}`}
                            className={INPUT_CLS + " flex-1"}
                            {...themeFocus(primary)}
                          />
                          {pollOptions.length > 2 && (
                            <button
                              type="button"
                              onClick={() => setPollOptions((o) => o.filter((_, idx) => idx !== i))}
                              className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/15 transition"
                              aria-label="Remove option"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                      {pollOptions.length < 6 && (
                        <button
                          type="button"
                          onClick={() => setPollOptions((o) => [...o, ""])}
                          className="flex items-center gap-2 text-sm font-semibold pl-1 pt-0.5 hover:opacity-80 transition"
                          style={{ color: primary }}
                        >
                          <span className="w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center" style={{ borderColor: `${primary}66` }}>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                            </svg>
                          </span>
                          Add option
                        </button>
                      )}
                    </div>
                  )}

                      {/* Attachments. A labelled section over a chip rail, to
                          match Backdrop above it — the composer reads as two
                          settings sections under one canvas rather than a text
                          box with a button bar bolted on. */}
                      {/* Inline label on a real screen; stacked on a phone,
                          where ~60px of label would squeeze the controls it is
                          naming. The modal is full-height there and already
                          scrolls, so the row it costs is free. */}
                      {/* Editing keeps the post's type. The save sends one kind of
                          post (text, photos, audio, video or poll), so a photo
                          attached to a text post was silently dropped. The row
                          is gone while editing; a photo post keeps GIF, which
                          lands in the same photo set. */}
                      {composerEdit ? (
                        <>
                          {mediaMode === "photo" && postMedia.length > 0 && postMedia.length < POST_MEDIA_MAX && (
                            <div className="mt-3.5 flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                              <ComposerSectionLabel>Attach</ComposerSectionLabel>
                              <GifPicker
                                onPick={pickPostGif}
                                dropUp
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-gray-200 hover:text-gray-800"
                              />
                            </div>
                          )}
                          <p className="mt-3 text-[12px] text-gray-400">
                            {mediaMode === "photo"
                              ? "To share a video, audio or poll, create a new post."
                              : "To share a photo, video, audio or poll, create a new post."}
                          </p>
                        </>
                      ) : (
                      <div className="mt-3.5 flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                        <ComposerSectionLabel>Attach</ComposerSectionLabel>
                        <div className="flex flex-wrap items-center gap-2.5">
                          {!photoDisabled && (
                            <ComposerAttachChip icon="photo" label="Photo" active={mediaMode === "photo" || postMedia.some((m) => !m.isGif)} onClick={startPhoto} />
                          )}
                          {!audioDisabled && (
                            <ComposerAttachChip icon="audio" label="Audio" active={mediaMode === "audio" || !!postAudio} onClick={startAudio} />
                          )}
                          {!videoDisabled && (
                            <ComposerAttachChip icon="video" label="Video" active={mediaMode === "video" || !!videoFile} onClick={startVideo} />
                          )}
                          {!pollDisabled && (
                            <ComposerAttachChip icon="poll" label="Poll" active={pollMode} onClick={togglePoll} />
                          )}
                          <GifPicker
                            onPick={pickPostGif}
                            dropUp
            /* Matches the icon buttons: same 40px square, same lift. */
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition duration-200 ease-out hover:-translate-y-0.5 hover:bg-gray-200 hover:text-gray-800"
                          />
                        </div>
                      </div>
                      )}

                      {/* Protected link — admins + instructors, on a new text or
                          photo post. The body is public as ever; only the URL
                          behind the button is held back, and members reach it
                          through the API, which re-checks their access on every
                          click. Hidden while editing: changing the link is P2. */}
                      {/* Visible to anyone who may publish a protected link — and,
                          when a post already carries one, to whoever may edit
                          that post, so the link can always be removed by someone
                          who could delete the whole post anyway. */}
                      {(perms.protected_posts || (composerEdit && composerEdit.post?.protected_link)) &&
                        !pollMode && !videoFile && !postAudio && (
                        <ProtectedFields
                          state={protectedPost}
                          set={setProtected}
                          primary={primary}
                          inputCls={INPUT_CLS}
                          focusProps={themeFocus(primary)}
                        />
                      )}

                      <button
                        onClick={composerEdit ? submitComposerEdit : createPost}
                        disabled={
                          posting ||
                          !!protectedProblem ||
                          (pollMode
                            ? false
                            : !composer.trim() &&
                              postMedia.length === 0 &&
                              !postAudio &&
                              !videoFile &&
                              // When EDITING, existing audio/video counts as content:
                              // `postAudio`/`videoFile` hold newly picked files only,
                              // so a caption-less audio post would otherwise open with
                              // Save permanently greyed out.
                              !(composerEdit && (postAudioPreview || videoFilePreview)))
                        }
                        style={{
                          // A white sheen over the brand colour rather than a
                          // second hard-coded stop, so this stays glossy whatever
                          // colour the group has picked.
                          backgroundImage:
                            "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0) 58%)",
                          backgroundColor: primary,
                          color: "var(--brand-btn-text, #fff)",
                          boxShadow: `0 12px 26px -14px ${primary}`,
                        }}
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-[15px] font-bold tracking-[-0.01em] transition hover:brightness-[1.06] active:scale-[0.995] disabled:opacity-40 disabled:shadow-none"
                      >
                        {posting && (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        )}
                        {composerEdit
                          ? posting
                            ? "Saving…"
                            : "Save changes"
                          : posting
                          ? "Publishing…"
                          : pollMode
                          ? "Publish poll"
                          : videoFile
                          ? "Publish video"
                          : postAudio
                          ? "Publish audio"
                          : "Publish post"}
                        {!posting && (
                          <svg className="h-[17px] w-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h13m0 0l-5-5m5 5l-5 5" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="border-t border-gray-100 p-5 text-center text-sm text-gray-500">
              <button onClick={() => openAuth("join")} className="font-semibold" style={{ color: primary }}>
                Join the community
              </button>{" "}
              to post and comment.
            </div>
          )}
          </div>
          {/* /Premium combined card */}

          {/* Next upcoming event — premium banner, opens a detail popup.
              placement="community" so the banner is opt-in like every other page:
              without it this context sent no placement at all and slipped past
              the server's gate (MembershipEventController::events). */}
          <UpcomingEventBanner token={token} groupToken={groupToken} placement="community" primary={primary} />

          {/* Category chips (left) + post-type Filter button (right). */}
          {!postsLoading && (categories.length > 0 || availableFilters.length > 1) && (
            <div className="-mt-1 flex flex-wrap items-center gap-2">
              {categories.length > 0 && (
                <>
                  <CategoryChip label="All" active={activeCategory === "all"} onClick={() => setActiveCategory("all")} primary={primary} />
                  {categories.map((c) => (
                    <CategoryChip
                      key={c.membership_group_category_id}
                      emoji={c.emoji}
                      label={c.name}
                      active={String(activeCategory) === String(c.membership_group_category_id)}
                      onClick={() => setActiveCategory(c.membership_group_category_id)}
                      primary={primary}
                    />
                  ))}
                </>
              )}
              {availableFilters.length > 1 && (
                <div className="ml-auto">
                  <FeedFilter
                    items={availableFilters.map((f) => ({
                      ...f,
                      count: f.key === "all" ? posts.length : posts.filter((p) => matchesFilter(f.key, p)).length,
                    }))}
                    active={activeFilter}
                    onChange={setFeedFilter}
                    primary={primary}
                  />
                </div>
              )}
            </div>
          )}

          {/* Feed */}
          {postsLoading ? (
            <FeedSkeleton count={3} />
          ) : posts.length === 0 ? (
            <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm py-16 px-6 text-center flex flex-col items-center">
              {/* Themed illustration — a translucent post card with a solid
                  "compose" badge, all tinted with the community colour. */}
              <svg viewBox="0 0 120 120" className="w-28 h-28 mb-5" style={{ color: primary }} fill="none">
                <circle cx="60" cy="60" r="56" fill="currentColor" opacity="0.07" />
                <rect x="28" y="32" width="64" height="48" rx="10" fill="currentColor" opacity="0.14" />
                <rect x="38" y="44" width="22" height="6" rx="3" fill="currentColor" opacity="0.55" />
                <rect x="38" y="56" width="44" height="5" rx="2.5" fill="currentColor" opacity="0.3" />
                <rect x="38" y="66" width="34" height="5" rx="2.5" fill="currentColor" opacity="0.3" />
                <circle cx="86" cy="84" r="17" fill="currentColor" />
                <path d="M86 77v14M79 84h14" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" />
              </svg>
              <h3 className="font-bold text-gray-900 text-lg">No posts yet</h3>
              <p className="text-sm text-gray-500 mt-1.5 max-w-xs">
                Be the first to share something with the community and get the conversation started.
              </p>
            </div>
          ) : visiblePosts.length === 0 ? (
            /* The group HAS posts, but none match what is selected. Without
               this the list rendered nothing at all and left only the "Load
               more" button — which reads as "still loading", so you press it
               until the feed runs out and get "You're all caught up" as the
               answer to a question you never asked. */
            (() => {
              const cat = categories.find(
                (c) => String(c.membership_group_category_id) === String(activeCategory),
              );
              const typeLabel =
                activeFilter === "all"
                  ? null
                  : FEED_FILTERS.find((f) => f.key === activeFilter)?.label?.toLowerCase();
              return (
                <div className="flex flex-col items-center rounded-2xl border border-gray-200 bg-surface px-6 py-14 text-center shadow-sm">
                  <svg viewBox="0 0 120 120" className="mb-5 h-24 w-24" style={{ color: primary }} fill="none">
                    <circle cx="60" cy="60" r="56" fill="currentColor" opacity="0.07" />
                    {/* A funnel with nothing coming out of it. */}
                    <path
                      d="M36 36h48L64 62v26l-8 6V62L36 36z"
                      fill="currentColor"
                      opacity="0.16"
                      stroke="currentColor"
                      strokeOpacity="0.45"
                      strokeWidth="3"
                      strokeLinejoin="round"
                    />
                    <circle cx="84" cy="82" r="15" fill="currentColor" opacity="0.9" />
                    <path d="M78 82h12" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" />
                  </svg>
                  <h3 className="text-lg font-bold text-gray-900">
                    {cat ? (
                      <>
                        Nothing in {cat.emoji ? `${cat.emoji} ` : ""}
                        {cat.name} yet
                      </>
                    ) : (
                      <>No {typeLabel || "posts"} here yet</>
                    )}
                  </h3>
                  <p className="mt-1.5 max-w-xs text-sm text-gray-500">
                    {cat && typeLabel
                      ? `No ${typeLabel} have been posted in this category.`
                      : cat
                      ? "No one has posted in this category yet - you could be the first."
                      : `No ${typeLabel || "posts"} have been shared in this community yet.`}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveCategory("all");
                        setFeedFilter("all");
                      }}
                      className="rounded-full px-5 py-2.5 text-sm font-bold transition hover:brightness-[1.06]"
                      style={{
                        backgroundColor: primary,
                        color: "var(--brand-btn-text, #fff)",
                        boxShadow: `0 10px 22px -14px ${primary}`,
                      }}
                    >
                      Show all posts
                    </button>
                    {/* Filtering happens over what is LOADED, so an older post
                        in this category may simply not be here yet. Saying so —
                        and offering the fetch — beats claiming the category is
                        empty when we cannot know that. */}
                    {/* {hasMorePosts && (
                      <button
                        type="button"
                        onClick={loadMorePosts}
                        disabled={loadingMore}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-surface px-5 py-2.5 text-sm font-bold text-gray-600 transition hover:border-gray-300 disabled:opacity-60"
                      >
                        {loadingMore ? <Spinner size={16} /> : null}
                        Search older posts
                      </button>
                    )} */}
                  </div>
                </div>
              );
            })()
          ) : (
            visiblePosts.map((p) => {
              const focused = focusPostId && String(p.post_id) === String(focusPostId);
              return (
                <div
                  key={p.post_id}
                  id={`post-${p.post_id}`}
                  className="scroll-mt-24 rounded-2xl transition-shadow"
                  style={focused ? { boxShadow: `0 0 0 2px ${primary}` } : undefined}
                >
                  <PostCard
                    post={p}
                    template={template}
                    aiComment={aiComments[p.post_id]}
                    mentionMembers={mentionMembers}
                    primary={primary}
                    member={member}
                    isAdmin={isAdmin}
                    perms={perms}
                    token={token}
                    groupToken={groupToken}
                    focusCommentId={focused ? focusCommentId : null}
                    onRequireAuth={() => openAuth("signin")}
                    onRequestEdit={openComposerForEdit}
                    onPointsChange={refreshMyPoints}
                    onDeleted={() => setPosts((ps) => ps.filter((x) => x.post_id !== p.post_id))}
                    onPinned={(pinned) =>
                      setPosts((ps) =>
                        [...ps.map((x) => (x.post_id === p.post_id ? { ...x, pinned } : x))].sort(
                          (a, b) => b.pinned - a.pinned || b.post_id - a.post_id
                        )
                      )
                    }
                  />
                </div>
              );
            })
          )}

          {/* "Load more" — fetches the next page of older posts on click.
              Hidden when nothing matches the filter: the empty state above
              carries its own "Search older posts" instead. */}
          {!postsLoading && visiblePosts.length > 0 && hasMorePosts && (
            <div className="flex justify-center py-6">
              <button
                type="button"
                onClick={loadMorePosts}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-surface px-6 py-2.5 text-sm font-bold shadow-sm transition hover:border-gray-300 hover:shadow disabled:cursor-not-allowed disabled:opacity-60"
                style={{ color: primary }}
              >
                {loadingMore ? (
                  <>
                    <Spinner size={16} />
                    Loading…
                  </>
                ) : (
                  <>
                    Load more posts
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
          {/* End-of-feed marker — only once there's enough to have scrolled. */}
          {!postsLoading && !hasMorePosts && visiblePosts.length > 0 && posts.length >= POSTS_PER_PAGE && (
            <p className="py-6 text-center text-xs font-medium text-gray-400">You're all caught up.</p>
          )}
        </main>

        {/* Right rail — Skool-style group card + leaderboard. The whole rail is a
            sticky sidebar (self-start so it hugs its content instead of stretching
            to the feed's height) that scrolls internally when it's taller than the
            viewport, so every card stays reachable while reading a long feed. */}
        <aside className="lg:col-span-4 space-y-4 lg:self-start lg:sticky lg:top-20 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
          {/* Group banner — same design as the course detail page, on the right. */}
          <GroupMiniBanner group={group} className="h-44" />

          {/* Leave group + (admin) Pending Posts — sit side by side in the rail. */}
          {member && (
            <div className="flex items-center gap-2">
              {perms.approve_posts && inGroupContext && (
                <button
                  type="button"
                  onClick={() => setSubView("pendingpost")}
                  className={`group flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold shadow-sm transition-all hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 active:scale-[0.99] ${
                    subView === "pendingpost"
                      ? "text-white"
                      : "border-gray-200 bg-surface text-gray-800 hover:bg-gray-50"
                  }`}
                  style={
                    subView === "pendingpost"
                      ? { backgroundColor: primary, borderColor: primary }
                      : undefined
                  }
                  title="Review pending posts"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                    <path strokeLinecap="round" strokeLinejoin="round" d={PENDING_ICON_PATH} />
                  </svg>
                  Pending Posts
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmLeave(true)}
                className="group flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#F5CE7A] dark:bg-amber-400/15 px-4 py-2.5 text-sm font-bold text-gray-900 dark:text-amber-200 shadow-sm dark:shadow-none dark:ring-1 dark:ring-amber-400/25 transition-all hover:bg-[#EFC061] dark:hover:bg-amber-400/25 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/70 active:scale-[0.99]"
              >
                <svg
                  className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.9"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 12h9m0 0l-3-3m3 3l-3 3" />
                </svg>
                Leave community
              </button>
            </div>
          )}

          {/* "Powered by FlexiFunnels" — always shown to members inside every
              community; the owner's on/off toggle no longer applies. */}
          {member && <PoweredByFlexiFunnels className="-mt-1" />}

          {/* My Points — mirrors the Members/Leaderboard pages, kept in sync via useMyPoints */}
          <MyPointsCard ready={groupReady} me={myStanding} pts={myPointsSettings} token={token} groupToken={groupToken} primary={primary} member={member} />

          {/* Banner ads for this page — shown under the points card. */}
          <InlineBanner token={token} groupToken={groupToken} placement="community" wrapperClass="space-y-3" />
        </aside>
      </div>
        </>
      )}

      {showMyPlans && member && (
        <MyPlansModal member={member} primary={primary} onClose={() => setShowMyPlans(false)} />
      )}
      {/* Every subscription has lapsed — gate the membership. Rendered last so
          it sits above the rest of the shell, and above My Plans only until
          that modal is opened from inside it. */}
      {subGate.blocked && member && !showMyPlans && (
        <SubscriptionExpiredOverlay
          plans={subGate.expired}
          primary={primary}
          membershipName={data?.community?.name || ""}
          onViewPlans={() => setShowMyPlans(true)}
          onSignOut={signOut}
          onRecheck={() => checkSubGate({ force: true })}
        />
      )}
      {showProfile && member && (
        <ProfileModal
          token={token}
          member={member}
          groupToken={groupToken}
          primary={primary}
          banner={profileBanner(community)}
          onClose={() => setShowProfile(false)}
          onUpdated={saveMember}
          onPointsChange={refreshMyPoints}
        />
      )}

      {showChangePassword && member && (
        <ChangePasswordModal
          token={token}
          member={member}
          primary={primary}
          banner={profileBanner(community)}
          onClose={() => setShowChangePassword(false)}
        />
      )}

      {showAccountSettings && member && (
        <AccountSettingsModal
          token={token}
          member={member}
          primary={primary}
          banner={profileBanner(community)}
          onClose={() => setShowAccountSettings(false)}
        />
      )}

      {showMobileCode && member && (
        <MobileCodeModal
          code={token}
          primary={primary}
          onClose={() => setShowMobileCode(false)}
        />
      )}

      {authMode && (
        <AuthModal
          mode={authMode}
          community={community}
          primary={primary}
          form={authForm}
          setForm={setAuthForm}
          busy={authBusy}
          msg={authMsg}
          onClose={closeAuth}
          onSwitch={openAuth}
          forgotStep={forgotStep}
          onSubmitJoin={submitJoin}
          onSubmitSignin={submitSignin}
          onSubmitForgot={submitForgot}
        />
      )}

      <WelcomeFlowers show={welcome.show} name={welcome.name} onDone={() => setWelcome({ show: false, name: "" })} />

      {/* Mobile-only floating group-info bubble — removed per request.
      {subView !== "about" && subView !== "map" && (
        <GroupInfoBubble group={group} totalMembers={total_members} primary={primary} />
      )}
      */}


      {/* On phones the member chat is a full-screen app surface (100dvh−header),
          so the page footer would spill below the composer and force an awkward
          scroll. Hide it there; from sm up the chat leaves room and it returns. */}
      <div className={subView === "memberchat" ? "hidden sm:contents" : "contents"}>
        <PolicyFooter
          policies={data.community?.policies || policies}
          // Lets the footer pull the Privacy/Terms text on demand — it is no
          // longer shipped with the page. See PolicyFooter.
          token={token}
          communityName={communityName || data.community?.name}
          primary={primary}
          showBranding={data.community?.show_branding}
        />
      </div>
      </div>{/* /creator content column */}

      <BannerPopup token={token} groupToken={groupToken} placement={SUBVIEW_PLACEMENT[subView] || "community"} />

      {/* Creator: app-like fixed bottom nav (mobile only) */}
      {feedBottomNav && (
        <nav className="fixed inset-x-0 bottom-0 z-30 flex justify-around border-t border-gray-100 bg-surface py-1.5 lg:hidden">
          {bottomNavItems.map((t) => {
            const isGroups = t.key === "groups";
            const active = !isGroups && subView === t.key;
            return (
              <button
                key={t.key}
                onClick={() => (isGroups ? onNavigate?.("groups") : setSubView(t.key))}
                className="flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] font-semibold"
                style={{ color: active ? primary : "rgb(var(--c-gray-400))" }}
              >
                {t.icon}
                {isGroups ? "Community" : t.label}
              </button>
            );
          })}
        </nav>
      )}

      {/* ---------- "Post pending approval" notice (member) ---------- */}
      {pendingNotice && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/50 p-4" onClick={() => setPendingNotice(false)}>
          <div className="relative w-full max-w-md rounded-2xl bg-surface p-7 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPendingNotice(false)} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-500/15 text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-500/15">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-500/15 text-amber-500">
              <svg className="h-9 w-9" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.01M10.29 3.86l-8.48 14.7A1.5 1.5 0 003.11 21h17.78a1.5 1.5 0 001.3-2.44l-8.48-14.7a1.5 1.5 0 00-2.62 0z" /></svg>
            </div>
            <p className="text-[15px] leading-relaxed text-gray-700">
              Admin will review your post and approve. Until then your post to the community will be pending.
            </p>
          </div>
        </div>
      )}

      {/* ---------- Leave-group confirmation modal ---------- */}
      {confirmLeave && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => !leaving && setConfirmLeave(false)}
        >
          <div
            className="bg-surface w-full max-w-sm rounded-2xl shadow-xl p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 dark:bg-red-500/15 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-600 dark:text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H3m0 0l4-4m-4 4l4 4M13 4h5a2 2 0 012 2v12a2 2 0 01-2 2h-5" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mt-4">Leave community?</h3>
            <p className="text-sm text-gray-500 mt-1.5">
              You'll leave <span className="font-semibold text-gray-700">{group?.name}</span> and lose access to its
              feed. You can re-join anytime.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setConfirmLeave(false)}
                disabled={leaving}
                className="flex-1 border border-gray-200 text-gray-700 text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={leaveGroup}
                disabled={leaving}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-red-700 transition disabled:opacity-60"
              >
                {leaving && (
                  <Spinner size={16} />
                )}
                {leaving ? "Leaving…" : "Leave"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}





// Renders text with bare URLs turned into clickable links.
// Renders body text with clickable URLs AND @[Name](id) mention chips.
// `onCanvas`: the text sits on a coloured post backdrop — links and mentions
// take the backdrop's own text colour (blue/brand would clash with the
// gradient) and links are marked by a thick underline instead.
// Long links read as noise in a short comment: show "site.com/some/pa…" and
// keep the full address in the href (and the hover title).
const SHORT_LINK_MAX = 40;
function shortLink(url) {
  const bare = url.replace(/^https?:\/\/(www\.)?/i, "");
  return bare.length > SHORT_LINK_MAX ? `${bare.slice(0, SHORT_LINK_MAX - 1)}…` : bare;
}

function LinkedText({ text, className, primary = "#16a34a", style, onCanvas = false, shortLinks = false }) {
  if (!text) return null;
  const re = /(https?:\/\/[^\s]+)|@\[([^\]]+)\]\((\d+)\)/gi;
  const nodes = [];
  let last = 0;
  let i = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) {
      nodes.push(
        <a
          key={i++}
          href={m[1]}
          target="_blank"
          rel="noreferrer"
          className={
            onCanvas
              ? "break-all underline decoration-2 underline-offset-4 transition hover:opacity-80"
              : "text-blue-600 dark:text-blue-300 underline break-all"
          }
          title={shortLinks ? m[1] : undefined}
        >
          {shortLinks ? shortLink(m[1]) : m[1]}
        </a>
      );
    } else {
      nodes.push(
        <span key={i++} className="font-semibold" style={onCanvas ? undefined : { color: primary }}>
          @{m[2]}
        </span>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <p className={className} style={style}>{nodes}</p>;
}

/**
 * Long-text wrapper with a "Read more" / "Show less" toggle. Used for posts,
 * comments and replies. Renders the full text via LinkedText (so links and
 * @mentions keep working) and, when the text is long, visually clamps it to
 * `collapsedLines` lines until the reader expands it.
 */
function ExpandableText({ text, className, primary = "#16a34a", collapsedLines = 6, threshold = 300, shortLinks = false }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = (text || "").length > threshold;
  const clampStyle =
    isLong && !expanded
      ? {
          display: "-webkit-box",
          WebkitLineClamp: collapsedLines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }
      : undefined;
  return (
    <>
      <LinkedText text={text} primary={primary} className={className} style={clampStyle} shortLinks={shortLinks} />
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-sm font-semibold hover:underline focus:outline-none"
          style={{ color: primary }}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </>
  );
}

function VideoEmbed({ url }) {
  const v = videoEmbedUrl(url);
  if (!v) return null;
  if (v.type === "iframe") {
    return (
      /*
       * Capped at 520px, not the full card width.
       *
       * A 16:9 frame across an ~780px card is ~440px tall, and with the caption
       * strip a single video pushed everything else below the fold — one post
       * filling the screen. Facebook caps feed media for the same reason. The
       * cap is on the WRAPPER so the player keeps its aspect ratio and the
       * caption stays flush with it.
       */
      <div className="mt-3 w-full max-w-[520px]">
        {/* rounded only at the top: the caption strip closes the card below */}
        <div className="relative w-full overflow-hidden rounded-t-xl border bg-black" style={{ paddingTop: "56.25%" }}>
          <iframe
            src={v.src}
            title="video"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <VideoCaption url={url} />
      </div>
    );
  }
  if (v.type === "video") {
    return <video src={v.src} controls className="mt-3 w-full max-h-[28rem] rounded-xl border bg-black" />;
  }
  return (
    <a href={v.src} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm underline text-blue-600 dark:text-blue-300 break-all">
      {v.src}
    </a>
  );
}

function Poll({ post, member, token, primary, onRequireAuth }) {
  const [poll, setPoll] = useState(post.poll);
  const [voting, setVoting] = useState(false);

  const vote = async (index) => {
    if (!member) return onRequireAuth?.();
    if (voting) return;
    setVoting(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-poll-vote`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
        option_index: index,
      });
      if (res.data?.error === 0) setPoll(res.data.poll);
    } catch {
      /* ignore */
    } finally {
      setVoting(false);
    }
  };

  const total = poll.total_votes || 0;
  const voted = poll.my_vote !== null && poll.my_vote !== undefined;

  return (
    <div className="mt-3 space-y-2">
      {poll.options.map((o, i) => {
        const pct = total ? Math.round((o.votes / total) * 100) : 0;
        const mine = poll.my_vote === i;
        return (
          <button
            key={i}
            onClick={() => vote(i)}
            disabled={voting}
            className="relative w-full text-left rounded-lg border overflow-hidden disabled:cursor-default"
            style={{ borderColor: mine ? primary : "rgb(var(--c-gray-200))" }}
          >
            {voted && (
              <div
                className="absolute inset-y-0 left-0"
                style={{ width: `${pct}%`, backgroundColor: `${primary}1f` }}
              />
            )}
            <div className="relative flex items-center justify-between px-3.5 py-2.5">
              <span className="text-sm text-gray-800 flex items-center gap-1.5">
                {mine && (
                  <svg className="w-4 h-4" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {o.text}
              </span>
              {voted && <span className="text-xs font-semibold text-gray-500">{pct}%</span>}
            </div>
          </button>
        );
      })}
      <p className="text-xs text-gray-400">
        {total} {total === 1 ? "vote" : "votes"}
        {!member && " · sign in to vote"}
      </p>
    </div>
  );
}

function Avatar({ name, img, primary, size = 40 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full overflow-hidden flex items-center justify-center font-bold shrink-0"
      style={
        img
          ? { width: size, height: size }
          : { width: size, height: size, backgroundColor: `${primary}1f`, color: primary, border: `1px solid ${primary}40`, fontSize: size * 0.4 }
      }
    >
      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : initial}
    </div>
  );
}

// Three-dot options menu for a comment / reply (Edit — own only; Delete — own or
// admin). Self-contained: own open state + click-outside, mirroring the post menu.
function CommentMenu({ canEdit, canDelete, onEdit, onDelete, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, []);
  if (!canEdit && !canDelete) return null;
  return (
    <div className={`relative shrink-0 ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Comment options"
        className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-36 rounded-xl border bg-surface py-1.5 shadow-lg">
          {canEdit && (
            <button
              type="button"
              onClick={() => { setOpen(false); onEdit(); }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.4-9.4a2 2 0 112.8 2.8L11.8 15.2 8 16l.8-3.8 8.8-8.8z" />
              </svg>
              Edit
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" />
              </svg>
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, aiComment, primary, member, isAdmin, perms = NO_PERMS, token, groupToken, template = "classic", mentionMembers = [], onRequireAuth, onPointsChange, onDeleted, onPinned, onRequestEdit, focusCommentId = null }) {
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.tot_likes);
  const [showComments, setShowComments] = useState(false);
  // Tablet/phone get the comments as a bottom sheet instead of inline; see
  // CommentsSheet.jsx for why. Read here rather than inside the sheet so the
  // two branches share one source of truth for the breakpoint.
  const compactComments = useCompactViewport();
  const [comments, setComments] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const commentMentions = useMentions({ value: commentText, setValue: setCommentText, members: mentionMembers, primary, dropUp: true });
  const [sending, setSending] = useState(false);
  // Unified comment attachments: uploaded images and/or GIF picks, mixed, up to 10.
  const COMMENT_MEDIA_MAX = 10;
  const [commentMedia, setCommentMedia] = useState([]); // { file?, url, isGif }
  const commentFileRef = useRef(null);
  const addCommentImages = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const accepted = acceptImages(arr, commentMedia.length, COMMENT_MEDIA_MAX)
      .map((file) => ({ file, url: URL.createObjectURL(file), isGif: false }));
    if (accepted.length) setCommentMedia((cur) => [...cur, ...accepted].slice(0, COMMENT_MEDIA_MAX));
    if (commentFileRef.current) commentFileRef.current.value = "";
  };
  const pickCommentGif = (url) => {
    if (!url) return;
    setCommentMedia((cur) => cur.length >= COMMENT_MEDIA_MAX ? cur : [...cur, { url, isGif: true }]);
  };
  const removeCommentMedia = (i) => setCommentMedia((cur) => cur.filter((_, idx) => idx !== i));
  const clearCommentMedia = () => {
    setCommentMedia([]);
    if (commentFileRef.current) commentFileRef.current.value = "";
  };
  const [count, setCount] = useState(post.comment_count);
  // Automatic AI reply (arrives ~seconds after posting): inject it into the
  // comment thread live and auto-open it, so it shows without a page refresh.
  const aiInjectedRef = useRef(null);
  useEffect(() => {
    if (!aiComment || aiInjectedRef.current === aiComment.comment_id) return;
    aiInjectedRef.current = aiComment.comment_id;
    setCount((n) => n + 1);
    setShowComments(true);
    setComments((cs) => {
      if (cs === null) return [aiComment];
      if (cs.some((x) => x.comment_id === aiComment.comment_id)) return cs;
      return [...cs, aiComment];
    });
  }, [aiComment]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [content, setContent] = useState(post.content);
  // Coloured background (text posts only). Card-local like `content`, so an
  // edit repaints this card without a feed refetch.
  const [bg, setBg] = useState(post.bg || "");
  const [hideLinkPreview, setHideLinkPreview] = useState(!!post.hide_link_preview);
  const [image, setImage] = useState(post.image);
  // Full image list for display (multi-image posts); `image` above stays as the
  // first one for the single-image edit UI.
  const [images, setImages] = useState(post.images?.length ? post.images : post.image ? [post.image] : []);
  const [video, setVideo] = useState(post.video);
  // Card-local, like content/images above: the edit modal hands the new state
  // back through `apply`, and this card renders from its own copy.
  const [protectedLink, setProtectedLink] = useState(post.protected_link || null);
  const [poll, setPoll] = useState(post.poll);
  const [audio, setAudio] = useState(post.audio);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteCmt, setConfirmDeleteCmt] = useState(null);
  // Comment the long-press action sheet is open for (compact only).
  const [cmtActions, setCmtActions] = useState(null);
  const longPress = useLongPressBinder();
  const [deletingCmt, setDeletingCmt] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content || "");
  const [savingEdit, setSavingEdit] = useState(false);
  // Multi-image editor: { url, file?: File, isExisting }. Existing images are kept
  // by URL (sent as keep_images[]); newly picked ones go to the media service
  // first and are sent as image_urls[] (see uploadMedia.js).
  const [editImages, setEditImages] = useState([]);
  const [editVideo, setEditVideo] = useState(post.video || "");
  const [editPollOptions, setEditPollOptions] = useState(post.poll?.options?.map((o) => o.text) || ["", ""]);
  const editFileRef = useRef(null);
  // Audio editor: replace with a new file, or remove the recording.
  const [editAudioFile, setEditAudioFile] = useState(null);
  const [editAudioPreview, setEditAudioPreview] = useState("");
  const [removeAudio, setRemoveAudio] = useState(false);
  const editAudioRef = useRef(null);

  const isImagePost = post.post_type === 2;
  const isVideoPost = post.post_type === 4;
  const isPollPost = post.post_type === 3;
  const isAudioPost = post.post_type === 5;
  const isOwner = member && post.member_id === member.community_member_id;
  // Admins/moderators can manage (edit/pin/delete) any post in the group.
  // Edit / delete: the author, or a moderator-and-up.
  const canManage = isOwner || perms.manage_posts;
  // Pinning reorders the whole group's feed, so it is admin-only and is NOT
  // implied by owning the post.
  const canPin = perms.pin_posts;

  // RETAINED, no longer reached: the Edit menu item now opens the shared
  // Create Post modal (see onRequestEdit above) instead of this card-local
  // editor, so `editing` is never set and the editor block further down is
  // dead. Kept for one release in case the shared modal needs backing out;
  // delete this, `saveEdit` and the `{editing ? … : …}` branch together.
  // eslint-disable-next-line no-unused-vars
  const startEdit = () => {
    setEditText(content || "");
    // Seed the editor with the post's current images (kept by URL). Strip any
    // cache-buster (?t=…) so keep_images[] matches the stored URL on the server.
    setEditImages(images.map((u) => ({ url: u.replace(/\?t=\d+$/, ""), isExisting: true })));
    setEditVideo(video || "");
    setEditPollOptions(poll?.options?.map((o) => o.text) || ["", ""]);
    setEditAudioFile(null);
    setEditAudioPreview("");
    setRemoveAudio(false);
    setEditing(true);
  };

  const pickEditAudio = (file) => {
    if (!file || !file.type.startsWith("audio/")) return;
    if (file.size > 15 * 1024 * 1024) return toast.warn("Audio must be under 15 MB.");
    setEditAudioFile(file);
    setEditAudioPreview(URL.createObjectURL(file));
    setRemoveAudio(false);
    if (editAudioRef.current) editAudioRef.current.value = "";
  };

  const addEditImages = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const accepted = acceptImages(arr, editImages.length, 10)
      .map((file) => ({ url: URL.createObjectURL(file), file, isExisting: false }));
    if (accepted.length) setEditImages((cur) => [...cur, ...accepted].slice(0, 10));
    if (editFileRef.current) editFileRef.current.value = "";
  };
  const removeEditImage = (i) => setEditImages((cur) => cur.filter((_, idx) => idx !== i));
  const pickEditGif = (url) => {
    if (!url) return;
    setEditImages((cur) => cur.length >= 10 ? cur : [...cur, { url, isGif: true, isExisting: false }]);
  };

  const saveEdit = async () => {
    const text = editText.trim();
    const willHaveImage = isImagePost && editImages.length > 0;
    const willHaveAudio = isAudioPost && (!!editAudioFile || (!!audio && !removeAudio));
    if (!text && !willHaveImage && !isVideoPost && !willHaveAudio) return toast.warn("Post cannot be empty.");

    let payload;
    // A video link pasted while EDITING counts too — creating a post already
    // auto-detects one, and editing silently did not. See the same fix in
    // CommunityFeed.jsx::saveEdit.
    if (!isVideoPost && !isPollPost && !isImagePost) {
      const detected = extractVideoUrl(text);
      if (detected) {
        payload = { content: text, video_url: detected };
      }
    }
    if (isVideoPost) {
      /*
       * An emptied field REMOVES the video rather than being rejected.
       *
       * This used to refuse to save ("Paste a video link."), so a video post
       * could be replaced but never turned back into a text post — the player
       * stayed for ever. remove_video is the same flag image and audio posts
       * have had all along.
       */
      payload = editVideo.trim()
        ? { content: text, video_url: editVideo.trim() }
        : { content: text, remove_video: "1" };
    } else if (isPollPost) {
      const opts = editPollOptions.map((o) => o.trim()).filter(Boolean);
      if (!text) return toast.warn("Add a question for your poll.");
      if (opts.length < 2) return toast.warn("A poll needs at least 2 options.");
      payload = { content: text, poll_options: JSON.stringify(opts) };
    }

    setSavingEdit(true);
    try {
      let res;
      if (isImagePost) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("community_member_id", member.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("post_id", post.post_id);
        fd.append("content", text);
        // The editor owns the whole image set: keep_images[] are the retained
        // existing URLs, image_urls[] the newly uploaded ones.
        fd.append("manage_images", "1");
        {
          const { keep, fresh } = await resolveComposerImages(editImages, {
            communityToken: token,
            memberId: member.community_member_id,
          });
          keep.forEach((u) => fd.append("keep_images[]", u));
          fresh.forEach((u) => fd.append("image_urls[]", u));
        }
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else if (isAudioPost && (editAudioFile || removeAudio)) {
        const audio_url = editAudioFile
          ? await uploadFile(editAudioFile, {
              communityToken: token,
              memberId: member.community_member_id,
            })
          : null;
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, {
          community_token: token,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          content: text,
          ...(audio_url ? { audio_url } : {}),
          ...(removeAudio ? { remove_audio: "1" } : {}),
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-edit`, {
          community_token: token,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          content: text,
          ...(payload || {}),
        });
      }
      if (res.data?.error === 0) {
        setContent(res.data.content);
        if (res.data.bg !== undefined) setBg(res.data.bg || "");
        if (res.data.hide_link_preview !== undefined) setHideLinkPreview(!!res.data.hide_link_preview);
        if (res.data.image !== undefined) setImage(res.data.image ? `${res.data.image}?t=${Date.now()}` : null);
        if (res.data.images !== undefined) setImages((res.data.images || []).map((u) => `${u}?t=${Date.now()}`));
        if (res.data.audio !== undefined) setAudio(res.data.audio ? `${res.data.audio}?t=${Date.now()}` : null);
        /*
         * `!== undefined`, not truthiness — the same test image and audio use.
         *
         * Removing a video returns video: null, which is falsy, so the old test
         * skipped setVideo entirely and the player stayed on screen. The row
         * WAS updated; only the UI disagreed, which reads as "remove doesn't
         * work" until you reload the page.
         */
        if (res.data.video !== undefined) setVideo(res.data.video || null);
        if (res.data.poll) setPoll(res.data.poll);
        setEditing(false);
      }
    } catch {
      /* ignore */
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, []);

  const pinPost = async () => {
    setMenuOpen(false);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-pin`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0) onPinned?.(res.data.pinned);
    } catch {
      /* ignore */
    }
  };

  const deletePost = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-delete`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0) {
        toast.success("Post deleted.");
        onDeleted?.();
      } else {
        toast.error(res.data?.msg || "Could not delete the post.");
        setDeleting(false);
        setConfirmDelete(false);
      }
    } catch {
      toast.error("Could not delete the post.");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const like = async () => {
    if (!member) return onRequireAuth?.();
    // optimistic
    setLiked((v) => !v);
    setLikes((n) => (liked ? n - 1 : n + 1));
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-post-like`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        post_id: post.post_id,
      });
      if (res.data?.error === 0) {
        setLiked(res.data.liked);
        setLikes(res.data.tot_likes);
        // Only on the like, never the un-like.
        if (res.data.liked) {
          notifyEmailEvent(token, "like_post", { post_id: post.post_id });
        }
        // Likes credit the post author. toastLikePoints checks the response's
        // earner_id against me, so it only announces (and only reports a change)
        // when the points actually landed in my balance.
        if (toastLikePoints(res.data, member)) onPointsChange?.();
      }
    } catch {
      /* ignore */
    }
  };

  const openComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) {
      setLoadingComments(true);
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comments`, {
          community_token: token,
          post_id: post.post_id,
          community_member_id: member?.community_member_id || undefined,
          // Proves membership to the backend gate for private/approval groups.
          member_token: member?.auth_token,
        });
        if (res.data?.error === 0) setComments(res.data.comments || []);
      } catch {
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!member) return onRequireAuth?.();
    const cmt = commentMentions.serialize(commentText.trim());
    if (!cmt && commentMedia.length === 0) return;
    setSending(true);
    try {
      let res;
      if (commentMedia.length > 0) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("post_id", post.post_id);
        fd.append("cmt", cmt);
        {
          const { fresh } = await resolveComposerImages(commentMedia, {
            communityToken: token,
            memberId: member.community_member_id,
          });
          fresh.forEach((u) => fd.append("image_urls[]", u));
        }
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          cmt,
        });
      }
      if (res.data?.error === 0) {
        setComments((cs) => [...(cs || []), res.data.comment]);
        setCount((n) => n + 1);
        // A top-level comment notifies the post author; @mentions in it notify
        // the tagged members. Both are derived server-side from the comment id.
        notifyEmailEvent(token, "comment", { comment_id: res.data.comment?.comment_id });
        if (/@\[[^\]]+\]\(\d+\)/.test(cmt || "")) {
          notifyEmailEvent(token, "mentions", { comment_id: res.data.comment?.comment_id });
        }
        setCommentText("");
        clearCommentMedia();
        toastPoints(res.data.points_awarded, "Comment added.");
        onPointsChange?.(); // commenting awards points — reflect it immediately
      } else {
        // Surfaces the reply-level lock and the member tagging-limit messages.
        toast.error(res.data?.msg || "Could not add your comment.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not add your comment.");
    } finally {
      setSending(false);
    }
  };

  // --- Replies (single level: a reply attaches to a top-level comment) ---
  const [replyTo, setReplyTo] = useState(null); // top-level comment_id whose reply box is open
  const [replyText, setReplyText] = useState("");
  const replyMentions = useMentions({ value: replyText, setValue: setReplyText, members: mentionMembers, primary, dropUp: true });
  const [replySending, setReplySending] = useState(false);
  // Reply attachments — uploaded images and/or GIF picks, mixed, up to 10.
  const REPLY_MEDIA_MAX = 10;
  const [replyMedia, setReplyMedia] = useState([]); // { file?, url, isGif }
  const replyFileRef = useRef(null);
  const addReplyImages = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const accepted = acceptImages(arr, replyMedia.length, REPLY_MEDIA_MAX)
      .map((file) => ({ file, url: URL.createObjectURL(file), isGif: false }));
    if (accepted.length) setReplyMedia((cur) => [...cur, ...accepted].slice(0, REPLY_MEDIA_MAX));
    if (replyFileRef.current) replyFileRef.current.value = "";
  };
  const pickReplyGif = (url) => {
    if (!url) return;
    setReplyMedia((cur) => cur.length >= REPLY_MEDIA_MAX ? cur : [...cur, { url, isGif: true }]);
  };
  const removeReplyMedia = (i) => setReplyMedia((cur) => cur.filter((_, idx) => idx !== i));
  const clearReplyMedia = () => {
    setReplyMedia([]);
    if (replyFileRef.current) replyFileRef.current.value = "";
  };
  const [expandedReplies, setExpandedReplies] = useState({}); // parentId -> show all replies

  // Deep-link from an email "View comment" link (?comment={id}): open this post's
  // comments, load them, expand the parent thread if the target is a reply, then
  // scroll to + highlight the comment. `cancelled` guards a stale run — critically
  // this uses NO "already ran" ref, so React StrictMode's double-invoke can't leave
  // the second run bailing while the first's fetch is cancelled (which stuck the
  // "Loading comments…" state forever).
  useEffect(() => {
    if (!focusCommentId) return;
    let cancelled = false;
    (async () => {
      setShowComments(true);
      let list = comments;
      if (list == null) {
        setLoadingComments(true);
        try {
          const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comments`, {
            community_token: token,
            post_id: post.post_id,
            community_member_id: member?.community_member_id || undefined,
            member_token: member?.auth_token,
          });
          list = res.data?.error === 0 ? (res.data.comments || []) : [];
        } catch {
          list = [];
        }
        if (cancelled) return;
        setComments(list);
        setLoadingComments(false);
      }
      if (cancelled) return;
      // If the target is a reply, make sure its parent thread is fully expanded.
      const target = (list || []).find((x) => String(x.comment_id) === String(focusCommentId));
      if (target?.parent_id) setExpandedReplies((s) => ({ ...s, [target.parent_id]: true }));
      // Scroll once the comment has rendered (retry briefly while React commits).
      // If it never appears (e.g. the comment was deleted), fall back to the post so
      // the link still lands somewhere sensible instead of dead-ending.
      let tries = 0;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(`comment-${focusCommentId}`);
        if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
        if (tries++ < 25) { setTimeout(tryScroll, 120); return; }
        const postEl = document.getElementById(`post-${post.post_id}`);
        if (postEl) postEl.scrollIntoView({ behavior: "smooth", block: "center" });
      };
      setTimeout(tryScroll, 120);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCommentId]);

  // --- Editing a comment / reply inline (own only) ---
  const [editingCmt, setEditingCmt] = useState(null); // comment_id being edited
  const [editCmtText, setEditCmtText] = useState("");
  const editCmtMentions = useMentions({ value: editCmtText, setValue: setEditCmtText, members: mentionMembers, primary, dropUp: true });
  const [editCmtImages, setEditCmtImages] = useState([]); // { url, file?, isGif?, isExisting }
  const [editCmtSending, setEditCmtSending] = useState(false);
  const editCmtFileRef = useRef(null);
  const startEditCmt = (c) => {
    setReplyTo(null);
    setEditingCmt(c.comment_id);
    setEditCmtText(c.cmt || "");
    const imgs = c.images?.length ? c.images : c.img ? [c.img] : [];
    setEditCmtImages(imgs.map((u) => ({ url: u.replace(/\?t=\d+$/, ""), isExisting: true })));
  };
  const cancelEditCmt = () => { setEditingCmt(null); setEditCmtText(""); setEditCmtImages([]); };
  const addEditCmtImages = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const accepted = acceptImages(arr, editCmtImages.length, 10)
      .map((file) => ({ url: URL.createObjectURL(file), file, isExisting: false }));
    if (accepted.length) setEditCmtImages((cur) => [...cur, ...accepted].slice(0, 10));
    if (editCmtFileRef.current) editCmtFileRef.current.value = "";
  };
  const pickEditCmtGif = (url) => {
    if (!url) return;
    setEditCmtImages((cur) => cur.length >= 10 ? cur : [...cur, { url, isGif: true, isExisting: false }]);
  };
  const removeEditCmtImage = (i) => setEditCmtImages((cur) => cur.filter((_, idx) => idx !== i));

  // Photo editor on a comment / reply / comment-being-edited thumbnail.
  // { list: "comment" | "reply" | "edit", index }
  const [cmtPhotoEdit, setCmtPhotoEdit] = useState(null);
  const cmtMediaLists = {
    comment: [commentMedia, setCommentMedia],
    reply: [replyMedia, setReplyMedia],
    edit: [editCmtImages, setEditCmtImages],
  };
  const cmtPhotoEditing = cmtPhotoEdit ? cmtMediaLists[cmtPhotoEdit.list][0][cmtPhotoEdit.index] : null;
  const saveCmtPhoto = (file) => {
    const target = cmtPhotoEdit;
    setCmtPhotoEdit(null);
    if (!target || !withinMediaLimit(file, "image")) return;
    cmtMediaLists[target.list][1]((cur) =>
      cur.map((m, j) => (j === target.index ? { ...m, file, url: URL.createObjectURL(file) } : m))
    );
  };
  const saveEditCmt = async (c) => {
    const cmt = editCmtMentions.serialize(editCmtText.trim());
    if (!cmt && editCmtImages.length === 0) return;
    setEditCmtSending(true);
    try {
      const fd = new FormData();
      fd.append("community_token", token);
      fd.append("community_member_id", member.community_member_id);
      fd.append("member_token", member?.auth_token);
      fd.append("comment_id", c.comment_id);
      fd.append("cmt", cmt);
      fd.append("manage_images", "1");
      {
        const { keep, fresh } = await resolveComposerImages(editCmtImages, {
          communityToken: token,
          memberId: member.community_member_id,
        });
        keep.forEach((u) => fd.append("keep_images[]", u));
        fresh.forEach((u) => fd.append("image_urls[]", u));
      }
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-edit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.error === 0) {
        setComments((cs) => cs.map((x) =>
          x.comment_id === c.comment_id
            ? { ...x, cmt: res.data.cmt, images: (res.data.images || []).map((u) => `${u}?t=${Date.now()}`), img: res.data.img }
            : x
        ));
        cancelEditCmt();
      } else {
        toast.error(res.data?.msg || "Could not update comment.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not update comment.");
    } finally {
      setEditCmtSending(false);
    }
  };

  const submitReply = async (parentId) => {
    if (!member) return onRequireAuth?.();
    const cmt = replyMentions.serialize(replyText.trim());
    if (!cmt && replyMedia.length === 0) return;
    setReplySending(true);
    try {
      let res;
      if (replyMedia.length > 0) {
        const fd = new FormData();
        fd.append("community_token", token);
        fd.append("group_token", groupToken);
        fd.append("community_member_id", member.community_member_id);
        fd.append("member_token", member?.auth_token);
        fd.append("post_id", post.post_id);
        fd.append("cmt", cmt);
        fd.append("parent_id", parentId);
        {
          const { fresh } = await resolveComposerImages(replyMedia, {
            communityToken: token,
            memberId: member.community_member_id,
          });
          fresh.forEach((u) => fd.append("image_urls[]", u));
        }
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-create`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-create`, {
          community_token: token,
          group_token: groupToken,
          community_member_id: member.community_member_id,
          member_token: member?.auth_token,
          post_id: post.post_id,
          cmt,
          parent_id: parentId,
        });
      }
      if (res.data?.error === 0) {
        setComments((cs) => [...(cs || []), res.data.comment]);
        setCount((n) => n + 1);
        // A reply notifies the parent comment's author (and the post author);
        // @mentions in it notify the tagged members.
        notifyEmailEvent(token, "replies", { comment_id: res.data.comment?.comment_id });
        if (/@\[[^\]]+\]\(\d+\)/.test(replyText || "")) {
          notifyEmailEvent(token, "mentions", { comment_id: res.data.comment?.comment_id });
        }
        setReplyText("");
        clearReplyMedia();
        setReplyTo(null);
        toastPoints(res.data.points_awarded, "Reply added.");
        onPointsChange?.();
      } else {
        // Surfaces the reply-level lock and the member tagging-limit messages.
        toast.error(res.data?.msg || "Could not add your reply.");
      }
    } catch (e) {
      toast.error(e.response?.data?.msg || "Could not add your reply.");
    } finally {
      setReplySending(false);
    }
  };

  const likeComment = async (c) => {
    if (!member) return onRequireAuth?.();
    setComments((cs) =>
      cs.map((x) =>
        x.comment_id === c.comment_id
          ? { ...x, liked: !x.liked, tot_likes: x.tot_likes + (x.liked ? -1 : 1) }
          : x
      )
    );
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-like`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        comment_id: c.comment_id,
      });
      if (res.data?.error === 0) {
        setComments((cs) =>
          cs.map((x) =>
            x.comment_id === c.comment_id ? { ...x, liked: res.data.liked, tot_likes: res.data.tot_likes } : x
          )
        );
        // Only on the like, never the un-like.
        if (res.data.liked) {
          notifyEmailEvent(token, "like_comment", { comment_id: c.comment_id });
        }
        // Same rule as post likes — the comment's author is the one credited.
        // Replies come through this handler too, so they behave identically.
        if (toastLikePoints(res.data, member)) onPointsChange?.();
      }
    } catch {
      /* ignore */
    }
  };

  // `target` lets the compact action sheet delete the comment it is confirming
  // without first round-tripping through `confirmDeleteCmt` state — a setState
  // is async, so reading it back in the same tick would see the old value.
  // Desktop still calls this with no argument from the ConfirmModal.
  const deleteComment = async (target) => {
    // Guarded on comment_id, not truthiness: any stray argument (a click event
    // from a bare `onClick={deleteComment}`) would otherwise pass for a comment.
    const c = target?.comment_id ? target : confirmDeleteCmt;
    if (!c) return;
    setDeletingCmt(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-comment-delete`, {
        community_token: token,
        community_member_id: member.community_member_id,
        member_token: member?.auth_token,
        comment_id: c.comment_id,
      });
      if (res.data?.error === 0) {
        // Drop the comment and any of its replies (deleting a parent cascades).
        setComments((cs) => cs.filter((x) => x.comment_id !== c.comment_id && x.parent_id !== c.comment_id));
        setCount((n) => Math.max(0, n - (res.data.deleted_count || 1)));
        toast.success("Comment deleted.");
      } else {
        toast.error(res.data?.msg || "Could not delete the comment.");
      }
    } catch {
      toast.error("Could not delete the comment.");
    } finally {
      setDeletingCmt(false);
      setConfirmDeleteCmt(null);
      setCmtActions(null); // closes the compact action sheet's confirm step
    }
  };

  /**
   * The ONE composer at the foot of the comments sheet, and what it is currently
   * writing: a new comment, a reply, or an edit.
   *
   * On compact there is exactly one text field. Replying used to open a second
   * input nested inside the thread and editing a third inside the bubble — three
   * inputs on a screen with room for one, each narrower than the last because
   * each sat a level deeper in the indentation. Instagram's model instead: tap
   * Reply, the field you already have takes the reply, with a chip above it
   * saying what you are answering.
   *
   * Desktop is untouched — it keeps the inline reply box and inline edit form,
   * where there is width for them and the context is visible at a glance.
   */
  const activeEditCmt =
    compactComments && editingCmt
      ? (comments || []).find((x) => String(x.comment_id) === String(editingCmt))
      : null;
  const activeReplyCmt =
    compactComments && replyTo
      ? (comments || []).find((x) => String(x.comment_id) === String(replyTo))
      : null;

  const cmtComposer = activeEditCmt
    ? {
        mode: "edit",
        chip: "Editing your comment",
        mentions: editCmtMentions,
        text: editCmtText,
        setText: setEditCmtText,
        placeholder: "Edit your comment…",
        media: editCmtImages,
        list: "edit",
        removeMedia: removeEditCmtImage,
        fileRef: editCmtFileRef,
        onFiles: addEditCmtImages,
        onGif: pickEditCmtGif,
        sending: editCmtSending,
        submit: () => saveEditCmt(activeEditCmt),
        cancel: cancelEditCmt,
      }
    : activeReplyCmt
      ? {
          mode: "reply",
          chip: `Replying to ${activeReplyCmt.author}`,
          mentions: replyMentions,
          text: replyText,
          setText: setReplyText,
          placeholder: `Reply to ${activeReplyCmt.author}…`,
          media: replyMedia,
          list: "reply",
          removeMedia: removeReplyMedia,
          fileRef: replyFileRef,
          onFiles: addReplyImages,
          onGif: pickReplyGif,
          sending: replySending,
          submit: () => submitReply(activeReplyCmt.comment_id),
          cancel: () => {
            setReplyTo(null);
            setReplyText("");
            clearReplyMedia();
          },
        }
      : {
          mode: "comment",
          chip: null,
          mentions: commentMentions,
          text: commentText,
          setText: setCommentText,
          placeholder: "Write a comment… use @ to tag",
          media: commentMedia,
          list: "comment",
          removeMedia: removeCommentMedia,
          fileRef: commentFileRef,
          onFiles: addCommentImages,
          onGif: pickCommentGif,
          sending,
          submit: null, // the form's own onSubmit={addComment} handles this
          cancel: null,
        };

  // The link the comment field is carrying, shown WhatsApp-style on top of it.
  const cmtLinkStrip = cmtComposer.media.length === 0 ? extractLinkUrl(cmtComposer.text || "") : "";

  // Tapping Reply (or Edit) has to put the caret where the text now goes.
  // Without this the field silently changes mode at the bottom of the screen
  // and the keyboard stays shut, which reads as the button doing nothing.
  const cmtFieldRef = cmtComposer.mentions.ref;
  useEffect(() => {
    if (compactComments && (replyTo || editingCmt)) cmtFieldRef.current?.focus();
    // cmtFieldRef is a ref object; replyTo/editingCmt are what actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compactComments, replyTo, editingCmt]);

  return (
    <>
    {cmtPhotoEditing?.file && (
      <ImageEditor
        file={cmtPhotoEditing.file}
        primary={primary}
        onCancel={() => setCmtPhotoEdit(null)}
        onSave={saveCmtPhoto}
      />
    )}
    <div
      className={`border transition duration-200 hover:shadow-lg ${
        template === "studio"
          ? "rounded-3xl p-6"
          : template === "catalog"
            ? "rounded-xl shadow-sm p-5"
            : template === "campus"
              ? "rounded-2xl p-5"
              : "rounded-2xl shadow-sm p-5"
      } ${
        post.pinned
          ? "bg-amber-50/50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 hover:border-amber-300 dark:hover:border-amber-500/30"
          : "bg-surface border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Author */}
      <div className="flex items-center gap-3">
        <span className="shrink-0">
          <Avatar name={post.author} img={post.author_img} primary={primary} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 text-[15px] leading-tight flex items-center gap-1.5 min-w-0">
            <span className="truncate">{post.author}</span>
            {(() => {
              // Distinct role badge. Falls back to "Admin" for older API
              // payloads that only send the author_is_admin boolean.
              const label =
                { instructor: "Instructor", admin: "Admin", moderator: "Moderator" }[
                  post.author_role_label
                ] || (post.author_is_admin ? "Admin" : null);
              return label ? (
                <span
                  className="shrink-0 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: `${primary}1a`, color: primary }}
                >
                  {label}
                </span>
              ) : null;
            })()}
          </p>
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
            <span>{post.time_ago}</span>
            {post.category_name && (
              <>
                <span className="text-gray-300 dark:text-gray-500">·</span>
                <span className="font-semibold" style={{ color: primary }}>
                  {post.category_emoji ? `${post.category_emoji} ` : ""}{post.category_name}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="ml-auto shrink-0 flex items-center gap-2">
          {post.pinned ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5 rounded-full">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14.4 3.6l6 6a1 1 0 01-.7 1.7c-1.3.1-2.6.6-3.5 1.5l-.9.9 1.6 4.8a1 1 0 01-1.6 1.1l-3.4-3.4-4.2 4.2a1 1 0 01-1.4-1.4l4.2-4.2-3.4-3.4a1 1 0 011.1-1.6l4.8 1.6.9-.9c.9-.9 1.4-2.2 1.5-3.5a1 1 0 011.7-.7z" />
              </svg>
              Pinned
            </span>
          ) : null}
          {canManage && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Post options"
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.6" />
                  <circle cx="12" cy="12" r="1.6" />
                  <circle cx="12" cy="19" r="1.6" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-40 bg-surface border rounded-xl shadow-lg z-20 py-1.5">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      // Editing now runs through the SAME popup a post is
                      // written in. The card hands up its current content (its
                      // own state, which may already differ from the `post`
                      // prop after an earlier edit) plus the callback that
                      // writes the saved result back into that state.
                      //
                      // Cache-busters are stripped so keep_images[] matches the
                      // URLs the server actually stores.
                      onRequestEdit?.(
                        {
                          post_id: post.post_id,
                          content,
                          image,
                          images: images.map((u) => u.replace(/\?t=\d+$/, "")),
                          audio: audio ? audio.replace(/\?t=\d+$/, "") : audio,
                          video,
                          poll,
                          bg,
                          hide_link_preview: hideLinkPreview,
                          // Without this the editor cannot tell the post HAS a
                          // protected link, so it never asks for the stored URL
                          // and the fields open empty.
                          protected_link: protectedLink,
                          membership_group_category_id: post.membership_group_category_id,
                        },
                        (d) => {
                          setContent(d.content);
                          if (d.bg !== undefined) setBg(d.bg || "");
                          if (d.hide_link_preview !== undefined) setHideLinkPreview(!!d.hide_link_preview);
                          if (d.image !== undefined) setImage(d.image ? `${d.image}?t=${Date.now()}` : null);
                          if (d.images !== undefined) setImages((d.images || []).map((u) => `${u}?t=${Date.now()}`));
                          if (d.audio !== undefined) setAudio(d.audio ? `${d.audio}?t=${Date.now()}` : null);
                          // `!== undefined` like its siblings — a removed video
                          // comes back as null, and truthiness skipped the setter
                          // so the player stayed until a reload.
                          if (d.video !== undefined) setVideo(d.video || null);
                          if (d.poll) setPoll(d.poll);
                          // null means the link was removed — the button goes
                          // with it.
                          if (d.protected_link !== undefined) setProtectedLink(d.protected_link);
                        },
                      );
                    }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="w-[16px] h-[16px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.4-9.4a2 2 0 112.8 2.8L11.8 15.2 8 16l.8-3.8 8.8-8.8z" />
                    </svg>
                    Edit post
                  </button>
                  {canPin && (
                    <button
                      onClick={pinPost}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="w-[16px] h-[16px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 4h6l-1 7 3 3v2H7v-2l3-3-1-7zM12 16v4" />
                      </svg>
                      {post.pinned ? "Unpin post" : "Pin post"}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDelete(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15"
                  >
                    <svg className="w-[16px] h-[16px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" />
                    </svg>
                    Delete post
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {/* Post editing is a MODAL, the same shape as the Create Post modal — a
          post is composed in a popup, so it should be edited in one too. It was
          previously an inline block inside the feed card, which on a phone meant
          a full composer (text, image grid, GIF, emoji, poll, audio) crammed
          into a ~340px column, and it pushed the rest of the feed down while you
          typed.

          The editor's own state and `saveEdit` are unchanged and still live in
          PostCard — only the container around them moved. Portalled to <body>
          because the feed card sits inside overflow/transform contexts that
          would clip a fixed child.

          NOTE: this is the presentation half. The Create modal and this editor
          are still two separate bodies of markup, so a field added to one must
          be added to the other (category is already create-only). Merging them
          into one <PostComposerModal/> used by both is the follow-up. */}
      {editing
        ? createPortal(
        <div className="fixed inset-0 z-[9999] flex items-stretch justify-center overflow-y-auto bg-black/50 p-0 sm:items-start sm:p-4 sm:py-10">
          <div className="flex h-full w-full flex-col rounded-none bg-surface shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-5rem)] sm:max-w-xl sm:rounded-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="text-lg font-bold text-gray-900">Edit post</h3>
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={savingEdit}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 disabled:opacity-50"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            placeholder="Write something…"
            className={INPUT_CLS + " resize-none"}
            {...themeFocus(primary)}
          />

          {/* Image edit (image posts only) */}
          {isImagePost && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-2">
                {editImages.map((im, i) => (
                  <div key={i} className="relative h-24 w-24 shrink-0">
                    <img src={im.url} alt="" className="h-full w-full rounded-xl border border-gray-200 object-cover" />
                    <button
                      type="button"
                      onClick={() => removeEditImage(i)}
                      className="absolute -top-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-white shadow"
                      aria-label="Remove"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
                {editImages.length < 10 && (
                  <button
                    type="button"
                    onClick={() => editFileRef.current?.click()}
                    className="flex h-24 w-24 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-200 text-gray-400 transition hover:border-gray-300 hover:text-gray-600"
                    aria-label="Add photos"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    <span className="text-[11px] font-semibold">Add</span>
                  </button>
                )}
              </div>
              <input
                ref={editFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => addEditImages(e.target.files)}
              />
            </div>
          )}

          {/* Video edit */}
          {isVideoPost && (
            <div className="mt-2">
              <input
                value={editVideo}
                onChange={(e) => setEditVideo(e.target.value)}
                placeholder="Paste a YouTube, Vimeo, Wistia or MP4 link…"
                className={INPUT_CLS}
                {...themeFocus(primary)}
              />
              {editVideo.trim() && <VideoEmbed url={editVideo.trim()} />}
            </div>
          )}

          {/* Audio edit — keep, replace, or remove the recording. */}
          {isAudioPost && (
            <div className="mt-2">
              {editAudioPreview || (audio && !removeAudio) ? (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                  <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-gray-400">Audio</span>
                  <audio controls src={editAudioPreview || audio} className="h-9 min-w-0 flex-1" />
                  <button
                    type="button"
                    onClick={() => { setEditAudioFile(null); setEditAudioPreview(""); setRemoveAudio(true); }}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500"
                    aria-label="Remove audio"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-400">Audio removed.</p>
              )}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => editAudioRef.current?.click()}
                  className="text-sm font-medium"
                  style={{ color: primary }}
                >
                  {audio && !removeAudio && !editAudioPreview ? "Replace audio" : editAudioPreview ? "Choose a different file" : "Add audio"}
                </button>
                <input
                  ref={editAudioRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => pickEditAudio(e.target.files?.[0])}
                />
              </div>
            </div>
          )}

          {/* Poll edit */}
          {isPollPost && (
            <div className="mt-2 space-y-2.5">
              {editPollOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span
                    className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: `${primary}14`, color: primary }}
                  >
                    {i + 1}
                  </span>
                  <input
                    value={opt}
                    onChange={(e) =>
                      setEditPollOptions((o) => o.map((v, idx) => (idx === i ? e.target.value : v)))
                    }
                    placeholder={`Option ${i + 1}`}
                    className={INPUT_CLS + " flex-1"}
                    {...themeFocus(primary)}
                  />
                  {editPollOptions.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setEditPollOptions((o) => o.filter((_, idx) => idx !== i))}
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/15 transition"
                      aria-label="Remove option"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {editPollOptions.length < 6 && (
                <button
                  type="button"
                  onClick={() => setEditPollOptions((o) => [...o, ""])}
                  className="flex items-center gap-2 text-sm font-semibold pl-1 hover:opacity-80 transition"
                  style={{ color: primary }}
                >
                  <span className="w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center" style={{ borderColor: `${primary}66` }}>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                    </svg>
                  </span>
                  Add option
                </button>
              )}
              <p className="text-[11px] text-gray-400">Editing options resets existing votes.</p>
            </div>
          )}

          <div className="mt-2 flex items-center justify-between gap-2">
            {/* Emoji everywhere; Photo + GIF for image posts (add to the set). */}
            <div className="flex items-center gap-0.5">
              <EmojiPicker onPick={(em) => setEditText((t) => t + em)} dropUp className="shrink-0" />
              {isImagePost && (
                <>
                  <button
                    type="button"
                    onClick={() => editFileRef.current?.click()}
                    aria-label="Add photo"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                  >
                    <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z" />
                    </svg>
                  </button>
                  <GifPicker onPick={pickEditGif} dropUp className="shrink-0" />
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditing(false)}
                disabled={savingEdit}
                className="text-sm font-medium text-gray-500 px-3 py-1.5 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit}
                style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                className="text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {savingEdit ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
            </div>
          </div>
        </div>,
        document.body,
      )
        : (
        <>
          {(() => {
            // Hide the embedded video URL from the text (it's shown as a player below).
            const shown = video ? (content || "").split(video).join("").trim() : content;
            if (!shown) return null;

            // Coloured background post: the text IS the visual, so it gets a
            // canvas instead of a paragraph. `parseBg` returns null for anything
            // this build does not recognise, which falls through to the ordinary
            // rendering below rather than breaking the feed.
            const canvas = images.length === 0 && !video && !audio ? parseBg(bg) : null;
            if (canvas) {
              return (
                <div
                  className="mt-3 flex items-center justify-center rounded-2xl px-6 py-10"
                  style={{ background: canvas.css, minHeight: "220px" }}
                >
                  <LinkedText
                    text={shown}
                    onCanvas
                    className="w-full whitespace-pre-wrap break-words text-center font-extrabold"
                    style={{
                      color: canvas.fg,
                      fontSize: `${bgFontSize(shown.length)}px`,
                      lineHeight: 1.3,
                    }}
                  />
                </div>
              );
            }

            return (
              <ExpandableText
                text={shown}
                primary={primary}
                className="text-[15px] text-gray-800 mt-3 whitespace-pre-wrap break-words"
                collapsedLines={8}
                threshold={400}
                shortLinks
              />
            );
          })()}
          {images.length > 0 && <PostImages images={images} size="md" className="mt-3" />}
          {!video && images.length === 0 && !hideLinkPreview && extractLinkUrl(content) && (
            <LinkPreview url={extractLinkUrl(content)} />
          )}
        </>
      )}
      {!editing && video && <VideoEmbed url={video} />}
      {!editing && audio && (
        <div className="mt-3 flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <span
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-white"
            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 18V5l12-2v13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </span>
          <audio controls src={audio} className="h-9 flex-1 min-w-0" />
        </div>
      )}
      {!editing && poll && (
        <Poll
          key={poll.options.map((o) => o.text).join("|")}
          post={{ ...post, poll }}
          member={member}
          token={token}
          primary={primary}
          onRequireAuth={onRequireAuth}
        />
      )}

      {/* Protected link. The payload carries the label and the expiry, never the
          URL — the click asks the API for it, and the API re-checks this
          member's access to the group before answering. */}
      {!editing && protectedLink && (
        <ProtectedLink
          /* Remount when the link changes (an edit can move the expiry or
             replace the URL), so the component's "expired" state is re-seeded
             from the new payload instead of the one it opened with. */
          key={`${protectedLink.button_text}|${protectedLink.expires_at}`}
          post={{ ...post, protected_link: protectedLink }}
          token={token}
          member={member}
          primary={primary}
          onRequireAuth={onRequireAuth}
        />
      )}

      {/* Actions */}
      <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-gray-100 text-sm">
        <button
          onClick={like}
          style={liked ? { color: primary, backgroundColor: `${primary}14` } : undefined}
          className={`flex items-center gap-1.5 font-semibold px-3.5 py-2 rounded-full transition active:scale-95 ${
            liked ? "" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          } disabled:opacity-50`}
        >
          <svg className="w-[18px] h-[18px]" fill={liked ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 11v9H4a1 1 0 01-1-1v-7a1 1 0 011-1h3zm0 0l4.5-7.5A2 2 0 0115 4.6V9h4.2a2 2 0 011.97 2.34l-1.2 7A2 2 0 0118 20H7" />
          </svg>
          {likes > 0 ? likes : "Like"}
        </button>
        <button onClick={openComments} className="flex items-center gap-1.5 font-semibold text-gray-500 px-3.5 py-2 rounded-full hover:bg-gray-100 hover:text-gray-700 transition active:scale-95">
          <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8m-8-4h8m-5 8H7l-3 3V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-6z" />
          </svg>
          {count > 0 ? `${count} ${count === 1 ? "Comment" : "Comments"}` : "Comment"}
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        /* Inline under the post on laptop/desktop; a bottom sheet on
           tablet and phone. Same thread markup either way — only the surface
           around it changes. See CommentsSheet.jsx. */
        <CommentsSheet
          compact={compactComments}
          onClose={() => setShowComments(false)}
          count={(comments || []).length}
          footer={
            member ? (
              <form
                onSubmit={(e) => {
                  // One field, three jobs — see the `cmtComposer` descriptor above.
                  if (cmtComposer.submit) {
                    e.preventDefault();
                    cmtComposer.submit();
                    return;
                  }
                  addComment(e);
                }}
                className="pt-1"
              >
                {cmtComposer.chip ? (
                  /* What the field is writing right now. Without it the only cue
                     that a reply is a reply would be an "@name" the user has to
                     notice, and an edit would silently overwrite a comment. */
                  <div className="mb-2 flex items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5 text-[12px] text-gray-500">
                    <span className="min-w-0 flex-1 truncate font-medium">{cmtComposer.chip}</span>
                    <button
                      type="button"
                      onClick={cmtComposer.cancel}
                      aria-label="Cancel"
                      className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-gray-400 transition hover:bg-gray-200 hover:text-gray-600"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : null}
                {cmtComposer.media.length > 0 && (
                  <div className="mb-2 ml-10 flex flex-wrap gap-2">
                    {cmtComposer.media.map((m, i) => (
                      <div key={i} className="relative h-20 w-20 shrink-0">
                        <img src={m.url} alt="" className="h-full w-full rounded-xl border border-gray-200 object-cover" />
                        <button
                          type="button"
                          onClick={() => cmtComposer.removeMedia(i)}
                          className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-white text-xs shadow"
                          aria-label="Remove"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        {m.isGif && <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 text-[9px] font-bold text-white">GIF</span>}
                        {isEditableImage(m) && (
                          <EditPhotoButton compact onClick={() => setCmtPhotoEdit({ list: cmtComposer.list, index: i })} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                  <span className="shrink-0">
                    <Avatar name={member.name} img={member.img} primary={primary} size={32} />
                  </span>
                  <div className="relative flex-1 min-w-0">
                    {cmtLinkStrip && <LinkPreviewStrip url={cmtLinkStrip} primary={primary} />}
                    <input
                      ref={cmtComposer.mentions.ref}
                      value={cmtComposer.text}
                      onChange={cmtComposer.mentions.onChange}
                      onKeyDown={cmtComposer.mentions.onKeyDown}
                      onBlur={cmtComposer.mentions.onBlur}
                      placeholder={cmtComposer.placeholder}
                      disabled={cmtComposer.sending}
                      className="w-full text-sm bg-gray-50 border border-gray-200 rounded-full px-4 py-2.5 outline-none transition focus:bg-surface placeholder:text-gray-400 [.lp-strip+&]:rounded-t-none [.lp-strip+&]:rounded-b-2xl"
                      {...themeFocus(primary)}
                    />
                    {cmtComposer.mentions.dropdown}
                  </div>
                  <input
                    ref={cmtComposer.fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={(e) => cmtComposer.onFiles(e.target.files)}
                  />
                  {/* Trailing controls, wrapped so they can drop to their own
                      line on a phone. They are all fixed-width and were
                      `shrink-0`, so together with the avatar they held ~175px
                      and the input was squeezed until the placeholder was cut
                      off mid-word. Below sm this strip takes the full width and
                      sits under a full-width input; from sm up it is auto-width
                      and the row is a single line exactly as before. */}
                  <div className="flex w-full items-center justify-end gap-3 sm:w-auto sm:gap-2.5">
                    <button
                      type="button"
                      onClick={() => cmtComposer.fileRef.current?.click()}
                      aria-label="Attach image"
                      className="shrink-0 text-gray-500 hover:text-gray-700 transition"
                    >
                      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </button>
                    <GifPicker onPick={cmtComposer.onGif} className="shrink-0" />
                    <EmojiPicker onPick={(e) => cmtComposer.setText((t) => t + e)} className="shrink-0" />
                    <button
                      type="submit"
                      disabled={cmtComposer.sending || (!cmtComposer.text.trim() && cmtComposer.media.length === 0)}
                      aria-label={cmtComposer.mode === "edit" ? "Save comment" : cmtComposer.mode === "reply" ? "Send reply" : "Send comment"}
                      style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {cmtComposer.sending ? (
                        <Spinner size={16} />
                      ) : (
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <button
                onClick={() => onRequireAuth?.()}
                className="pt-1 text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
                <span style={{ color: primary }} className="font-semibold">Sign in</span> to join the conversation.
              </button>
            )
          }
        >
          {loadingComments ? (
            <InlineLoader label="Loading comments…" primary={primary} />
          ) : (
            (() => {
              const all = comments || [];
              const repliesOf = (id) => all.filter((x) => x.parent_id === id);
              // Can this member act on this comment at all? Same test the "⋮"
              // uses on desktop, so the two entry points never disagree.
              const canActOn = (c) =>
                !!member && (c.member_id === member.community_member_id || perms.delete_comments);
              const longPressCmt = (c) => longPress(() => setCmtActions(c), {
                enabled: compactComments && canActOn(c) && editingCmt !== c.comment_id,
              });
              const renderComment = (c, isReply) => (
                <div
                  key={c.comment_id}
                  id={`comment-${c.comment_id}`}
                  className={`relative flex items-start gap-2.5 group scroll-mt-24 rounded-2xl transition-colors ${String(c.comment_id) === String(focusCommentId) ? "-mx-1.5 px-1.5 py-1.5" : ""}`}
                  style={String(c.comment_id) === String(focusCommentId) ? { boxShadow: `0 0 0 2px ${primary}`, background: `${primary}0d` } : undefined}
                  /* Press-and-hold replaces the "⋮" button below 1024px. Only
                     armed when there is actually something to do, so a press on
                     someone else's comment stays a press. */
                  {...longPressCmt(c)}
                >
                  <Avatar name={c.author} img={c.author_img} primary={primary} size={isReply ? 28 : 32} />
                  <div className="flex-1 min-w-0">
                    {/* Inline edit is a DESKTOP shape. On compact the same
                       text loads into the one pinned field at the foot of the
                       sheet (see cmtComposer), so no input is ever nested
                       inside another. */}
                    {!compactComments && editingCmt === c.comment_id ? (
                      /* Inline edit form (own comment/reply) — text + multi-image + GIF + emoji. */
                      <div className="rounded-2xl border border-gray-200 bg-surface p-2.5 shadow-sm">
                        {editCmtImages.length === 0 && extractLinkUrl(editCmtText) && (
                          <div className="mb-2 [&>div]:rounded-2xl [&>div]:border-b">
                            <LinkPreviewStrip url={extractLinkUrl(editCmtText)} primary={primary} />
                          </div>
                        )}
                        {editCmtImages.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {editCmtImages.map((m, i) => (
                              <div key={i} className="relative h-16 w-16 shrink-0">
                                <img src={m.url} alt="" className="h-full w-full rounded-lg border border-gray-200 object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeEditCmtImage(i)}
                                  className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-white shadow"
                                  aria-label="Remove"
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3 w-3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                                {m.isGif && <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 text-[9px] font-bold text-white">GIF</span>}
                                {isEditableImage(m) && (
                                  <EditPhotoButton compact onClick={() => setCmtPhotoEdit({ list: "edit", index: i })} />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="relative">
                          <textarea
                            ref={editCmtMentions.ref}
                            value={editCmtText}
                            onChange={editCmtMentions.onChange}
                            onKeyDown={editCmtMentions.onKeyDown}
                            onBlur={editCmtMentions.onBlur}
                            rows={2}
                            className="w-full resize-none rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-sm outline-none transition focus:bg-surface"
                            {...themeFocus(primary)}
                          />
                          {editCmtMentions.dropdown}
                        </div>
                        {/* flex-wrap + gap-y: attach/GIF/emoji plus Cancel and
                            Save add up to ~210px, and an edit box opened on a
                            REPLY only has ~190px of column. Without wrapping the
                            row simply overflowed and the whole edit card spilled
                            out past the right edge of the post. Cancel/Save keep
                            `ml-auto`, so when they do drop they sit right-aligned
                            on their own line rather than drifting left. */}
                        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2">
                          <input
                            ref={editCmtFileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            multiple
                            className="hidden"
                            onChange={(e) => addEditCmtImages(e.target.files)}
                          />
                          <button type="button" onClick={() => editCmtFileRef.current?.click()} aria-label="Attach image" className="text-gray-500 hover:text-gray-700 transition">
                            <svg className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z" />
                            </svg>
                          </button>
                          <GifPicker onPick={pickEditCmtGif} dropUp className="shrink-0" />
                          <EmojiPicker onPick={(em) => setEditCmtText((t) => t + em)} dropUp className="shrink-0" />
                          <div className="ml-auto flex items-center gap-2">
                            <button type="button" onClick={cancelEditCmt} className="text-sm font-semibold text-gray-500 hover:text-gray-700">Cancel</button>
                            <button
                              type="button"
                              onClick={() => saveEditCmt(c)}
                              disabled={editCmtSending || (!editCmtText.trim() && editCmtImages.length === 0)}
                              className="rounded-full px-4 py-1.5 text-sm font-bold text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                            >
                              {editCmtSending ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="bg-gray-50 rounded-2xl px-3.5 py-2">
                          {/* pr-7 clears the floated kebab above.
                              flex-wrap, not shrinking: the NAME matters more
                              than the timestamp, so when the two do not fit the
                              time drops whole onto a second line instead of
                              squeezing "Sathish main" down to "Sathish m…".
                              `truncate` on the name is the last resort, for a
                              name too long for the column on its own; the time
                              is `whitespace-nowrap` so it can never break into
                              "just / now" as it used to. */}
                          {/* pr-7 reserves room for the floated "⋮" — desktop
                              only; compact has no kebab to clear. */}
                          <div className={`flex min-w-0 flex-wrap items-baseline gap-x-2 ${compactComments ? "" : "pr-7"}`}>
                            <span className="truncate font-semibold text-sm text-gray-900">{c.author}</span>
                            <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-400">{c.time_ago}</span>
                          </div>
                          {c.cmt && (
                            <ExpandableText
                              text={c.cmt}
                              primary={primary}
                              className="text-sm text-gray-700 whitespace-pre-wrap break-words"
                              collapsedLines={4}
                              threshold={180}
                              shortLinks
                            />
                          )}
                        </div>
                        {(c.images?.length ? c.images : c.img ? [c.img] : []).length > 0 && (
                          <PostImages images={c.images?.length ? c.images : [c.img]} size="sm" className="mt-1.5 max-w-sm" />
                        )}
                        {/* A link in a comment unfurls like one in a post —
                            unless the comment carries its own photo. */}
                        {!c.images?.length && !c.img && extractLinkUrl(c.cmt || "") && (
                          <LinkPreview url={extractLinkUrl(c.cmt)} small />
                        )}
                        <div className="flex items-center gap-3 mt-1 ml-1.5 text-xs">
                          {/* Compact shows the heart ONLY in its own column at
                              the right edge of the row, so this text link is not
                              rendered at all there.

                              Not the `hidden` attribute: that is a UA-stylesheet
                              `display: none`, which the button's own
                              `inline-flex` class overrides — it kept showing. */}
                          {!compactComments && (
                            <button
                              onClick={() => likeComment(c)}
                              className={`inline-flex items-center gap-1 font-medium transition ${
                                c.liked ? "text-red-500" : "text-gray-400 hover:text-gray-600"
                              }`}
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={c.liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                              </svg>
                              {c.liked ? "Liked" : "Like"}
                              {c.tot_likes > 0 ? ` · ${c.tot_likes}` : ""}
                            </button>
                          )}
                          {member && (
                            <button
                              onClick={() => {
                                // Replies attach to the thread's top-level parent; replying
                                // to a reply pre-fills an @mention of its author.
                                const threadId = isReply ? c.parent_id : c.comment_id;
                                if (!isReply && replyTo === threadId) {
                                  setReplyTo(null);
                                } else {
                                  setReplyTo(threadId);
                                  setReplyText(isReply ? `@${c.author} ` : "");
                                }
                              }}
                              className="font-medium text-gray-400 hover:text-gray-600"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      </>
                    )}

                    {/* Replies + reply box (top-level comments only) */}
                    {!isReply &&
                      (repliesOf(c.comment_id).length > 0 ||
                        (!compactComments && replyTo === c.comment_id)) && (
                      <div className="mt-2.5 space-y-2.5 border-l-2 pl-3" style={{ borderColor: `${primary}1f` }}>
                        {(() => {
                          const reps = repliesOf(c.comment_id);
                          const expanded = expandedReplies[c.comment_id];
                          const visible = expanded ? reps : reps.slice(0, 2);
                          return (
                            <>
                              {visible.map((r) => renderComment(r, true))}
                              {reps.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedReplies((s) => ({ ...s, [c.comment_id]: !expanded }))}
                                  className="inline-flex items-center gap-1 text-xs font-semibold"
                                  style={{ color: primary }}
                                >
                                  <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                                  </svg>
                                  {expanded ? "Show less" : `Show ${reps.length - 2} more ${reps.length - 2 === 1 ? "reply" : "replies"}`}
                                </button>
                              )}
                            </>
                          );
                        })()}
                        {/* Desktop only, same reason as the edit form above. */}
                        {!compactComments && replyTo === c.comment_id && member && (
                          <div className="space-y-1.5">
                            {replyMedia.length > 0 && (
                              <div className="flex flex-wrap gap-2 pl-8">
                                {replyMedia.map((m, i) => (
                                  <div key={i} className="relative h-16 w-16 shrink-0">
                                    <img src={m.url} alt="" className="h-full w-full rounded-lg border border-gray-200 object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => removeReplyMedia(i)}
                                      className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-white text-xs shadow"
                                      aria-label="Remove"
                                    >
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-3.5 w-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                    {m.isGif && <span className="absolute bottom-0.5 left-0.5 rounded bg-black/60 px-1 text-[9px] font-bold text-white">GIF</span>}
                                    {isEditableImage(m) && (
                                      <EditPhotoButton compact onClick={() => setCmtPhotoEdit({ list: "reply", index: i })} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Avatar name={member.name} img={member.img} primary={primary} size={26} />
                              <div className="relative flex-1 min-w-0">
                                {replyMedia.length === 0 && extractLinkUrl(replyText) && (
                                  <LinkPreviewStrip url={extractLinkUrl(replyText)} primary={primary} />
                                )}
                                <input
                                  autoFocus
                                  ref={replyMentions.ref}
                                  value={replyText}
                                  onChange={replyMentions.onChange}
                                  onKeyDown={(e) => {
                                    replyMentions.onKeyDown(e);
                                    if (!e.defaultPrevented && e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      submitReply(c.comment_id);
                                    }
                                  }}
                                  onFocus={(e) => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${primary}26`; }}
                                  onBlur={(e) => { replyMentions.onBlur?.(e); e.currentTarget.style.borderColor = ""; e.currentTarget.style.boxShadow = ""; }}
                                  placeholder={`Reply to ${c.author}…`}
                                  className="w-full rounded-full bg-gray-50 border border-gray-200 px-3.5 py-1.5 text-sm outline-none transition focus:bg-surface [.lp-strip+&]:rounded-t-none [.lp-strip+&]:rounded-b-2xl"
                                />
                                {replyMentions.dropdown}
                              </div>
                              <input
                                ref={replyFileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                multiple
                                className="hidden"
                                onChange={(e) => addReplyImages(e.target.files)}
                              />
                              {/* Trailing controls, wrapped so they can drop to their own
                                  line on a phone. They are all fixed-width and were
                                  `shrink-0`, so together with the avatar they held ~175px
                                  and the input was squeezed until the placeholder was cut
                                  off mid-word. Below sm this strip takes the full width and
                                  sits under a full-width input; from sm up it is auto-width
                                  and the row is a single line exactly as before. */}
                              <div className="flex w-full items-center justify-end gap-3 sm:w-auto sm:gap-2.5">
                                <button
                                  type="button"
                                  onClick={() => replyFileRef.current?.click()}
                                  aria-label="Attach image"
                                  className="shrink-0 text-gray-500 hover:text-gray-700 transition"
                                >
                                  <svg className="w-[17px] h-[17px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.5-4.5a2 2 0 012.8 0L16 16m-2-2l1.5-1.5a2 2 0 012.8 0L20 14M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm5 2a1 1 0 11-2 0 1 1 0 012 0z" />
                                  </svg>
                                </button>
                                <GifPicker onPick={pickReplyGif} dropUp className="shrink-0" />
                                <EmojiPicker onPick={(em) => setReplyText((t) => t + em)} dropUp className="shrink-0" />
                                <button
                                  type="button"
                                  onClick={() => submitReply(c.comment_id)}
                                  disabled={replySending || (!replyText.trim() && replyMedia.length === 0)}
                                  aria-label="Send reply"
                                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                                  style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                                >
                                  {replySending ? (
                                    <Spinner size={14} />
                                  ) : (
                                    <svg className="h-[15px] w-[15px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {compactComments ? (
                    /* The heart, as its own column hard against the right edge
                       of the row — the reference layout. It is the one action
                       people use most, so it gets a permanent target instead of
                       a text link buried under the bubble; the count sits under
                       it so the column stays one glyph wide. */
                    <button
                      type="button"
                      onClick={() => likeComment(c)}
                      aria-label={c.liked ? "Unlike comment" : "Like comment"}
                      aria-pressed={c.liked}
                      className={`mt-1 flex shrink-0 flex-col items-center gap-0.5 transition ${
                        c.liked ? "text-red-500" : "text-gray-300 dark:text-gray-500"
                      }`}
                    >
                      <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" fill={c.liked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                      {c.tot_likes > 0 ? (
                        <span className="text-[10px] font-semibold leading-none text-gray-400 tabular-nums">
                          {c.tot_likes}
                        </span>
                      ) : null}
                    </button>
                  ) : (
                    editingCmt !== c.comment_id && member && (c.member_id === member.community_member_id || perms.delete_comments) && (
                      <CommentMenu
                        /* Floated over the bubble's top-right corner instead of
                           sitting in the row as a third column. As a flex item
                           it cost 28px + a 10px gap at EVERY nesting level, and
                           a reply is two levels deep. The header reserves pr-7
                           for it. Compact drops it entirely — long-press opens
                           the same actions there. */
                        className="absolute right-0 top-0"
                        canEdit={c.member_id === member.community_member_id}
                        canDelete={c.member_id === member.community_member_id || perms.delete_comments}
                        onEdit={() => startEditCmt(c)}
                        onDelete={() => setConfirmDeleteCmt(c)}
                      />
                    )
                  )}
                </div>
              );
              return all.filter((c) => !c.parent_id).map((c) => renderComment(c, false));
            })()
          )}

        </CommentsSheet>
      )}
    </div>

    {/* Delete-post confirmation modal */}
    {confirmDelete && (
      <ConfirmModal
        title="Delete post?"
        message="This post will be permanently removed. This action can't be undone."
        confirmLabel="Delete"
        busy={deleting}
        onCancel={() => !deleting && setConfirmDelete(false)}
        onConfirm={deletePost}
      />
    )}

    {/* Long-press actions (compact only) — the same Edit/Delete the "⋮"
        offers on desktop, reached by press-and-hold instead. Delete still goes
        through the existing confirmation modal below; nothing is destroyed
        straight off a gesture. */}
    <CommentActionSheet
      open={!!cmtActions}
      busy={deletingCmt}
      title={cmtActions ? `${cmtActions.author}'s comment` : ""}
      onClose={() => setCmtActions(null)}
      items={
        cmtActions
          ? [
              ...(cmtActions.member_id === member?.community_member_id
                ? [{ label: "Edit", icon: EditIcon, onClick: () => startEditCmt(cmtActions) }]
                : []),
              {
                label: "Delete",
                icon: DeleteIcon,
                danger: true,
                confirm: {
                  title: "Delete comment?",
                  message: "This comment will be permanently removed.",
                  confirmLabel: "Delete",
                },
                onClick: () => deleteComment(cmtActions),
              },
            ]
          : []
      }
    />

    {/* Delete-comment confirmation modal */}
    {confirmDeleteCmt && (
      <ConfirmModal
        title="Delete comment?"
        message="This comment will be permanently removed."
        confirmLabel="Delete"
        busy={deletingCmt}
        onCancel={() => !deletingCmt && setConfirmDeleteCmt(null)}
        /* Wrapped, NOT passed by reference: ConfirmModal does
           `onClick={onConfirm}`, so a bare reference receives the click
           EVENT as deleteComment's `target` argument — truthy, with no
           comment_id, which sent comment_id 0 and came back "Comment
           not found". */
        onConfirm={() => deleteComment()}
      />
    )}
    </>
  );
}

/* Reusable destructive-action confirmation modal (replaces window.confirm). */
function ConfirmModal({ title, message, confirmLabel = "Confirm", busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={onCancel}
    >
      <div
        className="bg-surface w-full max-w-sm rounded-2xl shadow-xl p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-red-50 dark:bg-red-500/15 flex items-center justify-center">
          <svg className="w-7 h-7 text-red-600 dark:text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mt-4">{title}</h3>
        <p className="text-sm text-gray-500 mt-1.5">{message}</p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 border border-gray-200 text-gray-700 text-sm font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-red-700 transition disabled:opacity-60"
          >
            {busy && (
              <Spinner size={16} />
            )}
            {busy ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MembershipShell;
